/** 极简 fetch 封装 + 类型化的端点。 */

const BASE = ""; // 通过 vite proxy 转发 /api/* 到后端

// M9: 多用户登录使用 cookie，所有 fetch 必须带 credentials
const CREDS: RequestCredentials = "include";

export interface StockOut {
  ts_code: string;
  symbol: string;
  name: string;
  area: string | null;
  industry: string | null;
  market: string | null;
  list_date: string | null;
}

export interface RealtimeQuoteOut {
  ts_code: string;
  name: string | null;
  price: number | null;
  open: number | null;
  high: number | null;
  low: number | null;
  pre_close: number | null;
  pct_chg: number | null;
  vol: number | null;
  amount: number | null;
  trade_date: string | null;
  quote_time: string | null;
  is_today: boolean;
}

export interface IndustrySummary {
  industry: string;
  n: number;
}

export interface KlineRow {
  trade_date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  pre_close: number | null;
  change: number | null;
  pct_chg: number | null;
  vol: number | null;
  amount: number | null;
}

export interface StockStats {
  ts_code: string;
  first_trade_date: string | null;
  last_trade_date: string | null;
  kline_count: number;
}

/**
 * AbortController + setTimeout 包出一个超时 signal；
 * fetch 抛 AbortError 时我们重写成"超时 Ns"，前端 UI 能直接显示给用户。
 */
function timeoutSignal(ms: number): { signal: AbortSignal; clear: () => void } {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return { signal: ctrl.signal, clear: () => clearTimeout(t) };
}

async function fetchJSON<T>(url: string, timeoutMs = 60_000): Promise<T> {
  const { signal, clear } = timeoutSignal(timeoutMs);
  let res: Response;
  try {
    res = await fetch(`${BASE}${url}`, { credentials: CREDS, signal });
  } catch (e) {
    if ((e as Error).name === "AbortError") {
      throw new Error(`请求超时 (${(timeoutMs / 1000).toFixed(0)}s) — 网络或服务可能临时不可用`);
    }
    throw e;
  } finally {
    clear();
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(friendlyHttpError(res.status, detail, res.statusText));
  }
  // 标记 SW 返回的 stale 缓存（PWA 离线模式）
  if (res.headers.get("X-WhaleSeek-Stale") === "1") {
    document.dispatchEvent(new CustomEvent("whaleseek:stale", { detail: url }));
  }
  return (await res.json()) as T;
}

// #7 ML 实验对比
export interface MlRollingIcPoint {
  eval_date: string;
  rolling_ic_mean: number | null;
  rolling_ic_std: number | null;
  n_window: number;
}
export interface MlRollingIcOut {
  exp_id: number;
  model_version: string | null;
  window: number;
  points: MlRollingIcPoint[];
  avg_mean: number | null;
  avg_std: number | null;
  sharpe_like: number | null;
}

export interface MlCompareItem {
  exp_id: number;
  name: string;
  status: string;
  is_active: boolean;
  model_version: string | null;
  holdout_ic: number | null;
  holdout_mse: number | null;
  n_train: number | null;
  config_summary: {
    horizon?: number;
    train_days?: number;
    features?: string[];
    params?: Record<string, unknown>;
  };
  feature_importance: Record<string, number> | null;
  paper_live: PaperTradingGroup | null;
  paper_sim: PaperTradingGroup | null;
  rolling_ic: MlRollingIcOut;
}

export interface MlCompareOut {
  items: MlCompareItem[];
  feature_union: string[];
}

// #4 周报
export interface WeeklyReportOut {
  user_id: number;
  week_of: string;
  overall: string | null;
  // LLM 偶尔把某段返回成对象/数组而非字符串；用 unknown，渲染层做兜底压平
  sections: {
    portfolio_review?: unknown;
    watchlist_summary?: unknown;
    decisions_review?: unknown;
  };
  summary_md: string;
  model: string | null;
  generated_at: string;
}

// #5 决策日记
export interface JournalReasonStat {
  reason: string;
  n: number;
  n_5d: number;
  n_20d: number;
  win_rate_5d: number | null;
  win_rate_20d: number | null;
  avg_ret_5d: number | null;
  avg_ret_20d: number | null;
}
export interface JournalStatsOut {
  days_back: number;
  total_trades: number;
  by_reason: JournalReasonStat[];
  overall_5d_win: number | null;
  overall_20d_win: number | null;
}

// #6 主力关注度（批量）
export interface AttentionBatchItem {
  ts_code: string;
  score_30d: number;
  top_list_30d: number;
  big_money_days_30d: number;
}
export interface AttentionBatchOut {
  items: AttentionBatchItem[];
}

export interface PeerRow {
  ts_code: string;
  name: string | null;
  composite_score: number | null;
  rank_rs_industry: number | null;
  rank_mom_20d: number | null;
  revenue_latest: number | null;
  revenue_yoy: number | null;
  n_income_latest: number | null;
  n_income_yoy: number | null;
  is_self: boolean;
}
export interface PeerCompareOut {
  industry: string | null;
  n_peers: number;
  self_rank: number | null;
  rows: PeerRow[];
}

export interface TimelineEvent {
  date: string;
  type: "news" | "anomaly" | "minute_alert" | "top_list" | "earnings";
  title: string;
  impact: "positive" | "negative" | "neutral" | null;
  severity: "critical" | "warning" | "info" | null;
  payload: Record<string, unknown>;
}
export interface TimelineOut {
  days_back: number;
  events: TimelineEvent[];
}

export interface AttentionMetric {
  days_back: number;
  top_list_count: number;
  top_list_net_amount: number;
  big_money_in_days: number;
  big_money_in_amount: number;
  minute_alert_count: number;
  minute_alert_high_value: number;
  score: number;
}
export interface AttentionOut {
  ts_code: string;
  metrics: AttentionMetric[];
}

export type NewsEventType =
  | "earnings_beat" | "earnings_miss" | "earnings_preview"
  | "dividend" | "buyback"
  | "share_increase" | "share_decrease"
  | "m_and_a" | "big_order" | "mgmt_change"
  | "regulatory" | "restructuring" | "index_inclusion"
  | "other";

export type NewsImpact = "positive" | "negative" | "neutral";

export interface NewsOut {
  id: number;
  source: string;
  title: string;
  summary: string | null;
  url: string | null;
  published_at: string;
  sentiment: number | null;   // -1 / 0 / 1 / null
  event_type: NewsEventType | null;
  impact: NewsImpact | null;
  entities: string[] | null;
  cluster_id: number | null;
  cluster_role: "rep" | "dup" | null;
}

export interface PortfolioImpactItem {
  news_id: number;
  ts_code: string;
  name: string | null;
  title: string;
  event_type: NewsEventType | null;
  impact: NewsImpact | null;
  entities: string[] | null;
  published_at: string;
  cluster_id: number | null;
}

export interface PortfolioImpactOut {
  days_back: number;
  items: PortfolioImpactItem[];
}

export interface EventTypeCounts {
  days_back: number;
  counts: Record<string, number>;
}

export interface NewsStats {
  total_news: number;
  by_source: Record<string, number>;
}

export interface SentimentStats {
  total: number;
  tagged: number;
  untagged: number;
  distribution: Record<string, number>;
}

export interface QACitation {
  n: number;
  id: number;
  title: string;
  source: string;
  published_at: string;
  url: string | null;
  distance: number | null;
  sentiment: number | null;
}

export interface QAResponse {
  answer: string;
  context_size: number;
  used_ts_code: string | null;
  citations: QACitation[];
}

export function friendlyHttpError(status: number, raw: string, statusText = ""): string {
  const text = raw.trim();
  if (text.startsWith("<") || /<html|<body|bad gateway/i.test(text)) {
    return `${status} 服务暂时不可用，请稍后重试`;
  }
  try {
    const parsed = JSON.parse(text) as { detail?: unknown };
    if (typeof parsed.detail === "string" && parsed.detail.trim()) {
      return `${status} ${parsed.detail.trim().slice(0, 200)}`;
    }
    if (Array.isArray(parsed.detail)) {
      const messages = parsed.detail.flatMap((item: unknown) => item && typeof item === "object" && "msg" in item && typeof item.msg === "string" ? [item.msg.replace(/^Value error,\s*/, "")] : []);
      return `${status} ${[...new Set(messages)].join("；").slice(0, 300) || "设置未通过检查，请检查填写内容"}`;
    }
    return `${status} 请求未完成，请稍后重试`;
  } catch {
    // Fall back to a short plain-text response below.
  }
  return `${status} ${(text || statusText || "请求失败").slice(0, 200)}`;
}

async function postJSON<T>(url: string, body: unknown, timeoutMs = 60_000): Promise<T> {
  const { signal, clear } = timeoutSignal(timeoutMs);
  let res: Response;
  try {
    res = await fetch(`${BASE}${url}`, {
      method: "POST",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") {
      throw new Error(`请求超时 (${(timeoutMs / 1000).toFixed(0)}s) — LLM/服务响应慢；可重试或简化请求`);
    }
    throw e;
  } finally {
    clear();
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(friendlyHttpError(res.status, detail, res.statusText));
  }
  return (await res.json()) as T;
}

async function putJSON<T>(url: string, body: unknown): Promise<T> {
  const { signal, clear } = timeoutSignal(60_000);
  let res: Response;
  try {
    res = await fetch(`${BASE}${url}`, {
      method: "PUT",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw new Error("请求超时 (60s)");
    throw e;
  } finally {
    clear();
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(friendlyHttpError(res.status, detail, res.statusText));
  }
  return (await res.json()) as T;
}

// 识图图片上限（与后端 MAX_IMAGE_BYTES 一致），附图时即时校验
export const VISION_MAX_BYTES = 5 * 1024 * 1024;
export function checkVisionFile(f: File): string | null {
  if (f.size > VISION_MAX_BYTES) {
    return `图片超过 5MB（当前 ${(f.size / 1048576).toFixed(1)}MB），请重截或裁剪`;
  }
  return null;
}

// 识图上传前压缩：nginx 请求体上限 + 公网上行慢，截图原图常 2-5MB；
// 长边 ≤1800px 的 JPEG 对 VL 模型识别足够。压缩失败则原样上传（错误会正常显示）。
const VISION_UPLOAD_TARGET = 900_000;
async function compressScreenshot(file: File): Promise<Blob> {
  if (file.size <= VISION_UPLOAD_TARGET) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1800 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d")!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close();
    let blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (blob && blob.size > VISION_UPLOAD_TARGET) {
      blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.7));
    }
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

async function deleteJSON(url: string): Promise<void> {
  const res = await fetch(`${BASE}${url}`, { method: "DELETE", credentials: CREDS });
  if (!res.ok && res.status !== 204) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status} ${detail.slice(0, 200)}`);
  }
}

export interface BatchAddResult {
  requested: number;
  added: number;
  already: number;
  invalid: string[];
  total_now: number;
}

export interface WatchlistItem {
  ts_code: string;
  name: string | null;
  industry: string | null;
  note: string | null;
  group_name: string;
  tags: string[];
  added_at: string;
}

export interface WatchlistQuote {
  ts_code: string;
  name: string | null;
  industry: string | null;
  note: string | null;
  group_name: string;
  tags: string[];
  last_trade_date: string | null;
  close: number | null;
  pct_chg: number | null;
  vol: number | null;
  is_realtime?: boolean;
  quote_time?: string | null;
  open?: number | null;
  high?: number | null;
  low?: number | null;
}

export interface GroupSummary {
  group_name: string;
  n_stocks: number;
}

export interface TagSummary {
  tag: string;
  n_stocks: number;
}

export interface BriefingMomentum {
  last_close: number;
  last_pct: number | null;
  pct_5d: number | null;
  ma5: number | null;
  ma10: number | null;
  vol_ratio_5d: number | null;
  n_klines: number;
  rs_5d?: number | null;
  rs_20d?: number | null;
  rs_benchmark?: string | null;
}

export interface BriefingOut {
  ts_code: string;
  name: string | null;
  industry: string | null;
  biz_date: string;
  summary: string;
  payload: {
    momentum: BriefingMomentum;
    news_ids: number[];
    news_count: number;
    klines_count: number;
  };
  model: string | null;
  created_at: string;
}

export interface GenerateAck {
  accepted: boolean;
  biz_date: string | null;
  targets: number;
}

export type PatternScopeType = "all" | "watchlist" | "market" | "industry";

export interface PatternScope {
  type: PatternScopeType;
  values: string[];
}

export interface PatternSampleOut {
  matching_mode?: "price" | "composite";
  id: number;
  name: string;
  ts_code: string;
  stock_name: string | null;
  start_date: string;
  end_date: string;
  window_n: number;
  enabled: boolean;
  push_enabled: boolean;
  push_max_distance: number | null;
  tags: string[];
  scope: PatternScope;
  scales: number[];
  created_at: string;
  norm_curve?: number[];
}

export interface PatternMatch {
  match_details?: PatternMatchDetails | null;
  ts_code: string;
  name: string | null;
  industry: string | null;
  match_end_date: string;
  dtw_distance: number;
  similarity: number;
  match_window_n: number;
}

export interface PatternMatchCurve {
  match_details?: PatternMatchDetails | null;
  sample_volume_curve?: number[] | null;
  match_volume_curve?: number[] | null;
  ts_code: string;
  name: string | null;
  sample_curve: number[];
  match_curve: number[];
  match_window_n: number;
  dtw_distance: number;
  similarity: number;
}

export interface PatternTodayAlert {
  sample_id: number;
  sample_name: string;
  matches: PatternMatch[];
}

export interface PatternMatchDetails {
  mode: "price" | "composite";
  version: string;
  score?: number;
  scores?: { price: number; volume: number; candle: number };
  weights?: { price: number; volume: number; candle: number };
  reasons?: string[];
}

export interface PatternScopeOptions {
  markets: string[];
  industries: string[];
}

export interface AnalogEpisode { ts_code: string; b_date: string; a_len?: number; }
export interface AnalogMatch {
  ts_code: string; dtw_distance: number;
  name?: string | null; industry?: string | null; similarity?: number;
  realized?: number | null;   // 历史回测模式下：asof→asof+c_len 的真实 B→C 收益%（未来未知则 null）
  curve?: number[];           // 与原型 DTW 对比的归一化窗口（0~1，长度=proto_len），前端叠图复核
}
export interface AnalogDist {
  n: number; win_rate?: number; mean?: number; median?: number;
  p25?: number; p75?: number; p10?: number; p90?: number; min?: number; max?: number;
}
export interface AnalogForecastResult {
  ok: boolean; note?: string | null;
  prototype?: number[] | null; proto_len?: number | null; n_members?: number | null;
  matches?: AnalogMatch[] | null;
  forecast?: AnalogDist | null; batch_outcome?: AnalogDist | null;
  de_market?: boolean | null; c_len?: number | null;
  scanned_windows?: number | null; asof?: string | null;
}

export interface AnalogContext {
  ts_code: string;
  financials: { roe: number | null; or_yoy: number | null; netprofit_yoy: number | null; gross_margin: number | null; debt: number | null; end_date: string } | null;
  valuation: { pe_ttm: number | null; pb: number | null; dv_ratio: number | null; trade_date: string } | null;
  moneyflow_20d_wan: number | null;
  toplist_30d: { n: number; net_wan: number | null };
  news: { title: string; source: string | null; at: string | null; sentiment: number | null }[];
}

export interface AnalogTrackItem {
  id: number; ts_code: string; name: string | null; group_name: string; de_market: boolean;
  base_date: string; c_len: number; elapsed: number;
  pred_median: number | null; pred_p25: number | null; pred_p75: number | null; pred_win_rate: number | null;
  realized: number | null; state: string; status: string; verdict: string | null; created_at: string;
}

export interface NotificationItem {
  id: number;
  category: string;
  title: string;
  body: string;
  severity: AlertSeverity;
  url: string | null;
  read: boolean;
  created_at: string;
}

export type BacktestStrategy = "ma_cross" | "breakout" | "pattern_signal" | "trend_factor";
export type TrendRule =
  | "threshold_above"
  | "threshold_below"
  | "cross_up"
  | "cross_down"
  | "slope_positive"
  | "slope_negative"
  | "slope_cross_up"
  | "slope_cross_down";

export interface BacktestTrade {
  trade_date: string;
  side: "buy" | "sell";
  price: number;
  equity_after: number;
}

export interface GridResultItem {
  params: Record<string, number | string>;
  final_return_pct: number | null;
  annual_return_pct: number | null;
  sharpe: number | null;
  sortino: number | null;
  calmar: number | null;
  max_drawdown_pct: number | null;
  n_days: number;
  error: string | null;
}
export interface GridSearchResult {
  ts_codes: string[];
  strategy: string;
  rebalance: string;
  n_combos: number;
  best_sharpe: GridResultItem | null;
  best_return: GridResultItem | null;
  best_calmar: GridResultItem | null;
  items: GridResultItem[];
}

export interface PortfolioBacktestPerStock {
  ts_code: string;
  name: string | null;
  weight: number;
  final_return_pct: number;
  sharpe: number | null;
  max_drawdown_pct: number;
  n_trades: number;
  win_rate: number | null;
}

export interface PortfolioBacktestResult {
  ts_codes: string[];
  weights: Record<string, number>;
  strategy: BacktestStrategy;
  params: Record<string, unknown>;
  rebalance: "none" | "monthly" | "quarterly" | "daily";
  n_rebalances: number;
  start_date: string;
  end_date: string;
  n_days: number;
  final_return_pct: number;
  benchmark_return_pct: number;
  index_benchmark: string | null;
  index_benchmark_return_pct: number | null;
  annual_return_pct: number | null;
  sharpe: number | null;
  sortino: number | null;
  calmar: number | null;
  max_drawdown_pct: number;
  per_stock: PortfolioBacktestPerStock[];
  equity_curve: [string, number][];
}

export interface BacktestResult {
  ts_code: string;
  stock_name: string | null;
  strategy: BacktestStrategy;
  params: Record<string, unknown>;
  start_date: string;
  end_date: string;
  n_klines: number;
  n_trades: number;
  n_round_trips: number;
  n_blocked_signals: number;
  final_return_pct: number;
  benchmark_return_pct: number;
  index_benchmark: string | null;
  index_benchmark_return_pct: number | null;
  max_drawdown_pct: number;
  annual_return_pct: number | null;
  sharpe: number | null;
  sortino: number | null;
  calmar: number | null;
  win_rate: number | null;
  profit_loss_ratio: number | null;
  fee_rate: number;
  t_plus_1: boolean;
  block_limit: boolean;
  equity_curve: [string, number][];
  trades: BacktestTrade[];
}

export interface AnomalyOut {
  ts_code: string;
  name: string | null;
  industry: string | null;
  biz_date: string;
  pct_chg: number;
  attribution: string;
  payload: {
    news_ids: number[];
    news_count: number;
    threshold: number;
    fina_ann_date?: string | null;
    fina_end_date?: string | null;
    top_list_reasons?: string[];
    index_pct?: number | null;
    index_benchmark?: string | null;
    // M7 结构化归因字段（旧记录可能没有，全 optional）
    primary_driver?: "earnings" | "top_list" | "policy" | "news" | "sector" | "none" | null;
    primary_driver_label?: string | null;
    confidence?: number | null;
    evidence_news_ids?: number[];
    sector_alignment?: "in_line" | "outperform" | "underperform" | "unknown" | null;
    // M7 #4 因子归因
    factor_composite?: number | null;
    factor_ranks?: {
      mom_5d?: number | null;
      mom_20d?: number | null;
      rev_5d?: number | null;
      rs_20d?: number | null;
      vol_5d?: number | null;
      price_pos?: number | null;
      mdd?: number | null;
    } | null;
  };
  model: string | null;
  created_at: string;
}

export interface StockSummaryOut {
  ts_code: string;
  name: string | null;
  industry: string | null;
  summary: string;
  payload: {
    long_term: {
      last_close: number;
      last_pct: number | null;
      pct_30d: number | null;
      pct_60d: number | null;
      pct_ytd: number | null;
      high_52w: number;
      low_52w: number;
      pct_below_high_52w: number;
      pct_above_low_52w: number;
      ma20: number | null;
      ma60: number | null;
      max_drawdown: number | null;
      avg_amount_30d: number | null;
      n_klines: number;
      rs_30d?: number | null;
      rs_60d?: number | null;
      rs_benchmark?: string | null;
    };
    news: {
      count: number;
      pos: number;
      neu: number;
      neg: number;
      untagged: number;
    };
    asof_trade_date: string;
  };
  model: string | null;
  generated_at: string;
  age_seconds: number;
  stale: boolean;
}

// ── M9: 多用户多账户 ────────────────────────────────────────

export interface AccountOut {
  id: number;
  broker: string;
  name: string;
  account_no_masked: string | null;
  currency: string;
  cash_balance: number;
  is_default: boolean;
  is_paper: boolean;
  active: boolean;
  created_at: string;
}

export interface MeOut {
  id: number;
  username: string;
  display_name: string | null;
  role: string;
  preferences: Record<string, unknown>;
  accounts: AccountOut[];
}

export interface InviteCodeOut {
  id: number;
  code: string;
  created_by_user_id: number;
  created_by_username: string | null;
  created_at: string;
  expires_at: string;
  used_by_user_id: number | null;
  used_by_username: string | null;
  used_at: string | null;
  revoked: boolean;
  note: string | null;
  status: "active" | "used" | "expired" | "revoked";
}

export interface AccountCreate {
  name: string;
  broker?: string;
  account_no_masked?: string | null;
  currency?: "CNY" | "HKD" | "USD";
  cash_balance?: number;
  is_default?: boolean;
  is_paper?: boolean;
}

export interface AccountPatch {
  name?: string;
  broker?: string;
  account_no_masked?: string | null;
  currency?: "CNY" | "HKD" | "USD";
  cash_balance?: number;
  is_default?: boolean;
  is_paper?: boolean;
  active?: boolean;
}

export interface CashLedgerOut {
  id: number;
  amount: number;
  kind: string;
  occurred_at: string;
  note: string | null;
}

export interface PaperAccountPerformancePoint {
  trade_date: string;
  cash: number;
  market_value: number;
  equity: number;
  external_cash_flow: number;
  cumulative_external_cash: number;
  realized_pnl: number;
  unrealized_pnl: number;
  fees: number;
  net_pnl: number;
  daily_return_pct: number | null;
  twrr_index: number;
}

export interface PaperStrategyAttribution {
  strategy_id: number;
  strategy_name: string | null;
  n_trades: number;
  gross_buys: number;
  gross_sells: number;
  fees: number;
  realized_pnl: number;
  unrealized_pnl: number;
  net_pnl: number;
  capital_employed: number;
  return_on_capital_pct: number | null;
  n_round_trips: number;
  win_rate_pct: number | null;
  open_positions: number;
}

export interface PaperAccountPerformance {
  account_id: number;
  account_name: string;
  currency: string;
  as_of: string;
  metrics: {
    cash: number;
    market_value: number;
    equity: number;
    net_deposits: number;
    net_pnl: number;
    total_return_pct: number;
    annualized_return_pct: number | null;
    max_drawdown_pct: number;
    realized_pnl: number;
    unrealized_pnl: number;
    fees: number;
    n_trades: number;
  };
  curve: PaperAccountPerformancePoint[];
  strategies: PaperStrategyAttribution[];
}

// 主题雷达（theme radar 增量2 持久化）
export interface ThemeScoreRow {
  rank: number | null;
  board_name: string;
  score: number | null;
  mom5: number | null;
  mom20: number | null;
  accel: number | null;
  flow5_wan: number | null;
  breadth: number | null;
  leading_stock: string | null;
  state: "latent" | "warming" | "active" | "overheated" | "cooling" | "neutral" | null;
  rank_change_5d: number | null;
}
export interface ThemeScoreOut {
  biz_date: string | null;
  rows: ThemeScoreRow[];
}
export interface ThemeScoreHistoryPoint {
  biz_date: string;
  rank: number | null;
  score: number | null;
  accel: number | null;
  mom5: number | null;
  mom20: number | null;
  flow5_wan: number | null;
  breadth: number | null;
  state: string;
  rank_change_5d: number | null;
}
export interface ThemeBacktestState {
  state: string;
  n: number;
  avg_forward_return: number;
  avg_excess_return: number;
  hit_rate: number;
}
export interface ThemeBacktestOut {
  forward_days: number;
  lookback_days: number;
  n: number;
  states: ThemeBacktestState[];
}
export interface ThemeCapitalRow {
  board_name: string;
  score: number;
  label: "strong" | "watch" | "none";
  positive_flow_days10: number;
  flow10: number;
  flow_rank_pct: number;
  amount_ratio: number;
  contraction_ratio: number;
  mom5: number;
  mom20: number;
  breadth: number;
}
export interface ThemeCapitalOut { biz_date: string | null; rows: ThemeCapitalRow[]; note: string; }
export interface ThemeCapitalBacktestOut {
  forward_days: number;
  threshold: number;
  signal: { n: number; avg_forward_return: number | null; avg_excess_return: number | null; hit_rate: number | null };
  all: { n: number; avg_forward_return: number | null; avg_excess_return: number | null; hit_rate: number | null };
}
export interface ThemeLongHistoryRow {
  ts_code: string;
  board_name: string;
  score: number;
  label: "strong" | "watch" | "none";
  mom5: number;
  mom20: number;
  accel: number;
  volume_ratio: number;
  turnover_ratio: number;
  contraction_ratio: number;
}
export interface ThemeLongHistoryOut {
  biz_date: string | null;
  start_date: string | null;
  rows: ThemeLongHistoryRow[];
  note: string;
}
export interface ThemeLongBacktestOut {
  forward_days: number;
  lookback_days: number;
  threshold: number;
  signal: { n: number; avg_forward_return: number | null; avg_excess_return: number | null; hit_rate: number | null };
}
export interface ThemePolicyRow {
  biz_date: string;
  theme: string;
  sectors: string | null;
  basis: string | null;
}
export interface ThemePolicyOut {
  rows: ThemePolicyRow[];
  n_news_latest: number | null;
}
export interface InterestRow {
  id: number;
  kind: string;
  value: string;
}
export interface ArxivTrendRow {
  theme: string;
  accel_z: number | null;
  latest_period: string | null;
  latest_count: number | null;
  spark: number[];
}
export interface PatentTrendRow {
  theme: string;
  accel_z: number | null;
  latest_period: string | null;
  latest_count: number | null;
  spark: number[];
}
export interface IntelRow {
  id: number;
  theme: string | null;
  title: string;
  summary: string | null;
  url: string;
  themes: string[] | null;
  importance: number | null;
  invest_relevant: boolean | null;
  published_at: string | null;
}

// ── ETF 资金监控 ──────────────────────────────────────────────

export interface EtfCell {
  amt_yi: number | null;
  pct: number | null;
}
export interface EtfRadarItem {
  ts_code: string;
  name: string;
  category: string | null;
  scale_yi: number | null;
  cells: Record<string, EtfCell>;   // d1/w1/w2/m1/m3/m6/ytd + 可选 custom
  label: string | null;
}
export interface EtfRadarOut {
  asof: string;
  n_etf: number;
  total_scale_yi: number;
  total_d1_yi: number;
  total_w1_yi: number;
  custom_range: { start: string; end: string } | null;
  items: EtfRadarItem[];
  note: string;
}
export interface EtfMarketOut {
  asof: string;
  since: string;
  daily: { date: string; amt_yi: number; cum_yi: number }[];
  cum_yi: number;
  contrib: { category: string; amt_yi: number }[];
  windows: Record<string, number>;
  direction: string;
}
export interface EtfRotationOut {
  asof: string;
  window: string;
  outflow: { category: string; cur: number; prev: number }[];
  inflow: { category: string; cur: number; prev: number }[];
  net_yi: number;
  speed_pct: number | null;
  crowding_pct: number | null;
  main_line: { prev: string | null; cur: string | null };
  note: string;
}
export interface EtfVixItem {
  ts_code: string;
  name: string;
  vix20: number | null;
  vix60: number | null;
  vix20_chg5d: number | null;
  day_pct: number;
  pe_ttm: number | null;
  pe_pctile: number | null;
  high_vol: boolean;
}
export interface EtfVixOut {
  asof: string | null;
  vix20_median: number | null;
  items: EtfVixItem[];
  note: string;
}
export interface EtfVixDetailOut {
  ts_code: string;
  name: string;
  series: { date: string; close: number; vix20: number | null; vix60: number | null; pe_ttm: number | null; pe_pctile: number | null }[];
}
export interface EtfPressureItem {
  ts_code: string;
  name: string | null;
  industry: string | null;
  n_etfs: number | null;
  hold_shares_wan: number | null;
  chg_shares_5d_wan: number | null;
  chg_shares_20d_wan: number | null;
  chg_amt_5d_yi: number | null;
  chg_amt_20d_yi: number | null;
  basis_date: string | null;
  rzye_yi: number | null;
  rqye_yi: number | null;
  circ_mv_yi: number | null;
  rz_pct: number | null;
}
export interface EtfPressureOut {
  asof: string;
  items: EtfPressureItem[];
  sum_chg_20d_yi: number;
  note: string;
}
export interface EtfPressureDetailOut {
  ts_code: string;
  name: string | null;
  industry: string | null;
  asof: string;
  n_etfs: number | null;
  hold_shares_wan: number | null;
  chg_shares_20d_wan: number | null;
  chg_amt_20d_yi: number | null;
  basis_date: string | null;
  etfs: { ts_code: string; name: string; basis_date: string; mkv_ratio: number | null; held_shares_wan: number; chg_shares_20d_wan: number | null }[];
  series: { date: string; chg_shares_wan: number; cum_shares_wan: number }[];
}

// ── 指标浏览器 + 自定义表达式指标 ──────────────────────────────

export interface IndicatorPoint {
  date: string;
  value: number | null;
}
export interface IndicatorSeries {
  label: string;
  unit?: string;
  series: IndicatorPoint[];
}
export interface BrowseOut {
  symbol: string;
  kind: string;
  name: string | null;
  kline: { date: string; open: number; high: number; low: number; close: number; vol: number | null }[];
  indicators: Record<string, IndicatorSeries>;
  factors: Record<string, IndicatorSeries>;
  available: { id: string; label: string; unit: string; hint: string }[];
  note: string;
}
export interface CustomFactorOut {
  id: number;
  name: string;
  expression: string;
  description: string | null;
  status: string;
  error_msg: string | null;
  last_computed: string | null;
  n_days: number;
  creator: string | null;
  scope: "mine" | "system";
  rank_ic: number | null;
  icir: number | null;
  has_signal: boolean | null;
  ic_checked_at: string | null;
}
export interface FactorCrossSectionOut {
  asof: string;
  items: { ts_code: string; name: string | null; industry: string | null; value: number }[];
}
export interface FactorDraftOut {
  name: string;
  expression: string;
  rationale: string;
  validated: boolean;
  persisted: boolean;
  note: string;
}
export interface FactorAnalysisOut {
  factor: { id: number; name: string; version_id: number | null; version_no: number | null; spec_hash: string | null };
  universe: { mode: string; dataset_id: number | null; requested_stocks: number };
  interval: { start: string; end: string; horizon: number };
  preprocessing: { winsorize: string; standardize: string; neutralize: string; point_in_time: boolean; input_count: number; output_count: number };
  evaluation: { ok: boolean; n_days?: number; n_samples?: number; rank_ic_mean?: number | null; rank_ic_icir?: number | null; quantile_returns?: Array<number | null>; long_short_spread?: number | null; oos_gate?: { passed: boolean; reason: string } };
  portfolio: { ok: boolean; metrics?: { total_return_pct?: number; annualized_pct?: number | null; sharpe?: number | null; max_drawdown_pct?: number }; avg_turnover?: number | null; curve?: Array<{ date: string; nav: number }> };
  reuse_contract: { factor_version_pinned: boolean; research_backtest_paper_definition: string };
}

export interface CapitalBottomSignal {
  trade_date: string;
  stage: "opportunity" | "confirmed" | "trend_broken";
  price: number;
  daily_score: number;
  confirmation_time: string | null;
  minute_parts: Record<string, boolean> | null;
}
export interface PatternMasterDefinition {
  key: string; name: string; category: string; description: string;
  confirmation: string; invalidation: string; min_bars: number;
  version: number; enabled: boolean; params: Record<string, number>;
}
export interface PatternMasterHit {
  ts_code: string; name: string | null; industry: string | null; trade_date: string;
  pattern: string; pattern_name: string; status: "forming" | "confirmed" | "invalid";
  confidence: number; trigger_price: number | null; support_price: number | null; evidence: string[];
}
export interface PatternReplayStatus {
  accepted?: boolean; state: "idle" | "queued" | "running" | "complete" | "failed" | "cancelled";
  processed_stocks: number; total_stocks: number; snapshots: number; members: number;
  patterns?: number; start_date?: string; end_date?: string; error?: string | null;
}
export interface PatternReplaySimulation {
  pattern_key: string; rule_name: string; entry_basis: "next_open"; initial_cash: number;
  research_only: boolean; writes_trading_tables: false;
  max_positions: number; fee_rate: number; stamp_tax_rate: number; slippage_bps: number; confirmed_only: boolean;
  metrics: { total_return_pct?: number; annualized_return_pct?: number; max_drawdown_pct?: number;
    annualized_volatility_pct?: number; sharpe?: number | null; turnover?: number;
    benchmark_return_pct?: number; excess_return_pct?: number };
  days: Array<{ signal_date: string | null; execution_date: string | null; selected: string[];
    orders: Array<{ ts_code: string; side: "buy" | "sell"; status: string; qty?: number; price?: number; amount?: number; fee?: number; reason?: string }>;
    status: string; cash?: number; market_value?: number; nav?: number; positions?: Record<string, number>;
    stale_price_codes?: string[] }>;
}
export interface ThemeBoardReviewEvent {
  biz_date: string;
  rank: number | null;
  score: number | null;
  state: string;
  rank_change_5d: number | null;
  fwd5: number | null;
  fwd10: number | null;
  fwd20: number | null;
}
export interface ThemeBoardReview {
  board: string;
  events: ThemeBoardReviewEvent[];
  summary: Record<"t5" | "t10" | "t20", { n: number; avg_return: number | null; win_rate: number | null }>;
  note: string;
}
export interface TdxPreviewOut {
  symbol: string;
  output_name: string;
  output_names: string[];
  output_kinds: Record<string, "line" | "marker">;
  lines: Record<string, IndicatorPoint[]>;
  audit: { future_safe: boolean; functions: string[]; fields: string[]; max_lookback: number; adjustment: string; dialect: "tdx" | "dzh" };
}

export const api = {
  llmTrainingStats: (days = 7) =>
    fetchJSON<LlmTrainingStats>(`/api/v1/admin/llm-training/stats?days=${days}`),
  llmTrainingTraces: (params: { reviewed?: boolean; task?: string; limit?: number } = {}) => {
    const query = new URLSearchParams({ limit: String(params.limit ?? 100) });
    if (params.reviewed !== undefined) query.set("reviewed", String(params.reviewed));
    if (params.task) query.set("task", params.task);
    return fetchJSON<{ items: LlmTraceSummary[]; limit: number; offset: number }>(
      `/api/v1/admin/llm-training/traces?${query}`,
    );
  },
  llmTrainingTrace: (traceId: string) =>
    fetchJSON<LlmTraceDetail>(`/api/v1/admin/llm-training/traces/${encodeURIComponent(traceId)}`),
  scorePendingLlmTraces: (limit = 500) =>
    postJSON<{ scored: number }>(`/api/v1/admin/llm-training/score-pending?limit=${limit}`, {}),
  reviewLlmTrace: (
    traceId: string,
    body: { decision: "approve" | "reject"; note: string; corrected_response?: Record<string, unknown> },
  ) => postJSON<LlmTraceSummary>(
    `/api/v1/admin/llm-training/traces/${encodeURIComponent(traceId)}/review`, body,
  ),
  researchTasks: (kind: string, targetId?: number) => fetchJSON<ResearchTask[]>(`/api/v1/research-tasks?kind=${encodeURIComponent(kind)}${targetId === undefined ? "" : `&target_id=${targetId}`}`),
  retryResearchTask: (id: string) => postJSON<{task_id: string}>(`/api/v1/research-tasks/${encodeURIComponent(id)}/retry`, {}),
  notificationDeliveries: () => fetchJSON<NotificationDelivery[]>("/api/v1/notifications/deliveries"),
  // 指标浏览器 + 自定义指标
  indicatorsBrowse: (params: { symbol: string; kind: string; days: number; indicators: string[]; factorIds: number[]; start?: string; end?: string }) => {
    const q = new URLSearchParams({
      symbol: params.symbol, kind: params.kind, days: String(params.days),
      indicators: params.indicators.join(","), factor_ids: params.factorIds.join(","),
    });
    if (params.start) q.set("start", params.start);
    if (params.end) q.set("end", params.end);
    return fetchJSON<BrowseOut>(`/api/v1/indicators/browse?${q}`, 90_000);
  },
  listCustomFactors: () => fetchJSON<CustomFactorOut[]>(`/api/v1/indicators/factors`),
  generateCustomFactor: (description: string) =>
    postJSON<FactorDraftOut>(`/api/v1/indicators/factors/generate`, { description }),
  createCustomFactor: (req: { name: string; expression: string; description?: string; backfill_years?: number }) =>
    postJSON<{ id: number; name: string; status: string; note: string }>(`/api/v1/indicators/factors`, req),
  deleteCustomFactor: (id: number) => deleteJSON(`/api/v1/indicators/factors/${id}`),
  recomputeCustomFactor: (id: number) =>
    postJSON<{ id: number; status: string }>(`/api/v1/indicators/factors/${id}/recompute`, {}),
  previewTdxFormula: (req: { symbol: string; formula: string; days?: number; dialect?: "auto" | "tdx" | "dzh" }) =>
    postJSON<TdxPreviewOut>(`/api/v1/indicators/tdx-preview`, {
      symbol: req.symbol, formula: req.formula, days: req.days ?? 750, dialect: req.dialect ?? "auto",
    }),
  factorCrossSection: (id: number, ascending = false) =>
    fetchJSON<FactorCrossSectionOut>(`/api/v1/indicators/factors/${id}/cross-section?ascending=${ascending}`),
  analyzeFactor: (id: number, req: { start: string; end: string; horizon: number; quantiles: number; winsorize: string; standardize: string; neutralize: string; direction: number; portfolio_size: number; rebalance_days: number; max_turnover?: number }) =>
    postJSON<FactorAnalysisOut>(`/api/v1/indicators/factors/${id}/analyze`, req, 180_000),

  // ETF 资金监控
  etfRadar: (params: { limit?: number; sort?: string; category?: string; customStart?: string; customEnd?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.sort) q.set("sort", params.sort);
    if (params.category) q.set("category", params.category);
    if (params.customStart) q.set("custom_start", params.customStart);
    if (params.customEnd) q.set("custom_end", params.customEnd);
    return fetchJSON<EtfRadarOut>(`/api/v1/etf/radar?${q}`);
  },
  etfMarket: (since?: string) =>
    fetchJSON<EtfMarketOut>(`/api/v1/etf/market${since ? `?since=${since}` : ""}`),
  etfRotation: (window: string) => fetchJSON<EtfRotationOut>(`/api/v1/etf/rotation?window=${window}`),
  etfVix: () => fetchJSON<EtfVixOut>(`/api/v1/etf/vix`),
  etfVixDetail: (tsCode: string, days = 504) =>
    fetchJSON<EtfVixDetailOut>(`/api/v1/etf/vix/${tsCode}?days=${days}`),
  etfPressure: (params: { limit?: number; direction?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.direction) q.set("direction", params.direction);
    return fetchJSON<EtfPressureOut>(`/api/v1/etf/pressure?${q}`);
  },
  etfPressureDetail: (tsCode: string) =>
    fetchJSON<EtfPressureDetailOut>(`/api/v1/etf/pressure/${tsCode}`),

  // 认证
  authMe: async (): Promise<MeOut | null> => {
    const r = await fetch(`${BASE}/api/v1/auth/me?_session=${Date.now()}`, { credentials: CREDS, cache: "no-store" });
    if (r.status === 401) return null;
    if (!r.ok) throw new Error(`${r.status}`);
    return (await r.json()) as MeOut;
  },
  authLogin: (username: string, password: string, remember = false) =>
    postJSON<MeOut>(`/api/v1/auth/login`, { username, password, remember }),
  authRegister: (req: { invite_code: string; username: string; password: string; display_name?: string }) =>
    postJSON<MeOut>(`/api/v1/auth/register`, req),
  authLogout: () => postJSON<{ ok: boolean }>(`/api/v1/auth/logout`, {}),
  authChangePassword: (old_password: string, new_password: string) =>
    postJSON<{ ok: boolean }>(`/api/v1/auth/me/password`, { old_password, new_password }),

  // 邀请码管理（仅 owner 看得到）
  adminListInvites: () => fetchJSON<InviteCodeOut[]>(`/api/v1/admin/invites`),
  adminCreateInvite: (req: { note?: string; ttl_days?: number }) =>
    postJSON<InviteCodeOut>(`/api/v1/admin/invites`, req),
  adminRevokeInvite: (id: number) => deleteJSON(`/api/v1/admin/invites/${id}`),
  authPatchPreferences: (preferences: Record<string, unknown>) =>
    fetch(`${BASE}/api/v1/auth/me/preferences`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ preferences }),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<MeOut>;
    }),

  // 账户
  listAccounts: () => fetchJSON<AccountOut[]>(`/api/v1/accounts`),
  createAccount: (req: AccountCreate) => postJSON<AccountOut>(`/api/v1/accounts`, req),
  patchAccount: (id: number, patch: AccountPatch) =>
    fetch(`${BASE}/api/v1/accounts/${id}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<AccountOut>;
    }),
  deleteAccount: (id: number) => deleteJSON(`/api/v1/accounts/${id}`),
  createPaperCashTransfer: (id: number, req: { kind: "deposit" | "withdrawal"; amount: number; note?: string }) =>
    postJSON<CashLedgerOut>(`/api/v1/accounts/${id}/cash-transfers`, req),
  paperCashLedger: (id: number) => fetchJSON<CashLedgerOut[]>(`/api/v1/accounts/${id}/cash-ledger`),
  paperAccountPerformance: (id: number, days = 365) =>
    fetchJSON<PaperAccountPerformance>(`/api/v1/accounts/${id}/performance?days=${days}`),

  listStocks: (params: { limit?: number; offset?: number; industry?: string; area?: string; market?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    if (params.industry) q.set("industry", params.industry);
    if (params.area) q.set("area", params.area);
    if (params.market) q.set("market", params.market);
    return fetchJSON<StockOut[]>(`/api/v1/stocks?${q}`);
  },
  searchStocks: (q: string, limit = 20) =>
    fetchJSON<StockOut[]>(`/api/v1/stocks/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  listIndustries: (min_stocks = 1) =>
    fetchJSON<IndustrySummary[]>(`/api/v1/stocks/industries?min_stocks=${min_stocks}`),
  getStock: (tsCode: string) => fetchJSON<StockOut>(`/api/v1/stocks/${tsCode}`),
  stockRealtime: (tsCode: string) => fetchJSON<RealtimeQuoteOut>(`/api/v1/stocks/${tsCode}/realtime`),
  realtimeQuotes: (ts_codes: string[]) =>
    fetchJSON<RealtimeQuoteOut[]>(`/api/v1/realtime/quotes?ts_codes=${encodeURIComponent(ts_codes.join(","))}`),
  getStats: (tsCode: string) => fetchJSON<StockStats>(`/api/v1/stocks/${tsCode}/stats`),
  getPeers: (tsCode: string, limit = 20) =>
    fetchJSON<PeerCompareOut>(`/api/v1/stocks/${tsCode}/peers?limit=${limit}`),
  getTimeline: (tsCode: string, days_back = 60) =>
    fetchJSON<TimelineOut>(`/api/v1/stocks/${tsCode}/timeline?days_back=${days_back}`),
  getAttention: (tsCode: string) =>
    fetchJSON<AttentionOut>(`/api/v1/stocks/${tsCode}/attention`),
  dailyKlines: (tsCode: string, params: { start?: string; end?: string; limit?: number; adjustment?: "qfq" | "raw" | "hfq" } = {}) => {
    const q = new URLSearchParams();
    if (params.start) q.set("start", params.start);
    if (params.end) q.set("end", params.end);
    if (params.limit) q.set("limit", String(params.limit));
    if (params.adjustment) q.set("adjustment", params.adjustment);
    return fetchJSON<KlineRow[]>(`/api/v1/stocks/${tsCode}/klines/daily?${q}`);
  },
  capitalBottomSignals: (tsCode: string, params: { start?: string; end?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.start) q.set("start", params.start);
    if (params.end) q.set("end", params.end);
    return fetchJSON<CapitalBottomSignal[]>(`/api/v1/stocks/${tsCode}/signals/capital-bottom?${q}`);
  },
  indexKlines: (tsCode: string, params: { start?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.start) q.set("start", params.start);
    if (params.limit) q.set("limit", String(params.limit));
    return fetchJSON<{ trade_date: string; close: number; pct_chg: number | null }[]>(
      `/api/v1/stocks/index/${tsCode}/klines/daily?${q}`,
    );
  },

  // 新闻
  listNews: (params: { ts_code?: string; event_type?: NewsEventType; impact?: NewsImpact; fold_clusters?: boolean; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.ts_code) q.set("ts_code", params.ts_code);
    if (params.event_type) q.set("event_type", params.event_type);
    if (params.impact) q.set("impact", params.impact);
    if (params.fold_clusters) q.set("fold_clusters", "true");
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    return fetchJSON<NewsOut[]>(`/api/v1/news?${q}`);
  },
  searchNews: (q: string, limit = 50) =>
    fetchJSON<NewsOut[]>(`/api/v1/news/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  newsStats: () => fetchJSON<NewsStats>(`/api/v1/news/stats`),
  newsEventTypeCounts: (days_back = 7) =>
    fetchJSON<EventTypeCounts>(`/api/v1/news/event-types?days_back=${days_back}`),
  newsPortfolioImpact: (params: { days_back?: number; account_id?: number | null; only_high_impact?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.days_back) q.set("days_back", String(params.days_back));
    if (params.account_id != null) q.set("account_id", String(params.account_id));
    if (params.only_high_impact != null) q.set("only_high_impact", String(params.only_high_impact));
    return fetchJSON<PortfolioImpactOut>(`/api/v1/news/portfolio-impact?${q}`);
  },
  triggerNewsClassify: (limit = 200) =>
    postJSON<{ accepted: boolean }>(`/api/v1/news/classify/run?limit=${limit}`, {}),
  triggerNewsClusterRebuild: (days_back = 7) =>
    postJSON<{ accepted: boolean }>(`/api/v1/news/cluster/rebuild?days_back=${days_back}`, {}),

  // 情感
  sentimentStats: () => fetchJSON<SentimentStats>(`/api/v1/sentiment/stats`),

  // QA (RAG 启用：news_top_k=6 默认)；可选 tag/group 限定范围
  ask: (params: {
    question: string;
    ts_code?: string;
    tag?: string;
    group?: string;
    kline_days?: number;
    news_top_k?: number;
    news_days?: number;
  }) =>
    postJSON<QAResponse>(`/api/v1/qa`, {
      kline_days: 30, news_top_k: 6, ...params,
    }, 180_000),

  // Watchlist
  listWatchlist: (params: { group?: string; tag?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.group) q.set("group", params.group);
    if (params.tag) q.set("tag", params.tag);
    const qs = q.toString();
    return fetchJSON<WatchlistItem[]>(`/api/v1/watchlist${qs ? "?" + qs : ""}`);
  },
  listWatchlistGroups: () => fetchJSON<GroupSummary[]>(`/api/v1/watchlist/groups`),
  listWatchlistTags: () => fetchJSON<TagSummary[]>(`/api/v1/watchlist/tags`),
  watchlistQuotes: (params: { group?: string; tag?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.group) q.set("group", params.group);
    if (params.tag) q.set("tag", params.tag);
    const qs = q.toString();
    return fetchJSON<WatchlistQuote[]>(`/api/v1/watchlist/quotes${qs ? "?" + qs : ""}`);
  },
  batchTagsWatchlist: (ts_codes: string[], tags: string[], mode: "add" | "remove") =>
    postJSON<{ requested: number; updated: number; mode: string; tags: string[] }>(
      `/api/v1/watchlist/batch-tags`, { ts_codes, tags, mode },
    ),
  tagSuggestions: (ts_code: string) =>
    fetchJSON<{ tag: string; reason: string; confidence: number }[]>(
      `/api/v1/watchlist/${ts_code}/tag-suggestions`,
    ),
  scanByTagOptions: () =>
    fetchJSON<{ tag: string; label: string }[]>(`/api/v1/watchlist/scan-by-tag/options`),
  hotTags: () =>
    fetchJSON<{ tag: string; label: string; n_stocks: number; top3: { ts_code: string; name: string | null; industry: string | null }[] }[]>(
      `/api/v1/watchlist/hot-tags`,
    ),
  hotTagsHistory: (days = 7) =>
    fetchJSON<{ tag: string; points: { snapshot_date: string; n_stocks: number }[] }[]>(
      `/api/v1/watchlist/hot-tags/history?days=${days}`,
    ),
  scanByTag: (tag: string, limit = 50) =>
    fetchJSON<{ ts_code: string; name: string | null; industry: string | null; metric: string; value: number | null; reason?: string | null; as_of?: string | null; data_quality?: string }[]>(
      `/api/v1/watchlist/scan-by-tag?tag=${encodeURIComponent(tag)}&limit=${limit}`,
    ),
  compositeScreen: (criteria: CompositeScreenCriteria) =>
    postJSON<CompositeScreenResult>(`/api/v1/watchlist/screen`, criteria),
  triggerTagWeeklyScan: () =>
    postJSON<{ scanned: number; with_new_suggestions: number; details: object[] }>(
      `/api/v1/watchlist/tag-weekly-scan`, {},
    ),
  tagHistory: (ts_code: string, days = 90) =>
    fetchJSON<{ snapshot_date: string; user_tags: string[]; sys_tags: string[] }[]>(
      `/api/v1/watchlist/${ts_code}/tag-history?days=${days}`,
    ),
  tagLearnRecommendations: () =>
    fetchJSON<{ ts_code: string; name: string | null; industry: string | null; suggested_tag: string; density: number; n_evidence: number; evidence_codes: string[]; reason: string }[]>(
      `/api/v1/watchlist/tag-learn/recommendations`,
    ),
  addWatchlist: (ts_code: string, note?: string, group_name?: string) =>
    postJSON<WatchlistItem>(`/api/v1/watchlist`, { ts_code, note, group_name }),
  addWatchlistBatch: (codes: string[], note?: string, group_name?: string) =>
    postJSON<BatchAddResult>(`/api/v1/watchlist/batch`, { codes, note, group_name }),
  updateWatchlist: (ts_code: string, body: { note?: string; group_name?: string; tags?: string[] }) =>
    fetch(`/api/v1/watchlist/${ts_code}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return (await r.json()) as WatchlistItem;
    }),
  batchGroupWatchlist: (ts_codes: string[], group_name: string) =>
    postJSON<{ requested: number; updated: number; skipped: string[]; group_name: string }>(
      `/api/v1/watchlist/batch-group`, { ts_codes, group_name },
    ),
  autoGroupByIndustry: () =>
    postJSON<{ total: number; by_group: Record<string, number>; unclassified: number }>(
      `/api/v1/watchlist/auto-group-by-industry`, {},
    ),
  removeWatchlist: (ts_code: string) => deleteJSON(`/api/v1/watchlist/${ts_code}`),
  removeWatchlistBatch: (codes: string[]) =>
    postJSON<{ requested: number; removed: number; missing: string[]; total_now: number }>(
      `/api/v1/watchlist/batch-remove`, { codes },
    ),

  // Briefing
  listBriefings: (biz_date?: string) => {
    const q = new URLSearchParams();
    if (biz_date) q.set("biz_date", biz_date);
    return fetchJSON<BriefingOut[]>(`/api/v1/briefing?${q}`);
  },
  briefingDates: (limit = 30) =>
    fetchJSON<string[]>(`/api/v1/briefing/dates?limit=${limit}`),
  generateBriefing: (params: {
    biz_date?: string;
    ts_codes?: string[];
    force?: boolean;
  } = {}) => postJSON<GenerateAck>(`/api/v1/briefing/generate`, params),

  // 个股摘要：404 时静默返回 null（前端展示"暂无"）
  getStockSummary: async (ts_code: string): Promise<StockSummaryOut | null> => {
    const res = await fetch(`${BASE}/api/v1/summary/${ts_code}`, { credentials: CREDS });
    if (res.status === 404) return null;
    if (!res.ok) {
      const detail = await res.text().catch(() => res.statusText);
      throw new Error(`${res.status} ${detail.slice(0, 200)}`);
    }
    return (await res.json()) as StockSummaryOut;
  },
  generateStockSummary: (ts_code: string) =>
    postJSON<{ accepted: boolean; ts_code: string }>(
      `/api/v1/summary/${ts_code}/generate`,
      {},
      180_000,
    ),

  // 形态样本 + 匹配
  listPatterns: (enabled_only = false) => {
    const q = enabled_only ? "?enabled_only=true" : "";
    return fetchJSON<PatternSampleOut[]>(`/api/v1/patterns${q}`);
  },
  getPattern: (id: number) => fetchJSON<PatternSampleOut>(`/api/v1/patterns/${id}`),
  createPattern: (params: {
    matching_mode?: "price" | "composite";
    name: string;
    ts_code: string;
    start_date: string;
    end_date: string;
    scope?: PatternScope;
    scales?: number[];
  }) => postJSON<PatternSampleOut>(`/api/v1/patterns`, params),
  patchPattern: (
    id: number,
    params: {
      matching_mode?: "price" | "composite";
      name?: string;
      enabled?: boolean;
      push_enabled?: boolean;
      push_max_distance?: number;
      tags?: string[];
      scope?: PatternScope;
      scales?: number[];
    },
  ) =>
    fetch(`${BASE}/api/v1/patterns/${id}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${await r.text().catch(() => r.statusText)}`);
      return (await r.json()) as PatternSampleOut;
    }),
  patternScopeOptions: () => fetchJSON<PatternScopeOptions>(`/api/v1/patterns/scope/options`),
  deletePattern: (id: number) => deleteJSON(`/api/v1/patterns/${id}`),
  scanPattern: (id: number) =>
    postJSON<{ accepted: boolean; sample_id: number }>(
      `/api/v1/patterns/${id}/scan`,
      {},
    ),
  patternMatches: (id: number, biz_date?: string, limit = 30) => {
    const q = new URLSearchParams();
    if (biz_date) q.set("biz_date", biz_date);
    q.set("limit", String(limit));
    return fetchJSON<PatternMatch[]>(`/api/v1/patterns/${id}/matches?${q}`);
  },
  patternMatchCurve: (id: number, ts_code: string, biz_date?: string) => {
    const q = new URLSearchParams();
    if (biz_date) q.set("biz_date", biz_date);
    return fetchJSON<PatternMatchCurve>(
      `/api/v1/patterns/${id}/matches/${ts_code}/curve?${q}`,
    );
  },
  patternsTodayAlerts: (top_per_sample = 10) =>
    fetchJSON<PatternTodayAlert[]>(
      `/api/v1/patterns/alerts/today?top_per_sample=${top_per_sample}`,
    ),
  patternMasterCatalog: () => fetchJSON<PatternMasterDefinition[]>(`/api/v1/pattern-master/catalog`),
  updatePatternMasterRule: (key: string, body: { params?: Record<string, number>; enabled?: boolean; change_note: string }) =>
    putJSON<PatternMasterDefinition>(`/api/v1/pattern-master/rules/${encodeURIComponent(key)}`, body),
  patternMasterRuleVersions: (key: string) => fetchJSON<Array<{ version: number; params: Record<string, number>; enabled: boolean; change_note: string; effective_date: string; created_at: string }>>(`/api/v1/pattern-master/rules/${encodeURIComponent(key)}/versions`),
  patternMasterScan: (pattern: string, scope = "watchlist", value?: string, limit = 100) => {
    const q = new URLSearchParams({ pattern, scope, limit: String(limit) });
    if (value) q.set("value", value);
    return fetchJSON<PatternMasterHit[]>(`/api/v1/pattern-master/scan?${q}`, 180_000);
  },
  patternMasterReplayStatus: () =>
    fetchJSON<PatternReplayStatus>(`/api/v1/pattern-master/replay/status`),
  startPatternMasterReplay: (days = 250, pattern_keys?: string[], simulate = true) =>
    postJSON<PatternReplayStatus>(`/api/v1/pattern-master/replay`, { days, pattern_keys, simulate }),
  cancelPatternMasterReplay: () =>
    postJSON<PatternReplayStatus>(`/api/v1/pattern-master/replay/cancel`, {}),
  patternMasterReplaySimulation: (patternKey: string, options?: { initialCash?: number; maxPositions?: number;
    feeRate?: number; stampTaxRate?: number; slippageBps?: number; confirmedOnly?: boolean }) => {
    const q = new URLSearchParams();
    if (options?.initialCash != null) q.set("initial_cash", String(options.initialCash));
    if (options?.maxPositions != null) q.set("max_positions", String(options.maxPositions));
    if (options?.feeRate != null) q.set("fee_rate", String(options.feeRate));
    if (options?.stampTaxRate != null) q.set("stamp_tax_rate", String(options.stampTaxRate));
    if (options?.slippageBps != null) q.set("slippage_bps", String(options.slippageBps));
    if (options?.confirmedOnly != null) q.set("confirmed_only", String(options.confirmedOnly));
    return fetchJSON<PatternReplaySimulation>(`/api/v1/pattern-master/replay/simulation/${encodeURIComponent(patternKey)}?${q}`, 180_000);
  },

  // 异动归因
  stockAnomalyHistory: (ts_code: string, days = 180) =>
    fetchJSON<{ biz_date: string; pct_chg: number; primary_driver_label: string | null }[]>(
      `/api/v1/anomaly/by-stock/${ts_code}/history?days=${days}`,
    ),
  listAnomalies: (biz_date?: string) => {
    const q = biz_date ? `?biz_date=${biz_date}` : "";
    return fetchJSON<AnomalyOut[]>(`/api/v1/anomaly${q}`);
  },
  triggerAnomaly: (params: { biz_date?: string; threshold?: number; force?: boolean } = {}) =>
    postJSON<{ accepted: boolean; biz_date: string | null }>(`/api/v1/anomaly/run`, params, 180_000),

  // 回测
  runBacktest: (params: {
    ts_code: string;
    strategy: BacktestStrategy;
    start_date: string;
    end_date: string;
    params?: Record<string, unknown>;
    fee_rate?: number;
    t_plus_1?: boolean;
    block_limit?: boolean;
  }) => postJSON<BacktestResult>(`/api/v1/backtest`, params, 180_000),
  runTrendBacktest: (params: {
    ts_code: string;
    indicator?: string;
    factor_id?: number;
    start_date: string;
    end_date: string;
    entry_rule: TrendRule;
    exit_rule: TrendRule;
    entry_threshold?: number;
    exit_threshold?: number;
    slope_window?: number;
    fee_rate?: number;
    t_plus_1?: boolean;
    block_limit?: boolean;
  }) => postJSON<BacktestResult>(`/api/v1/backtest/trend`, params, 180_000),

  // 语音转文字（Whisper）
  transcribe: async (audio: Blob, language = "zh"): Promise<{ text: string; language: string; duration: number }> => {
    const fd = new FormData();
    fd.append("audio", audio, "recording.webm");
    if (language) fd.append("language", language);
    const r = await fetch(`${BASE}/api/v1/asr`, { method: "POST", credentials: CREDS, body: fd });
    if (!r.ok) {
      const detail = await r.text().catch(() => r.statusText);
      throw new Error(`${r.status} ${detail.slice(0, 200)}`);
    }
    return r.json();
  },

  // ML 实验工作台
  mlFeatures: () => fetchJSON<{ features: string[]; custom_features?: { name: string; rank_ic: string | null; has_signal: boolean }[] }>(`/api/v1/ml/features`),
  mlListExperiments: (limit = 50) =>
    fetchJSON<MlExperimentOut[]>(`/api/v1/ml/experiments?limit=${limit}`),
  mlGetExperiment: (id: number) => fetchJSON<MlExperimentOut>(`/api/v1/ml/experiments/${id}`),
  mlRollingIc: (exp_id: number, window = 30) =>
    fetchJSON<MlRollingIcOut>(`/api/v1/ml/experiments/${exp_id}/rolling-ic?window=${window}`),
  mlCompare: (exp_ids: number[], rolling_window = 30) =>
    postJSON<MlCompareOut>(`/api/v1/ml/experiments/compare`, { exp_ids, rolling_window }),
  mlCreateExperiment: (req: MlExperimentCreate) =>
    postJSON<MlExperimentOut>(`/api/v1/ml/experiments`, req),
  mlActivateExperiment: (id: number) =>
    postJSON<MlExperimentOut>(`/api/v1/ml/experiments/${id}/activate`, {}),
  mlPatchExperiment: (id: number, patch: { name?: string; notes?: string; tags?: string[] }) => {
    return fetch(`${BASE}/api/v1/ml/experiments/${id}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<MlExperimentOut>;
    });
  },
  mlGetPaper: (id: number) => fetchJSON<PaperTradingSummary | null>(`/api/v1/ml/experiments/${id}/paper`),
  mlBackfillSim: (id: number, days_back = 60) =>
    postJSON<{ accepted: boolean; days_back: number; horizon: number }>(
      `/api/v1/ml/experiments/${id}/backfill-simulation`, { days_back },
    ),
  mlDeleteExperiment: (id: number) => deleteJSON(`/api/v1/ml/experiments/${id}`),
  mlCreateGrid: (req: {
    name_prefix: string;
    horizons: number[];
    train_days_list: number[];
    holdout_days?: number;
    features?: string[] | null;
    hyperparams?: Record<string, unknown> | null;
  }) => postJSON<{ created: number; ids: number[] }>(`/api/v1/ml/experiments/grid`, req),
  mlActive: () => fetchJSON<{ active: Record<string, string> }>(`/api/v1/ml/active`),

  // ML 预测
  mlToday: (params: { horizon?: number; direction?: "up" | "down"; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.horizon) q.set("horizon", String(params.horizon));
    if (params.direction) q.set("direction", params.direction);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return fetchJSON<MlPredRow[]>(`/api/v1/ml/today${qs ? "?" + qs : ""}`);
  },
  mlTrainings: (limit = 10) => fetchJSON<MlTrainingOut[]>(`/api/v1/ml/training?limit=${limit}`),
  mlStockPredictions: (ts_code: string) =>
    fetchJSON<StockPredictionsOut>(`/api/v1/ml/predictions/${ts_code}`),
  mlPredictionsBatch: (ts_codes: string[]) =>
    postJSON<PredictionSummary[]>(`/api/v1/factors/ml-batch`, { ts_codes }),
  mlStockTimeline: (ts_code: string, horizon = 5, days = 90) =>
    fetchJSON<StockPredictionTimelineOut>(
      `/api/v1/ml/predictions/${ts_code}/timeline?horizon=${horizon}&days=${days}`,
    ),
  mlPaperRunNow: () =>
    postJSON<{ accepted: boolean; msg: string }>(`/api/v1/ml/paper/run`, {}),
  mlForwardTrackingWatchlist: (horizon = 5, days = 180, tsCodes?: string) =>
    fetchJSON<ForwardTrackingOut>(
      `/api/v1/ml/forward-tracking/watchlist?horizon=${horizon}&days=${days}`
      + (tsCodes ? `&ts_codes=${encodeURIComponent(tsCodes)}` : ""),
    ),
  mlGetAlertSetting: (tsCode: string, horizon = 5) =>
    fetchJSON<MlAlertSettingOut[]>(
      `/api/v1/ml/alert-settings?ts_code=${encodeURIComponent(tsCode)}&horizon=${horizon}`,
    ),
  mlSaveAlertSetting: (body: MlAlertSettingIn) =>
    putJSON<MlAlertSettingOut>(`/api/v1/ml/alert-settings`, body),
  mlResetAlertSetting: (tsCode: string, horizon = 5) =>
    deleteJSON(`/api/v1/ml/alert-settings?ts_code=${encodeURIComponent(tsCode)}&horizon=${horizon}`),
  mlRunPredictionAlerts: () =>
    postJSON<{ accepted: boolean; msg: string }>(`/api/v1/ml/prediction-alerts/run`, {}),
  mlPredictionAlertEvents: (limit = 30) =>
    fetchJSON<MlAlertEventOut[]>(`/api/v1/ml/prediction-alerts/events?limit=${limit}`),
  mlPerformanceFeedback: (params: { days?: number; horizon?: number; source?: "live" | "simulation" } = {}) => {
    const q = new URLSearchParams();
    if (params.days != null) q.set("days", String(params.days));
    if (params.horizon != null) q.set("horizon", String(params.horizon));
    if (params.source) q.set("source", params.source);
    return fetchJSON<MlPerformanceOut>(`/api/v1/ml/performance-feedback?${q}`);
  },
  mlListJumps: (params: { horizon?: number; min_z?: number; days?: number; cause?: string; limit?: number; only_watchlist?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.horizon != null) q.set("horizon", String(params.horizon));
    if (params.min_z != null) q.set("min_z", String(params.min_z));
    if (params.days != null) q.set("days", String(params.days));
    if (params.cause) q.set("cause", params.cause);
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.only_watchlist === false) q.set("only_watchlist", "false");
    return fetchJSON<JumpAttributionOut[]>(`/api/v1/ml/jumps?${q}`);
  },
  mlStockJumps: (ts_code: string, horizon?: number, days = 180) => {
    const q = new URLSearchParams();
    if (horizon != null) q.set("horizon", String(horizon));
    q.set("days", String(days));
    return fetchJSON<JumpAttributionOut[]>(`/api/v1/ml/jumps/by-stock/${ts_code}?${q}`);
  },
  mlJumpRunNow: (body: { biz_date?: string; horizons?: number[]; z_threshold?: number; scope?: string; backfill_days?: number; force?: boolean }) =>
    postJSON<{ accepted: boolean; mode: string; scope: string }>(`/api/v1/ml/jumps/run`, body),
  mlWalkforward: (body: {
    horizon?: number;
    n_folds?: number;
    train_days?: number;
    test_days?: number;
    features?: string[] | null;
    hyperparams?: Record<string, unknown>;
  }) => postJSON<WalkForwardResultOut>(`/api/v1/ml/walkforward`, body, 300_000),

  mlDecay: (params: { days?: number; only_active?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.days) q.set("days", String(params.days));
    if (params.only_active === false) q.set("only_active", "false");
    const qs = q.toString();
    return fetchJSON<ModelDecayOut[]>(`/api/v1/ml/decay${qs ? "?" + qs : ""}`);
  },

  // 运维：tushare 用量
  tushareQuota: (hours = 24) =>
    fetchJSON<TushareQuotaSummary>(`/api/v1/health/data/tushare-quota?hours=${hours}`),
  jobRuns: (limit = 200) =>
    fetchJSON<JobRunSummary>(`/api/v1/health/data/job-runs?limit=${limit}`),

  // L2 ML 模型自训
  autoTrainActive: () => fetchJSON<AutoTrainActive[]>(`/api/v1/auto-train/active`),
  autoTrainPublish: (id: number) => postJSON<AutoTrainRunOut>(`/api/v1/auto-train/runs/${id}/publish`, {}),
  autoTrainRuns: (horizon?: number, limit = 20) => {
    const q = new URLSearchParams();
    if (horizon !== undefined) q.set("horizon", String(horizon));
    q.set("limit", String(limit));
    return fetchJSON<AutoTrainRunOut[]>(`/api/v1/auto-train/runs?${q}`);
  },
  autoTrainTrigger: (body: {
    horizon: number;
    train_days?: number;
    holdout_days?: number;
    wf_folds?: number;
    wf_train_days?: number;
    wf_test_days?: number;
    paper_lookback_days?: number;
    min_improve_pct?: number;
  }) => postJSON<{ accepted: boolean; horizon: number; msg: string }>(`/api/v1/auto-train/run`, body),

  // L1 自动调参
  autoTuneActive: () => fetchJSON<AutoTuneActive[]>(`/api/v1/auto-tune/active`),
  autoTuneRun: (id: number) => fetchJSON<AutoTuneRunOut>(`/api/v1/auto-tune/runs/${id}`),
  autoTuneRuns: (strategy?: string, limit = 20) => {
    const q = new URLSearchParams();
    if (strategy) q.set("strategy", strategy);
    q.set("limit", String(limit));
    return fetchJSON<AutoTuneRunOut[]>(`/api/v1/auto-tune/runs?${q}`);
  },
  autoTuneTrigger: (body: {
    strategy: "ma_cross" | "breakout";
    grid?: Record<string, (number | string)[]>;
    lookback_days?: number;
    folds?: number;
    train_days?: number;
    test_days?: number;
    min_improve_pct?: number;
  }) => postJSON<{ accepted: boolean; strategy: string; msg: string }>(`/api/v1/auto-tune/run`, body),
  autoTuneRollback: (run_id: number) =>
    postJSON<AutoTuneActive>(`/api/v1/auto-tune/rollback`, { run_id }),
  autoTuneGrids: () => fetchJSON<Record<string, Record<string, (number | string)[]>>>(`/api/v1/auto-tune/grids`),

  // 网格扫参（组合层面）
  runGridSearch: (params: {
    ts_codes: string[];
    strategy: "ma_cross" | "breakout";
    start_date: string;
    end_date: string;
    param_grid: Record<string, (number | string)[]>;
    weights?: Record<string, number>;
    rebalance?: "none" | "monthly" | "quarterly" | "daily";
    fee_rate?: number;
    concurrency?: number;
  }) => postJSON<GridSearchResult>(`/api/v1/backtest/grid`, params, 300_000),

  // 组合层面回测
  runPortfolioBacktest: (params: {
    ts_codes: string[];
    strategy: BacktestStrategy;
    start_date: string;
    end_date: string;
    params?: Record<string, unknown>;
    weights?: Record<string, number>;
    fee_rate?: number;
    t_plus_1?: boolean;
    block_limit?: boolean;
    rebalance?: "none" | "monthly" | "quarterly" | "daily";
  }) => postJSON<PortfolioBacktestResult>(`/api/v1/backtest/portfolio`, params, 300_000),

  // 组合视角分析（V2：可传 weights + windows）
  portfolioAnalysis: (params: {
    ts_codes: string[];
    weights?: Record<string, number>;
    days?: number;
    windows?: number[];
  }) => postJSON<PortfolioAnalysisOut>(`/api/v1/portfolio/analysis`, params),

  // 卖方研报
  stockReportsLlmSummary: (ts_code: string, days_back = 180, limit = 15) =>
    postJSON<ReportLlmSummary>(
      `/api/v1/reports/${ts_code}/llm-summary?days_back=${days_back}&limit=${limit}`, {},
    ),
  stockReports: (ts_code: string, days_back = 180, limit = 20) =>
    fetchJSON<StockReportSummary>(`/api/v1/reports/${ts_code}?days_back=${days_back}&limit=${limit}`),
  recentReports: (params: { days_back?: number; rating?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.days_back) q.set("days_back", String(params.days_back));
    if (params.rating) q.set("rating", params.rating);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return fetchJSON<ReportRow[]>(`/api/v1/reports${qs ? "?" + qs : ""}`);
  },

  // 告警规则
  listAlertRules: () => fetchJSON<AlertRuleOut[]>(`/api/v1/alerts/rules`),
  createAlertRule: (req: AlertRuleCreate) => postJSON<AlertRuleOut>(`/api/v1/alerts/rules`, req),
  patchAlertRule: (id: number, patch: Partial<AlertRuleCreate>) => {
    return fetch(`${BASE}/api/v1/alerts/rules/${id}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<AlertRuleOut>;
    });
  },
  deleteAlertRule: (id: number) => deleteJSON(`/api/v1/alerts/rules/${id}`),
  runAlertsNow: () => postJSON<{ accepted: boolean }>(`/api/v1/alerts/run-now`, {}),
  listAlertEvents: (rule_id?: number, limit = 50, filters: { start?: string; end?: string; severity?: string; before_id?: number } = {}) => {
    const q = new URLSearchParams();
    if (rule_id) q.set("rule_id", String(rule_id));
    q.set("limit", String(limit));
    Object.entries(filters).forEach(([key, value]) => { if (value != null) q.set(key, String(value)); });
    return fetchJSON<AlertEventOut[]>(`/api/v1/alerts/events?${q}`);
  },

  // 个股雷达
  stockRadar: (ts_code: string) => fetchJSON<RadarOut>(`/api/v1/radar/${ts_code}`),

  // 行业板块
  industryToday: (params: { direction?: "up" | "down" | "flow_in" | "flow_out"; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.direction) q.set("direction", params.direction);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return fetchJSON<IndustryBoardRow[]>(`/api/v1/industry/today${qs ? "?" + qs : ""}`);
  },
  industryByStock: (ts_code: string, days = 20) =>
    fetchJSON<IndustryContext>(`/api/v1/industry/by-stock/${ts_code}?days=${days}`),
  industryDetail: (board: string, days = 120) =>
    fetchJSON<IndustryDetail>(`/api/v1/industry/detail/${encodeURIComponent(board)}?days=${days}`),

  // 资金流
  hsgtSeries: (days = 30) => fetchJSON<HsgtSeries>(`/api/v1/moneyflow/hsgt?days=${days}`),
  watchlistMfToday: () => fetchJSON<StockMfRow[]>(`/api/v1/moneyflow/watchlist/today`),
  stockMoneyflow: (ts_code: string, days = 60) =>
    fetchJSON<StockMfSeries>(`/api/v1/moneyflow/stock/${ts_code}?days=${days}`),

  // 事件研究
  studyPattern: (sample_id: number, limit = 500) =>
    fetchJSON<EventStudyResult>(`/api/v1/events/study/pattern/${sample_id}?limit=${limit}`),
  studyAnomaly: (params: { direction?: "up" | "down"; days_back?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.direction) q.set("direction", params.direction);
    if (params.days_back) q.set("days_back", String(params.days_back));
    const qs = q.toString();
    return fetchJSON<EventStudyResult>(`/api/v1/events/study/anomaly${qs ? "?" + qs : ""}`);
  },
  studyTopList: (days_back = 180) =>
    fetchJSON<EventStudyResult>(`/api/v1/events/study/top_list?days_back=${days_back}`),
  studyEventType: (event_type: string, days_back = 180) =>
    fetchJSON<EventStudyResult>(`/api/v1/events/study/event_type/${event_type}?days_back=${days_back}`),

  // 多因子选股
  factorsToday: (params: { industry?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.industry) q.set("industry", params.industry);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return fetchJSON<FactorRow[]>(`/api/v1/factors/today${qs ? "?" + qs : ""}`);
  },
  stockFactorsLatest: (ts_code: string) =>
    fetchJSON<FactorRow>(`/api/v1/factors/${ts_code}/latest`),
  factorsBatch: (ts_codes: string[]) =>
    postJSON<FactorRow[]>(`/api/v1/factors/batch`, { ts_codes }),

  // 数据管道健康
  healthData: () => fetchJSON<HealthReport>(`/api/v1/health/data?view=live-v2`),
  dataAndon: () => fetchJSON<AndonStatus>(`/api/v1/health/data/andon?view=live-v2`),

  // Web Push
  getVapidPublicKey: () => fetchJSON<{ public_key: string }>(`/api/v1/push/vapid-public-key`),
  subscribePush: (sub: PushSubscriptionJSON) =>
    postJSON<{ ok: boolean; total_subscriptions: number }>(`/api/v1/push/subscribe`, sub),
  unsubscribePush: (endpoint: string) =>
    postJSON<{ ok: boolean; removed: number }>(`/api/v1/push/unsubscribe`, { endpoint }),
  testPush: (endpoint?: string) =>
    postJSON<{ sent: number; failed: number; total: number }>(`/api/v1/push/test`, endpoint ? { endpoint } : {}),
  listPushSubscriptions: () =>
    fetchJSON<PushSubscriptionsList>(`/api/v1/push/subscriptions`),

  // 类比预测
  analogForecast: (req: {
    batch: AnalogEpisode[]; c_len: number; a_len?: number; scope: string;
    scope_values?: string[]; de_market: boolean; top_k_matches?: number; run_history?: boolean;
    asof?: string;
    hist_top_k?: number; hist_step?: number; hist_max_stocks?: number;
    // 全市场 + 全历史 k-NN 扫描可能跑十几~几十秒，给 5 分钟长超时（前端配实时计时器）
  }) => postJSON<AnalogForecastResult>(`/api/v1/analog/forecast`, req, 300_000),
  analogTrack: (req: {
    ts_codes: string[]; group_name: string; c_len: number; de_market: boolean;
    forecast: { median?: number; p25?: number; p75?: number; win_rate?: number };
  }) => postJSON<{ added: number; tracked: number }>(`/api/v1/analog/track`, req),
  analogTracks: (status = "active") =>
    fetchJSON<AnalogTrackItem[]>(`/api/v1/analog/tracks?status=${status}`),
  analogCancelTrack: (id: number) => deleteJSON(`/api/v1/analog/tracks/${id}`),
  analogContext: (ts_code: string, asof?: string) =>
    fetchJSON<AnalogContext>(`/api/v1/analog/context/${ts_code}${asof ? `?asof=${asof}` : ""}`),

  // 站内信
  listNotifications: (unread_only = false, limit = 100) => {
    const q = new URLSearchParams();
    if (unread_only) q.set("unread_only", "true");
    q.set("limit", String(limit));
    return fetchJSON<NotificationItem[]>(`/api/v1/notifications?${q}`);
  },
  notificationsUnreadCount: () =>
    fetchJSON<{ count: number }>(`/api/v1/notifications/unread_count`),
  markNotificationsRead: (req: { ids?: number[]; all?: boolean }) =>
    postJSON<{ marked: number }>(`/api/v1/notifications/read`, req),

  // 设置：告警通道
  getNotifierConfig: () => fetchJSON<NotifierConfig>(`/api/v1/settings/notifiers`),
  updateNotifierConfig: (cfg: NotifierConfigUpdate) =>
    putJSON<NotifierConfig>(`/api/v1/settings/notifiers`, cfg),
  testNotifier: (req: { title?: string; body?: string } = {}) =>
    postJSON<{ sent: boolean; provider: string | null; message: string }>(
      `/api/v1/settings/notifiers/test`, req,
    ),
  getQQConnection: () => fetchJSON<QQConnection>(`/api/v1/settings/qq/connection`),
  refreshQQLogin: () => postJSON<QQConnection>(`/api/v1/settings/qq/refresh`, {}),

  // 事件分类（I1）
  eventsSummary: (params: { days?: number; per_type?: number; exclude_noise?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.days) q.set("days", String(params.days));
    if (params.per_type) q.set("per_type", String(params.per_type));
    if (params.exclude_noise !== undefined) q.set("exclude_noise", String(params.exclude_noise));
    const qs = q.toString();
    return fetchJSON<EventTypeSummary[]>(`/api/v1/intelligence/events/summary${qs ? "?" + qs : ""}`);
  },

  // 突发雷达（I4）
  breakingNews: (params: { hours?: number; min_sources?: number; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.hours) q.set("hours", String(params.hours));
    if (params.min_sources) q.set("min_sources", String(params.min_sources));
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return fetchJSON<BreakingItem[]>(`/api/v1/intelligence/breaking${qs ? "?" + qs : ""}`);
  },

  // 舆情时序（S2/S3）
  sentimentTimeseries: (params: { ts_code?: string; industry?: string; days?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.ts_code) q.set("ts_code", params.ts_code);
    if (params.industry) q.set("industry", params.industry);
    if (params.days) q.set("days", String(params.days));
    const qs = q.toString();
    return fetchJSON<SentimentTimeSeries>(`/api/v1/sentiment/timeseries${qs ? "?" + qs : ""}`);
  },

  // 日内分钟线异动
  listMinuteAlerts: (params: { biz_date?: string; ts_code?: string; alert_type?: MinuteAlertType; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.biz_date) q.set("biz_date", params.biz_date);
    if (params.ts_code) q.set("ts_code", params.ts_code);
    if (params.alert_type) q.set("alert_type", params.alert_type);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return fetchJSON<MinuteAlertOut[]>(`/api/v1/minute-alerts${qs ? "?" + qs : ""}`);
  },
  listIntradayTracks: (includeDisabled = true, groupId?: number | null) => {
    const group = groupId != null ? `&group_id=${groupId}` : "";
    return fetchJSON<IntradayTrackOut[]>(`/api/v1/intraday-tracking?include_disabled=${includeDisabled ? "true" : "false"}${group}`);
  },
  listIntradayTrackSnapshots: (groupId?: number | null) => {
    const query = groupId != null ? `?group_id=${groupId}` : "";
    return fetchJSON<IntradayTrackSnapshotOut[]>(`/api/v1/intraday-tracking/snapshots${query}`);
  },
  createIntradayTrack: (req: {
    ts_code: string;
    enabled?: boolean;
    window_minutes?: number;
    return_threshold_pct?: number;
    volume_ratio_threshold?: number;
    baseline_minutes?: number;
    cooldown_minutes?: number;
    track_return?: boolean;
    track_volume?: boolean;
    track_breakout?: boolean;
    condition_mode?: "any" | "all";
    threshold_mode?: "fixed" | "adaptive";
    target_type?: "none" | "price" | "ma" | "ema" | "vwap" | "open" | "daily_ma" | "daily_ema" | "prev_high" | "prev_low";
    target_value?: number | null;
    target_period?: number | null;
    target_tolerance_pct?: number;
    target_direction?: "near" | "cross_up" | "cross_down" | "above" | "below";
    target_confirm_minutes?: number;
    group_name?: string;
  }) => postJSON<IntradayTrackOut>(`/api/v1/intraday-tracking`, req),
  previewIntradayTrack: (req: {
    ts_code: string;
    enabled?: boolean;
    window_minutes?: number;
    return_threshold_pct?: number;
    volume_ratio_threshold?: number;
    baseline_minutes?: number;
    cooldown_minutes?: number;
    track_return?: boolean;
    track_volume?: boolean;
    track_breakout?: boolean;
    condition_mode?: "any" | "all";
    threshold_mode?: "fixed" | "adaptive";
    target_type?: "none" | "price" | "ma" | "ema" | "vwap" | "open" | "daily_ma" | "daily_ema" | "prev_high" | "prev_low";
    target_value?: number | null;
    target_period?: number | null;
    target_tolerance_pct?: number;
    target_direction?: "near" | "cross_up" | "cross_down" | "above" | "below";
    target_confirm_minutes?: number;
  }) => postJSON<IntradayTrackPreviewOut>(`/api/v1/intraday-tracking/preview`, req),
  previewIntradayTrackBatch: (req: IntradayTrackBatchRequest) =>
    postJSON<IntradayTrackBatchPreviewOut>(`/api/v1/intraday-tracking/batch-preview`, req),
  createIntradayTrackBatch: (req: IntradayTrackBatchRequest) =>
    postJSON<IntradayTrackBatchPreviewOut>(`/api/v1/intraday-tracking/batch`, req),
  patchIntradayTrack: (id: number, patch: Partial<Omit<IntradayTrackOut, "id" | "user_id" | "ts_code" | "name" | "industry" | "last_signal_at" | "created_at" | "updated_at">>) =>
    fetch(`${BASE}/api/v1/intraday-tracking/${id}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<IntradayTrackOut>;
    }),
  disableIntradayTrack: (id: number) => deleteJSON(`/api/v1/intraday-tracking/${id}`),
  getIntradayTrackBars: (tsCode: string, minutes = 120) =>
    fetchJSON<IntradayTrackBarsOut>(`/api/v1/intraday-tracking/bars/${encodeURIComponent(tsCode)}?minutes=${minutes}`),
  listIntradayTrackEvents: (limit = 100, tsCode?: string) => {
    const query = new URLSearchParams({ limit: String(limit) });
    if (tsCode) query.set("ts_code", tsCode);
    return fetchJSON<IntradayTrackEventOut[]>(`/api/v1/intraday-tracking/events?${query}`);
  },
  listIntradayTrackGroups: () =>
    fetchJSON<IntradayTrackGroupOut[]>(`/api/v1/intraday-tracking/groups`),
  previewIntradayTrackGroup: (id: number) =>
    postJSON<IntradayTrackGroupSyncPreviewOut>(`/api/v1/intraday-tracking/groups/${id}/preview-sync`, {}),
  createIntradayTrackGroup: (req: IntradayTrackGroupCreate) =>
    postJSON<IntradayTrackGroupOut>(`/api/v1/intraday-tracking/groups`, req),
  createIntradayTrackGroupFromSource: (req: IntradayTrackGroupFromSourceCreate) =>
    postJSON<IntradayTrackGroupOut>(`/api/v1/intraday-tracking/groups/from-source`, req),
  patchIntradayTrackGroup: (id: number, patch: Partial<IntradayTrackGroupCreate>) =>
    fetch(`${BASE}/api/v1/intraday-tracking/groups/${id}`, {
      method: "PATCH", credentials: CREDS, headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<IntradayTrackGroupOut>;
    }),
  syncIntradayTrackGroup: (id: number) =>
    postJSON<{ ok: boolean; group_id: number; group_name: string; n: number; added: number; removed: number; summary: string }>(`/api/v1/intraday-tracking/groups/${id}/sync`, {}),
  deleteIntradayTrackGroup: (id: number) => deleteJSON(`/api/v1/intraday-tracking/groups/${id}`),
  getIntradayTrackPerformance: (params: { days_back?: number; group_id?: number | null } = {}) => {
    const q = new URLSearchParams();
    if (params.days_back) q.set("days_back", String(params.days_back));
    if (params.group_id != null) q.set("group_id", String(params.group_id));
    const qs = q.toString();
    return fetchJSON<IntradayTrackPerformanceOut>(`/api/v1/intraday-tracking/performance${qs ? "?" + qs : ""}`);
  },
  sectorCatalog: (params: { kind?: "all" | "industry" | "concept" | "area"; q?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.kind) qs.set("kind", params.kind);
    if (params.q) qs.set("q", params.q);
    if (params.limit) qs.set("limit", String(params.limit));
    return fetchJSON<SectorCatalog>(`/api/v1/industry/catalog?${qs}`);
  },
  getIntradayTrackGroupLeague: (daysBack = 365) =>
    fetchJSON<IntradayTrackGroupLeagueOut>(`/api/v1/intraday-tracking/groups/league?days_back=${daysBack}`),
  getAlertOutcomes: (params: { days_back?: number; severity?: AlertSeverity } = {}) => {
    const q = new URLSearchParams();
    if (params.days_back) q.set("days_back", String(params.days_back));
    if (params.severity) q.set("severity", params.severity);
    const qs = q.toString();
    return fetchJSON<AlertOutcomesOut>(`/api/v1/minute-alerts/outcomes${qs ? "?" + qs : ""}`);
  },
  triggerOutcomesBackfill: () =>
    postJSON<{ accepted: boolean }>(`/api/v1/minute-alerts/outcomes/backfill`, {}),

  // 持仓 PnL 账本（M9: 可选 account_id；不传 = 当前 user 全部账户聚合）
  getPortfolio: (account_id?: number | null) => {
    const q = account_id != null ? `?account_id=${account_id}` : "";
    return fetchJSON<PortfolioSummary>(`/api/v1/positions${q}`);
  },
  createTrade: (body: TradeIn) => postJSON<TradeOut>(`/api/v1/positions/trades`, body),
  listTrades: (params: { ts_code?: string; account_id?: number | null; limit?: number; start?: string; end?: string; journal_only?: boolean; before_id?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.ts_code) q.set("ts_code", params.ts_code);
    if (params.account_id != null) q.set("account_id", String(params.account_id));
    if (params.limit) q.set("limit", String(params.limit));
    for (const key of ["start", "end", "journal_only", "before_id"] as const) { if (params[key] != null) q.set(key, String(params[key])); }
    const qs = q.toString();
    return fetchJSON<TradeOut[]>(`/api/v1/positions/trades${qs ? "?" + qs : ""}`);
  },
  deleteTrade: (id: number) => deleteJSON(`/api/v1/positions/trades/${id}`),
  // #5 决策日记
  journalStats: (days_back = 365, account_id?: number | null, range: { start?: string; end?: string } = {}) => {
    const q = new URLSearchParams({ days_back: String(days_back) });
    if (account_id != null) q.set("account_id", String(account_id));
    Object.entries(range).forEach(([key, value]) => { if (value) q.set(key, value); });
    return fetchJSON<JournalStatsOut>(`/api/v1/positions/journal/stats?${q}`);
  },
  // #4 周报
  getWeeklyReport: (week_of?: string) => {
    const q = week_of ? `?week_of=${week_of}` : "";
    return fetchJSON<WeeklyReportOut | null>(`/api/v1/positions/weekly-report${q}`);
  },
  regenerateWeeklyReport: () =>
    postJSON<{ accepted: boolean }>(`/api/v1/positions/weekly-report/regenerate`, {}),
  // #6 主力关注度（批量）
  attentionBatch: (ts_codes: string[]) =>
    postJSON<AttentionBatchOut>(`/api/v1/stocks/attention-batch`, { ts_codes }),
  positionsHistory: (days = 180, account_id?: number | null) => {
    const q = new URLSearchParams();
    q.set("days", String(days));
    if (account_id != null) q.set("account_id", String(account_id));
    return fetchJSON<PositionsHistoryOut>(`/api/v1/positions/history?${q}`);
  },

  // ── M9.2 lab: 篮子 ─────────────────────────────────
  listBaskets: () => fetchJSON<BasketOut[]>(`/api/v1/baskets`),
  listSandboxWorkspaces: () => fetchJSON<SandboxWorkspaceOut[]>(`/api/v1/sandbox`),
  createSandboxWorkspace: (req: SandboxWorkspaceCreate) =>
    postJSON<SandboxWorkspaceOut>(`/api/v1/sandbox`, req),
  sandboxCoverage: (id: number, start?: string, end?: string) => {
    const q = new URLSearchParams();
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    return fetchJSON<{ note: string; stocks: Array<{ ts_code: string; daily: { start: string; end: string; days: number; missing_factors: number; factor_versions: number } | null; minute: { start: string; end: string; days: number } | null }> }>(`/api/v1/sandbox/${id}/coverage?${q}`);
  },
  sandboxActivity: () => fetchJSON<Array<SandboxRunOut & { workspace_name: string }>>(`/api/v1/sandbox/activity`),
  sandboxHistory: () => fetchJSON<Array<SandboxRunOut & { workspace_name: string }>>(`/api/v1/sandbox/activity?history=true`),
  sandboxRun: (workspaceId: number, runId: number) => fetchJSON<SandboxRunOut>(`/api/v1/sandbox/${workspaceId}/runs/${runId}`),
  listSandboxRuns: (id: number, before_id?: number, active_only = false) =>
    fetchJSON<SandboxRunOut[]>(`/api/v1/sandbox/${id}/runs?active_only=${active_only}${before_id ? `&before_id=${before_id}` : ""}`),
  createSandboxRun: (id: number, req: SandboxRunCreate) =>
    postJSON<SandboxRunOut>(`/api/v1/sandbox/${id}/runs`, req, 180_000),
  stopSandboxRun: (id: number, runId: number) =>
    postJSON<SandboxRunOut>(`/api/v1/sandbox/${id}/runs/${runId}/stop`, {}),
  createSandboxCandidate: (id: number, runId: number, accountId: number) =>
    postJSON<SandboxCandidateOut>(`/api/v1/sandbox/${id}/runs/${runId}/candidate`, { account_id: accountId, confirmed: true }, 180_000),
  getBasket: (id: number) => fetchJSON<BasketOut>(`/api/v1/baskets/${id}`),
  createBasket: (req: BasketCreate) => postJSON<BasketOut>(`/api/v1/baskets`, req),
  patchBasket: (id: number, patch: BasketPatch) =>
    fetch(`${BASE}/api/v1/baskets/${id}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<BasketOut>;
    }),
  deleteBasket: (id: number) => deleteJSON(`/api/v1/baskets/${id}`),
  rebalanceBasket: (id: number, account_id?: number | null) => {
    const q = account_id != null ? `?account_id=${account_id}` : "";
    return postJSON<RebalanceOut>(`/api/v1/baskets/${id}/rebalance${q}`, {});
  },

  // ── 篮子自动化（定投 / 止盈止损 / 再平衡）─────────────
  listAutomations: (basketId?: number) => {
    const q = basketId != null ? `?basket_id=${basketId}` : "";
    return fetchJSON<AutomationOut[]>(`/api/v1/basket-automation${q}`);
  },
  createAutomation: (req: AutomationCreate) =>
    postJSON<AutomationOut>(`/api/v1/basket-automation`, req),
  patchAutomation: (id: number, patch: AutomationPatch) =>
    fetch(`${BASE}/api/v1/basket-automation/${id}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<AutomationOut>;
    }),
  deleteAutomation: (id: number) => deleteJSON(`/api/v1/basket-automation/${id}`),

  // ── 策略赛马 Arena ─────────────────────────────────
  listArena: () => fetchJSON<ArenaEntryMetrics[]>(`/api/v1/arena`),
  createArenaEntry: (req: ArenaEntryCreate) =>
    postJSON<ArenaEntryMetrics>(`/api/v1/arena`, req),
  deleteArenaEntry: (id: number) => deleteJSON(`/api/v1/arena/${id}`),
  compareArena: (ids: number[]) =>
    fetchJSON<ArenaEntryCurve[]>(`/api/v1/arena/compare?ids=${ids.join(",")}`),

  // ── M9.2 lab: 配对策略 ─────────────────────────────
  listPairs: () => fetchJSON<PairOut[]>(`/api/v1/pairs`),
  getPair: (id: number) => fetchJSON<PairOut>(`/api/v1/pairs/${id}`),
  createPair: (req: PairCreate) => postJSON<PairOut>(`/api/v1/pairs`, req),
  patchPair: (id: number, patch: PairPatch) =>
    fetch(`${BASE}/api/v1/pairs/${id}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<PairOut>;
    }),
  deletePair: (id: number) => deleteJSON(`/api/v1/pairs/${id}`),
  pairZScore: (id: number, days = 365) =>
    fetchJSON<ZSeriesOut>(`/api/v1/pairs/${id}/zscore?days=${days}`),
  pairBacktest: (id: number, days = 500) =>
    postJSON<PairBacktestOut>(`/api/v1/pairs/${id}/backtest?days=${days}`, {}, 180_000),

  // ── M10.C 市场体检 ─────────────────────────────────
  getMarketBrief: async (biz_date?: string): Promise<MarketBriefOut | null> => {
    const q = biz_date ? `?biz_date=${biz_date}` : "";
    const r = await fetch(`${BASE}/api/v1/intelligence/market-brief${q}`, { credentials: CREDS });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`${r.status}`);
    const j = await r.json();
    return j ?? null;
  },
  getTopgunSnapshot: async (trade_date?: string): Promise<TopgunSnapshotOut | null> => {
    const q = trade_date ? `?trade_date=${trade_date}` : "";
    const r = await fetch(`${BASE}/api/v1/topgun/snapshot${q}`, { credentials: CREDS });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`${r.status}`);
    return (await r.json()) ?? null;
  },
  marketBriefHistory: (days = 30) =>
    fetchJSON<MarketBriefHistoryItem[]>(`/api/v1/intelligence/market-brief/history?days=${days}`),
  regenerateMarketBrief: (biz_date?: string) =>
    postJSON<{ accepted: boolean; biz_date: string }>(
      `/api/v1/intelligence/market-brief/regenerate${biz_date ? `?biz_date=${biz_date}` : ""}`, {},
      180_000,
    ),

  // ── M10.D 全市场 RPS ───────────────────────────────
  listRps: (params: { biz_date?: string; min_rps_120?: number; industry?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.biz_date) q.set("biz_date", params.biz_date);
    if (params.min_rps_120 != null) q.set("min_rps_120", String(params.min_rps_120));
    if (params.industry) q.set("industry", params.industry);
    if (params.limit) q.set("limit", String(params.limit));
    return fetchJSON<RpsLeader[]>(`/api/v1/intelligence/rps?${q}`);
  },
  rpsRanks: (codes: string[], bizDate?: string) => {
    const q = new URLSearchParams({ codes: [...new Set(codes)].join(",") });
    if (bizDate) q.set("biz_date", bizDate);
    return fetchJSON<RpsRankItem[]>(`/api/v1/intelligence/rps/ranks?${q}`);
  },
  getRpsBrief: async (biz_date?: string): Promise<RpsBriefOut | null> => {
    const q = biz_date ? `?biz_date=${biz_date}` : "";
    const r = await fetch(`${BASE}/api/v1/intelligence/rps/brief${q}`, { credentials: CREDS });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`${r.status}`);
    const j = await r.json();
    return j ?? null;
  },
  rpsHistory: (days = 30) =>
    fetchJSON<RpsHistoryItem[]>(`/api/v1/intelligence/rps/history?days=${days}`),
  rpsSeries: (ts_code: string, days = 60) =>
    fetchJSON<RpsSeriesPoint[]>(`/api/v1/intelligence/rps/series/${ts_code}?days=${days}`),
  rpsCompare: (codes: string[], start: string, end: string) =>
    postJSON<{ series: Record<string, RpsSeriesPoint[]>; start: string; end: string }>(
      "/api/v1/intelligence/rps/compare", { codes, start, end }),
  rpsBreakouts: (params: { days?: number; min_jump?: number; min_rps_now?: number; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.days != null) q.set("days", String(params.days));
    if (params.min_jump != null) q.set("min_jump", String(params.min_jump));
    if (params.min_rps_now != null) q.set("min_rps_now", String(params.min_rps_now));
    if (params.limit != null) q.set("limit", String(params.limit));
    return fetchJSON<RpsBreakoutItem[]>(`/api/v1/intelligence/rps/breakouts?${q}`);
  },
  rpsBacktest: (params: { threshold?: number; lookback_days?: number; hold_days?: string; start?: string; end?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.threshold != null) q.set("threshold", String(params.threshold));
    if (params.lookback_days != null) q.set("lookback_days", String(params.lookback_days));
    if (params.start) q.set("start", params.start);
    if (params.end) q.set("end", params.end);
    if (params.hold_days) q.set("hold_days_csv", params.hold_days);
    return fetchJSON<RpsBacktestOut>(`/api/v1/intelligence/rps/backtest?${q}`, 180_000);
  },
  backfillRps: (days = 90) =>
    postJSON<{ accepted: boolean; start: string; end: string }>(
      `/api/v1/intelligence/rps/backfill?days=${days}`, {}
    ),
  rpsRotation: (days = 5, direction: "up" | "down" | "all" = "all", limit = 20) =>
    fetchJSON<RpsRotationItem[]>(
      `/api/v1/intelligence/rps/rotation?days=${days}&direction=${direction}&limit=${limit}`
    ),
  // ── 选股进出对比（screen_run）────────────────────────
  screenRunRules: () => fetchJSON<ScreenRuleSummary[]>(`/api/v1/screen-runs/rules`),
  stockPoolTree: (days = 20) =>
    fetchJSON<PoolTreeOut>(`/api/v1/screen-runs/pool-tree?days=${days}`),
  stockPoolEntryEvents: (tsCode: string, start?: string, end?: string) => {
    const q = new URLSearchParams();
    if (start) q.set("start", start);
    if (end) q.set("end", end);
    const suffix = q.size ? `?${q}` : "";
    return fetchJSON<StockPoolEntryEvent[]>(`/api/v1/screen-runs/stock/${encodeURIComponent(tsCode)}/entry-events${suffix}`);
  },
  screenRuns: (rule: string, limit = 60) =>
    fetchJSON<ScreenRunItem[]>(`/api/v1/screen-runs?rule=${encodeURIComponent(rule)}&limit=${limit}`),
  screenRunDiff: (rule: string, curr?: string, prev?: string) => {
    const q = new URLSearchParams({ rule });
    if (curr) q.set("curr", curr);
    if (prev) q.set("prev", prev);
    return fetchJSON<ScreenDiffOut>(`/api/v1/screen-runs/diff?${q}`);
  },
  screenRunSignalStudy: (rule: string, kind: "entered" | "exited") =>
    fetchJSON<ScreenSignalStudyOut>(
      `/api/v1/screen-runs/event-study?rule=${encodeURIComponent(rule)}&kind=${kind}`
    ),
  replaySelectionRule: (request: SelectionReplayRequest) =>
    postJSON<SelectionReplayOut>(`/api/v1/screen-runs/replay`, request, 300_000),
  selectionStrategyLeague: (limit = 12) =>
    fetchJSON<StrategyLeagueOut>(`/api/v1/screen-runs/league?limit=${limit}`),
  selectionCockpit: (limit = 20) =>
    fetchJSON<SelectionCockpitOut>(`/api/v1/screen-runs/cockpit?limit=${limit}`),
  selectionCockpitConfig: () =>
    fetchJSON<CockpitStrategyConfig>(`/api/v1/screen-runs/cockpit/config`),
  selectionCockpitConfigVersions: () =>
    fetchJSON<CockpitStrategyConfig[]>(`/api/v1/screen-runs/cockpit/config/versions`),
  updateSelectionCockpitConfig: (config: CockpitStrategyConfig, expectedVersion?: number) =>
    putJSON<CockpitStrategyConfig>(`/api/v1/screen-runs/cockpit/config${expectedVersion == null ? '' : `?expected_version=${expectedVersion}`}`, config),
  selectionScoreReview: (reviewDate: string, threshold = 80, horizon = 5, limit = 100) => {
    const q = new URLSearchParams({
      review_date: reviewDate,
      threshold: String(threshold),
      horizon: String(horizon),
      limit: String(limit),
    });
    return fetchJSON<ScoreReviewOut>(`/api/v1/screen-runs/score-review?${q}`);
  },
  latestMaturedScoreDate: (horizon = 20) =>
    fetchJSON<{ horizon: number; score_date: string | null; latest_market_date: string | null }>(
      `/api/v1/screen-runs/score-review/latest-matured-date?horizon=${horizon}`,
    ),
  removeScreenRunMember: (rule: string, runDate: string, tsCode: string) => {
    const q = new URLSearchParams({ rule, run_date: runDate, ts_code: tsCode });
    return fetch(`${BASE}/api/v1/screen-runs/member?${q}`, { method: "DELETE", credentials: CREDS })
      .then(async (response) => {
        if (!response.ok) throw new Error(friendlyHttpError(response.status, await response.text(), response.statusText));
        return response.json() as Promise<{ rule_name: string; run_date: string; ts_code: string; removed: boolean; remaining: number }>;
      });
  },
  themeRadarScore: (limit = 30, biz_date?: string) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (biz_date) q.set("biz_date", biz_date);
    return fetchJSON<ThemeScoreOut>(`/api/v1/theme-radar/score?${q}`);
  },
  themeRadarScoreHistory: (board: string, days = 60) =>
    fetchJSON<ThemeScoreHistoryPoint[]>(
      `/api/v1/theme-radar/score/history?board=${encodeURIComponent(board)}&days=${days}`
    ),
  themeRadarDates: () => fetchJSON<string[]>(`/api/v1/theme-radar/dates?limit=5000`),
  themeRadarBacktest: (lookbackDays = 750, forwardDays = 5) =>
    fetchJSON<ThemeBacktestOut>(
      `/api/v1/theme-radar/backtest?lookback_days=${lookbackDays}&forward_days=${forwardDays}`
    ),
  themeRadarBoardReview: (board: string, lookbackDays = 750, limit = 120) =>
    fetchJSON<ThemeBoardReview>(`/api/v1/theme-radar/board-review?board=${encodeURIComponent(board)}&lookback_days=${lookbackDays}&limit=${limit}`),
  themeRadarCapital: (limit = 30, bizDate?: string) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (bizDate) q.set("biz_date", bizDate);
    return fetchJSON<ThemeCapitalOut>(`/api/v1/theme-radar/capital-tracking?${q}`);
  },
  themeRadarCapitalBacktest: (lookbackDays = 750, forwardDays = 5, threshold = 55) =>
    fetchJSON<ThemeCapitalBacktestOut>(
      `/api/v1/theme-radar/capital-backtest?lookback_days=${lookbackDays}&forward_days=${forwardDays}&threshold=${threshold}`
    ),
  themeRadarLongHistory: (limit = 30, bizDate?: string) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (bizDate) q.set("biz_date", bizDate);
    return fetchJSON<ThemeLongHistoryOut>(`/api/v1/theme-radar/long-history?${q}`);
  },
  themeRadarLongBacktest: (lookbackDays = 5000, forwardDays = 5, threshold = 55) =>
    fetchJSON<ThemeLongBacktestOut>(
      `/api/v1/theme-radar/long-backtest?lookback_days=${lookbackDays}&forward_days=${forwardDays}&threshold=${threshold}`
    ),
  themeRadarPolicy: (days = 30) =>
    fetchJSON<ThemePolicyOut>(`/api/v1/theme-radar/policy?days=${days}`),
  themeRadarIntel: (params: { theme?: string; only_relevant?: boolean; mine?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.theme) q.set("theme", params.theme);
    if (params.only_relevant) q.set("only_relevant", "true");
    if (params.mine) q.set("mine", "true");
    q.set("limit", String(params.limit ?? 40));
    return fetchJSON<IntelRow[]>(`/api/v1/theme-radar/intel?${q}`);
  },
  themeRadarIntelThemes: () => fetchJSON<string[]>(`/api/v1/theme-radar/intel/themes`),
  themeRadarArxiv: () => fetchJSON<ArxivTrendRow[]>(`/api/v1/theme-radar/arxiv`),
  themeRadarPatents: () => fetchJSON<PatentTrendRow[]>(`/api/v1/theme-radar/patents`),
  listInterests: () => fetchJSON<InterestRow[]>(`/api/v1/theme-radar/interests`),
  addInterest: (kind: string, value: string) =>
    postJSON<InterestRow>(`/api/v1/theme-radar/interests`, { kind, value }),
  deleteInterest: (id: number) =>
    fetch(`${BASE}/api/v1/theme-radar/interests/${id}`, { method: "DELETE", credentials: CREDS }).then(() => undefined),
  industryMomentum: (params: { sort_by?: "pct_5d" | "pct_20d" | "pct_60d" | "n_strong"; direction?: "up" | "down"; limit?: number; min_n_stocks?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.sort_by) q.set("sort_by", params.sort_by);
    if (params.direction) q.set("direction", params.direction);
    if (params.limit) q.set("limit", String(params.limit));
    if (params.min_n_stocks) q.set("min_n_stocks", String(params.min_n_stocks));
    return fetchJSON<IndustryMomentumItem[]>(`/api/v1/intelligence/industry/momentum?${q}`);
  },
  regenerateRps: (biz_date?: string) =>
    postJSON<{ accepted: boolean; biz_date: string }>(
      `/api/v1/intelligence/rps/regenerate${biz_date ? `?biz_date=${biz_date}` : ""}`, {},
      180_000,
    ),

  // ── M10.A 中短期波段 ────────────────────────────────
  getSwingPlan: async (ts_code: string): Promise<SwingPlanOut | null> => {
    const r = await fetch(`${BASE}/api/v1/swing/${ts_code}`, { credentials: CREDS });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`${r.status}`);
    const j = await r.json();
    return j ?? null;
  },
  listSwingBriefs: (ts_codes?: string[]) => {
    const q = ts_codes && ts_codes.length > 0 ? `?ts_codes=${encodeURIComponent(ts_codes.join(","))}` : "";
    return fetchJSON<SwingPlanBrief[]>(`/api/v1/swing${q}`);
  },
  regenerateSwing: (ts_code: string) =>
    postJSON<{ accepted: boolean; ts_code: string }>(
      `/api/v1/swing/${ts_code}/regenerate`, {}
    ),
  regenerateAllSwing: () =>
    postJSON<{ accepted: boolean }>(`/api/v1/swing/regenerate-all`, {}),

  // ── M10.B 日内 T 网格 ────────────────────────────────
  // Bypass stale responses left by pre-v24 service workers. Execution state
  // is operational data and must always reflect the production database.
  listGrids: (accountId: number) => fetchJSON<GridOut[]>(`/api/v1/grid?account_id=${accountId}&view=execution-v2`),
  recomputeAllGrids: () => postJSON<{ accepted: boolean }>(`/api/v1/grid/recompute`, {}),
  recomputeGrid: (account_id: number, ts_code: string) =>
    postJSON<{ accepted: boolean }>(`/api/v1/grid/${account_id}/${ts_code}/recompute`, {}),
  patchGridStatus: (account_id: number, ts_code: string, status: "active" | "paused") =>
    fetch(`${BASE}/api/v1/grid/${account_id}/${ts_code}/status?status=${status}`, {
      method: "PATCH", credentials: CREDS,
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json();
    }),
  configureGridExecution: (
    account_id: number,
    ts_code: string,
    mode: "alerts_only" | "auto_simulation" | "live_approval",
    connection_id: number | null,
  ) => putJSON<{ account_id: number; ts_code: string; execution: GridExecutionConfig }>(
    `/api/v1/grid/${account_id}/${ts_code}/execution`,
    { mode, connection_id },
  ),
  deleteGrid: (account_id: number, ts_code: string) =>
    deleteJSON(`/api/v1/grid/${account_id}/${ts_code}`),
  listGridAlerts: (accountId: number, days = 7, limit = 50) =>
    fetchJSON<GridAlert[]>(`/api/v1/grid/alerts?account_id=${accountId}&days=${days}&limit=${limit}`),
  createCustomGrid: (req: { account_id: number; ts_code: string; anchor_type: string; custom_anchor?: number | null; step_pct?: number | null; qty_unit: number; n_per_side: number }) =>
    postJSON<{ ok: boolean; ts_code: string; name: string; anchor_price: number; step_pct: number; n_levels: number }>(`/api/v1/grid/custom`, req),
  gridBacktest: (req: { ts_code: string; start?: string; end?: string; step_pct: number; n_per_side: number; qty_unit: number; anchor_mode: string }) =>
    postJSON<GridBacktestOut>(`/api/v1/grid/backtest`, req, 120_000),
  gridOptimize: (req: { ts_code: string; start?: string; end?: string; qty_unit: number; anchor_mode: string }) =>
    postJSON<GridOptimizeOut>(`/api/v1/grid/optimize`, req, 180_000),
  gridWalkForward: (req: { ts_code: string; start?: string; end?: string; qty_unit: number; anchor_mode: string }, folds = 4) =>
    postJSON<GridWalkForwardOut>(`/api/v1/grid/walk-forward?folds=${folds}`, req, 180_000),
  gridAdvice: (ts_code: string) =>
    fetchJSON<AdaptiveGridAdvice>(`/api/v1/grid/advice/${encodeURIComponent(ts_code)}`),
  gridFeedback: (accountId: number, limit = 100) =>
    fetchJSON<GridAgentFeedback[]>(`/api/v1/grid/feedback?account_id=${accountId}&limit=${limit}`),
  gridLearnerStatus: () => fetchJSON<GridLearnerStatus>(`/api/v1/grid/learner/status`),
  trainGridLearner: (minSamples = 100) =>
    postJSON<GridLearnerStatus>(`/api/v1/grid/learner/train?min_samples=${minSamples}`, {}, 180_000),
  gridLearningRuns: () => fetchJSON<GridLearningRun[]>(`/api/v1/grid/learning/runs?view=live-v2`),
  gridLearningRun: (id: number) => fetchJSON<GridLearningRun>(`/api/v1/grid/learning/runs/${id}`),
  gridLearningResults: (runId: number) =>
    fetchJSON<GridLearningResult[]>(`/api/v1/grid/learning/runs/${runId}/results?passed_only=true&limit=300&view=live-v2`),
  applyGridLearningResult: (resultId: number, accountId: number) =>
    postJSON<{ applied: boolean; ts_code: string }>(`/api/v1/grid/learning/results/${resultId}/apply`, { account_id: accountId, qty_unit: 100 }),

  // ── 使用统计 ────────────────────────────
  usageStats: (days = 7) => fetchJSON<UsageStatsOut>(`/api/v1/usage/stats?days=${days}`),
  // ── 持仓深化：做 T 复盘 ────────────────────
  positionsTRecap: (days = 60, account_id?: number) => {
    const q = new URLSearchParams();
    q.set("days", String(days));
    if (account_id != null) q.set("account_id", String(account_id));
    return fetchJSON<TRecapOut>(`/api/v1/positions/t-recap?${q}`);
  },
  positionsRisk: (account_id?: number) => {
    const q = new URLSearchParams();
    if (account_id != null) q.set("account_id", String(account_id));
    return fetchJSON<RiskOut>(`/api/v1/positions/risk?${q}`);
  },
  rebalanceAdvice: async (account_id?: number): Promise<RebalanceAdviceOut | null> => {
    const q = account_id != null ? `?account_id=${account_id}` : "";
    const r = await fetch(`${BASE}/api/v1/positions/rebalance-advice${q}`, { credentials: CREDS });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`${r.status}`);
    const j = await r.json();
    return j ?? null;
  },
  regenerateRebalanceAdvice: (account_id?: number) =>
    postJSON<{ accepted: boolean; account_id: number }>(
      `/api/v1/positions/rebalance-advice/regenerate${account_id != null ? `?account_id=${account_id}` : ""}`, {}
    ),

  usageEvents: (params: { limit?: number; user_id?: number; event_type?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.user_id != null) q.set("user_id", String(params.user_id));
    if (params.event_type) q.set("event_type", params.event_type);
    return fetchJSON<UsageEventOut[]>(`/api/v1/usage/events?${q}`);
  },

  // ── intent (AI Copilot) ────────────────────────────────
  intentRun: (req: { description?: string; plan?: Record<string, unknown>; prev_plan?: Record<string, unknown>; thread_id?: number; run_token?: string; page_context?: CopilotPageContext; workspace?: "research" | "trading" | "publishing" }) =>
    postJSON<IntentRunResponse>(`/api/v1/intent/run`, req, 300_000),
  // 识图问数：截图 → Qwen3-VL 识别 → 32B 路由执行；返回结构同 intentRun
  intentVision: async (file: File, question: string, opts: { thread_id?: number; run_token?: string; workspace?: "research" | "trading" | "publishing" } = {}): Promise<IntentRunResponse> => {
    const fd = new FormData();
    const blob = await compressScreenshot(file);
    fd.append("file", blob, file.name || "screenshot.jpg");
    fd.append("question", question);
    if (opts.thread_id != null) fd.append("thread_id", String(opts.thread_id));
    if (opts.run_token) fd.append("run_token", opts.run_token);
    fd.append("workspace", opts.workspace ?? "research");
    const { signal, clear } = timeoutSignal(300_000);
    let res: Response;
    try {
      res = await fetch(`${BASE}/api/v1/intent/vision`, {
        method: "POST", credentials: CREDS, body: fd, signal,
      });
    } finally {
      clear();
    }
    if (!res.ok) throw new Error(`${res.status} ${(await res.text().catch(() => "")).slice(0, 200)}`);
    return (await res.json()) as IntentRunResponse;
  },
  // 会话线程（A 会话续接）
  intentThreads: (limit = 50) =>
    fetchJSON<IntentThreadSummary[]>(`/api/v1/intent/threads?limit=${limit}`),
  intentThread: (id: number) =>
    fetchJSON<IntentThreadDetail>(`/api/v1/intent/threads/${id}`),
  intentRenameThread: (id: number, title: string) =>
    fetch(`${BASE}/api/v1/intent/threads/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ title }),
    }).then((r) => {
      if (!r.ok) throw new Error(`rename failed: ${r.status}`);
      return r.json() as Promise<IntentThreadSummary>;
    }),
  intentDeleteThread: (id: number) => deleteJSON(`/api/v1/intent/threads/${id}`),
  intentProgress: (token: string) =>
    fetchJSON<IntentProgress>(`/api/v1/intent/progress?token=${encodeURIComponent(token)}`),
  stockSparklines: (ts_codes: string[], days = 20) =>
    postJSON<Record<string, number[]>>(`/api/v1/stocks/sparklines`, { ts_codes, days }),
  intentHistory: (limit = 20) =>
    fetchJSON<IntentPlanOut[]>(`/api/v1/intent/history?limit=${limit}`),
  intentHistoryOne: (id: number) =>
    fetchJSON<IntentPlanOut>(`/api/v1/intent/history/${id}`),
  intentHistoryAttachment: async (id: number): Promise<{ file: File; question: string }> => {
    const res = await fetch(`${BASE}/api/v1/intent/history/${id}/attachment`, { credentials: CREDS });
    if (!res.ok) throw new Error((await res.text().catch(() => "")) || `attachment failed: ${res.status}`);
    const decodeHeader = (name: string, fallback: string) => {
      const encoded = res.headers.get(name);
      if (!encoded) return fallback;
      try {
        const bytes = Uint8Array.from(atob(encoded), (char) => char.charCodeAt(0));
        return new TextDecoder().decode(bytes);
      } catch { return fallback; }
    };
    const blob = await res.blob();
    const filename = decodeHeader("X-Vision-Filename-B64", "screenshot.jpg");
    const question = decodeHeader("X-Vision-Question-B64", "");
    return { file: new File([blob], filename, { type: blob.type || "image/jpeg" }), question };
  },
  intentRunResearch: (id: number) =>
    postJSON<SelectionResearchReport>(`/api/v1/intent/history/${id}/research`, {}, 180_000),
  intentForwardResearch: (id: number) =>
    fetchJSON<SelectionForwardEvidence>(`/api/v1/intent/history/${id}/research/forward`, 180_000),
  intentCapabilities: () =>
    fetchJSON<IntentCapability[]>(`/api/v1/intent/capabilities`),
  intentListFavorites: () =>
    fetchJSON<IntentFavoriteOut[]>(`/api/v1/intent/favorites`),
  intentAddFavorite: (req: { name: string; description?: string; plan: Record<string, unknown> }) =>
    postJSON<IntentFavoriteOut>(`/api/v1/intent/favorites`, req),
  intentDeleteFavorite: (id: number) => deleteJSON(`/api/v1/intent/favorites/${id}`),
  intentRunFavorite: (id: number, workspace: "research" | "trading" | "publishing" = "research") =>
    postJSON<IntentRunResponse>(`/api/v1/intent/favorites/${id}/run?workspace=${workspace}`, {}, 300_000),

  intentSchedulePresets: () =>
    fetchJSON<Record<string, string>>(`/api/v1/intent/schedule/presets`),
  intentListScheduled: () =>
    fetchJSON<IntentPlanOut[]>(`/api/v1/intent/schedule`),
  intentSetSchedule: (planId: number, cron: string) =>
    fetch(`${BASE}/api/v1/intent/history/${planId}/schedule`, {
      method: "PUT",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cron }),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<IntentPlanOut>;
    }),
  intentUnschedule: (planId: number) =>
    deleteJSON(`/api/v1/intent/history/${planId}/schedule`),

  // 对话定义的自定义能力
  intentListCustom: () => fetchJSON<CustomCapOut[]>(`/api/v1/intent/custom`),
  intentDeleteCustom: (id: number) => deleteJSON(`/api/v1/intent/custom/${id}`),

  // 写操作暂存 → 确认（路线 B：pending_action）
  intentPending: (planId?: number) =>
    fetchJSON<PendingActionOut[]>(
      `/api/v1/intent/pending${planId != null ? `?plan_id=${planId}` : ""}`,
    ),
  intentConfirmPending: (ids: number[], workspace: "research" | "trading" | "publishing" = "trading") =>
    postJSON<PendingConfirmResult>(`/api/v1/intent/pending/confirm`, { ids, workspace }),
  intentCancelPending: (ids: number[]) =>
    postJSON<PendingConfirmResult>(`/api/v1/intent/pending/cancel`, { ids }),

  // 使用帮助多轮对话（保存 & 继续）
  helpConversations: () =>
    fetchJSON<HelpConvSummary[]>(`/api/v1/intent/help/conversations`),
  helpConversation: (id: number) =>
    fetchJSON<HelpConvDetail>(`/api/v1/intent/help/conversations/${id}`),
  helpDeleteConversation: (id: number) =>
    deleteJSON(`/api/v1/intent/help/conversations/${id}`),
  helpChat: (req: { conversation_id?: number; message: string }) =>
    postJSON<HelpChatResponse>(`/api/v1/intent/help/chat`, req, 120_000),

  // ── pair cointegration（全市场两两配对）──────────────────────
  cointAnomaly: (params: { biz_date?: string; industry?: string; same_industry?: boolean; min_abs_z?: number; min_return_correlation?: number; only_anomaly?: boolean; sort_by?: "stability" | "return_correlation" | "price_correlation" | "z_score"; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.biz_date) q.set("biz_date", params.biz_date);
    if (params.industry) q.set("industry", params.industry);
    if (params.same_industry != null) q.set("same_industry", String(params.same_industry));
    if (params.min_abs_z != null) q.set("min_abs_z", String(params.min_abs_z));
    if (params.min_return_correlation != null) q.set("min_return_correlation", String(params.min_return_correlation));
    if (params.only_anomaly != null) q.set("only_anomaly", String(params.only_anomaly));
    if (params.sort_by) q.set("sort_by", params.sort_by);
    if (params.limit != null) q.set("limit", String(params.limit));
    return fetchJSON<CointAnomalyOut[]>(`/api/v1/pairs/coint/anomaly?${q}`);
  },
  cointMeta: () => fetchJSON<CointMetaOut>(`/api/v1/pairs/coint/meta`),

  // ── MCP token mgmt ───────────────────────
  mcpListTokens: () => fetchJSON<McpTokenOut[]>(`/api/v1/mcp/tokens`),
  mcpCreateToken: (label: string) =>
    postJSON<McpTokenCreated>(`/api/v1/mcp/tokens`, { label }),
  mcpRevokeToken: (id: number) => deleteJSON(`/api/v1/mcp/tokens/${id}`),
  mcpInfo: () => fetchJSON<McpInfo>(`/api/v1/mcp/info`),
  cointSeries: (ts_a: string, ts_b: string, days = 120) =>
    fetchJSON<CointSeriesOut>(`/api/v1/pairs/coint/${ts_a}/${ts_b}/series?days=${days}`),
  cointOverlay: (ts_a: string, ts_b: string, days = 120) =>
    fetchJSON<CointOverlayOut>(`/api/v1/pairs/coint/${ts_a}/${ts_b}/overlay?days=${days}`),
  cointAdhocBacktest: (ts_a: string, ts_b: string, opts: { days?: number; lookback_days?: number; entry_z?: number; exit_z?: number; commission_bps?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.days != null) q.set("days", String(opts.days));
    if (opts.lookback_days != null) q.set("lookback_days", String(opts.lookback_days));
    if (opts.entry_z != null) q.set("entry_z", String(opts.entry_z));
    if (opts.exit_z != null) q.set("exit_z", String(opts.exit_z));
    if (opts.commission_bps != null) q.set("commission_bps", String(opts.commission_bps));
    return fetchJSON<CointAdhocBacktestOut>(`/api/v1/pairs/coint/${ts_a}/${ts_b}/backtest?${q}`, 180_000);
  },
  triggerCointScan: () =>
    postJSON<{ accepted: boolean }>(`/api/v1/pairs/coint/scan/trigger`, {}),

  // ── PairsStrategy CRUD（配对监控） ───────────────
  pairsList: () => fetchJSON<PairOut[]>(`/api/v1/pairs`),
  pairsCreate: (req: { name: string; ts_code_a: string; ts_code_b: string; lookback_days?: number; entry_z?: number; exit_z?: number; notes?: string }) =>
    postJSON<PairOut>(`/api/v1/pairs`, req),
  pairsDelete: (id: number) => deleteJSON(`/api/v1/pairs/${id}`),

  // ── 微信群消息（M11.WX）────────────────────────────
  wxListDevices: () => fetchJSON<WxDeviceOut[]>(`/api/v1/wechat/devices`),
  wxCreateDevice: (label: string) =>
    postJSON<WxDeviceCreated>(`/api/v1/wechat/devices`, { label }),
  wxRevokeDevice: (id: number) => deleteJSON(`/api/v1/wechat/devices/${id}`),
  wxListGroups: (enabledOnly = false) =>
    fetchJSON<WxGroupOut[]>(`/api/v1/wechat/groups${enabledOnly ? "?enabled_only=true" : ""}`),
  // ── 个股定向新闻源（官网/地方新闻爬取）─────────────────────
  watchSources: () => fetchJSON<WatchSourceOut[]>(`/api/v1/watch-sources`),
  watchSourceCreate: (body: { ts_code: string; url: string; label?: string; link_pattern?: string }) =>
    postJSON<WatchSourceOut>(`/api/v1/watch-sources`, body),
  watchSourcePatch: (id: number, patch: { label?: string; link_pattern?: string; enabled?: boolean; note?: string }) =>
    fetch(`${BASE}/api/v1/watch-sources/${id}`, {
      method: "PATCH", credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<WatchSourceOut>;
    }),
  watchSourceDelete: (id: number) => deleteJSON(`/api/v1/watch-sources/${id}`),
  watchSourceRun: (id: number) =>
    postJSON<{ status: string; n_links: number; n_new: number; n_kept: number }>(
      `/api/v1/watch-sources/${id}/run`, {}, 180_000),
  watchSourceItems: (ts_code?: string, limit = 50) => {
    const q = new URLSearchParams();
    if (ts_code) q.set("ts_code", ts_code);
    q.set("limit", String(limit));
    return fetchJSON<WatchItemOut[]>(`/api/v1/watch-sources/items?${q}`);
  },

  wxPatchGroup: (id: number, patch: { enabled?: boolean; name?: string; notes?: string | null }) =>
    fetch(`${BASE}/api/v1/wechat/groups/${id}`, {
      method: "PATCH",
      credentials: CREDS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<WxGroupOut>;
    }),
  wxListKeywords: () => fetchJSON<WxKeywordOut[]>(`/api/v1/wechat/keywords`),
  wxAddKeyword: (keyword: string) => postJSON<WxKeywordOut>(`/api/v1/wechat/keywords`, { keyword }),
  wxDeleteKeyword: (id: number) => deleteJSON(`/api/v1/wechat/keywords/${id}`),
  wxListMessages: (params: {
    group_id?: number;
    ts_code?: string;
    keyword?: string;
    hours?: number;
    limit?: number;
    offset?: number;
    include_dropped?: boolean;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.group_id != null) q.set("group_id", String(params.group_id));
    if (params.ts_code) q.set("ts_code", params.ts_code);
    if (params.keyword) q.set("keyword", params.keyword);
    if (params.hours != null) q.set("hours", String(params.hours));
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    if (params.include_dropped) q.set("include_dropped", "true");
    return fetchJSON<WxMessageListOut>(`/api/v1/wechat/messages?${q}`);
  },
  wxStats: (hours = 24) => fetchJSON<WxStatsOut>(`/api/v1/wechat/stats?hours=${hours}`),

  // ── Skill packs（M11.skills，OpenClaw 风格 prompt-pack）─────
  listSkills: () => fetchJSON<SkillListOut>(`/api/v1/skills/list`),
  setSkillEnabled: (skillName: string, enabled: boolean) =>
    postJSON<SkillListItem>(`/api/v1/skills/${encodeURIComponent(skillName)}/enabled`, { enabled }),
  runSkill: (req: { skill_name?: string; query?: string; args: Record<string, unknown>; use_llm_selector?: boolean }) =>
    postJSON<SkillRunOut>(`/api/v1/skills/run`, req),
  listSkillRuns: (params: { skill_name?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.skill_name) q.set("skill_name", params.skill_name);
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    return fetchJSON<SkillRunHistoryItem[]>(`/api/v1/skills/runs?${q}`);
  },
  rateSkillRun: (runId: number, rating: -1 | 0 | 1, note?: string) =>
    postJSON<SkillRunHistoryItem>(`/api/v1/skills/runs/${runId}/rate`, { rating, note }),

  // ── 双闭环量化 Agent（研究进化 + 审批式模拟交易）─────
  runQuantResearch: (req: QuantResearchRequest) =>
    postJSON<QuantLoopResult>(`/api/v1/quant-agents/research/run`, req, 180_000),
  runQuantTrading: (req: QuantTradingRequest) =>
    postJSON<QuantLoopResult>(`/api/v1/quant-agents/trading/run`, req, 120_000),
  quantAgentRuns: (loopType?: "research" | "trading") =>
    fetchJSON<QuantAgentRunOut[]>(`/api/v1/quant-agents/runs${loopType ? `?loop_type=${loopType}` : ""}`),
  quantAgentRun: (runId: number) =>
    fetchJSON<QuantAgentRunOut>(`/api/v1/quant-agents/runs/${runId}`),
  quantAgentDecisions: (runId: number) =>
    fetchJSON<QuantAgentDecisionOut[]>(`/api/v1/quant-agents/runs/${runId}/decisions`),
  quantAgentRunFeedback: (runId: number) =>
    fetchJSON<QuantAgentFeedbackOut[]>(`/api/v1/quant-agents/runs/${runId}/feedback`),
  submitQuantAgentFeedback: (decisionId: number, req: QuantAgentFeedbackInput) =>
    postJSON<QuantAgentFeedbackOut>(`/api/v1/quant-agents/decisions/${decisionId}/feedback`, req),
  quantAgentFeedbackSummary: (days = 90) =>
    fetchJSON<QuantAgentFeedbackSummaryOut>(`/api/v1/quant-agents/feedback/summary?days=${days}`),
  quantAgentStrategies: () =>
    fetchJSON<QuantAgentStrategyOut[]>(`/api/v1/quant-agents/strategies`),
  quantAgentAccountActivity: (accountId: number, limit = 100) =>
    fetchJSON<QuantAgentActivityOut[]>(`/api/v1/quant-agents/accounts/${accountId}/activity?limit=${limit}`),
  quantAgentAdoptPositions: (strategyId: number, tsCodes?: string[]) =>
    postJSON<QuantAgentStrategyOut>(`/api/v1/quant-agents/strategies/${strategyId}/adopt-positions`, {
      ts_codes: tsCodes?.length ? tsCodes : null,
    }),
  previewQuantAgentStrategy: (req: QuantAgentStrategyCreate) =>
    postJSON<QuantAgentStrategyPreview>(`/api/v1/quant-agents/strategies/preview`, req, 180_000),
  createQuantAgentStrategy: (req: QuantAgentStrategyCreate) =>
    postJSON<QuantAgentStrategyOut>(`/api/v1/quant-agents/strategies`, req),
  previewQuantAgentStrategyRun: (id: number) =>
    postJSON<QuantAgentStrategyPreview>(`/api/v1/quant-agents/strategies/${id}/preview`, {}, 180_000),
  patchQuantAgentStrategy: (id: number, patch: Partial<QuantAgentStrategyCreate>) =>
    fetch(`${BASE}/api/v1/quant-agents/strategies/${id}`, {
      method: "PATCH", credentials: CREDS,
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`${r.status} ${(await r.text()).slice(0, 200)}`);
      return r.json() as Promise<QuantAgentStrategyOut>;
    }),
  quantAgentStrategyAction: (id: number, action: "run" | "pause" | "resume" | "kill") =>
    postJSON<QuantAgentStrategyOut>(`/api/v1/quant-agents/strategies/${id}/${action}`, {}, 180_000),
  promoteQuantAgentStrategy: (id: number) =>
    postJSON<QuantAgentStrategyOut>(`/api/v1/quant-agents/strategies/${id}/promote`, {}, 180_000),
  backtestQuantAgentStrategy: (id: number, req: QuantStrategyBacktestRequest) =>
    postJSON<QuantStrategyBacktestResult>(`/api/v1/quant-agents/strategies/${id}/backtest`, req, 600_000),
  evolveQuantAgentStrategy: (id: number, req: QuantStrategyEvolutionRequest) =>
    postJSON<QuantStrategyEvolutionResult>(`/api/v1/quant-agents/strategies/${id}/evolve`, req, 600_000),
  quantAgentStrategyBacktests: (id: number, frequency?: "daily" | "1min", limit = 5) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (frequency) q.set("frequency", frequency);
    return fetchJSON<QuantStrategyBacktestHistory>(`/api/v1/quant-agents/strategies/${id}/backtests?${q}`);
  },
  previewQuantExperiment: (req: QuantExperimentSpec) =>
    postJSON<QuantExperimentPreview>(`/api/v1/quant-agents/experiments/preview`, req),
  createQuantExperiment: (req: QuantExperimentSpec & { confirmation_token: string }) =>
    postJSON<QuantExperimentOut>(`/api/v1/quant-agents/experiments`, req),
  quantExperiments: () =>
    fetchJSON<QuantExperimentOut[]>(`/api/v1/quant-agents/experiments`),
  quantExperimentReport: (id: number) =>
    fetchJSON<QuantExperimentReport>(`/api/v1/quant-agents/experiments/${id}/report`),
  quantExperimentAction: (id: number, action: "pause" | "resume" | "kill") =>
    postJSON<QuantExperimentOut>(`/api/v1/quant-agents/experiments/${id}/${action}`, {}),
  brokerDashboard: () => fetchJSON<BrokerDashboardOut>(`/api/v1/broker/dashboard`),
  createBrokerOrder: (req: BrokerOrderCreate) =>
    postJSON<{ id: number; client_order_id: string; status: string }>(`/api/v1/broker/orders`, req),
  cancelBrokerOrder: (id: number) =>
    postJSON<{ id: number; status: string }>(`/api/v1/broker/orders/${id}/cancel`, {}),
  approveBrokerOrder: (id: number) =>
    postJSON<{ id: number; status: string }>(`/api/v1/broker/orders/${id}/approve`, {}),
  rejectBrokerOrder: (id: number) =>
    postJSON<{ id: number; status: string }>(`/api/v1/broker/orders/${id}/reject`, {}),
};

export interface BrokerConnectionOut {
  id: number; bridge_id: string; account_id: number | null; broker: string; environment: "simulation" | "live";
  status: string; connected: boolean; account_subscribed: boolean; trading_enabled: boolean;
  account_no_masked: string | null; detail: string | null; last_heartbeat_at: string | null;
}
export interface BrokerDashboardOut {
  connections: BrokerConnectionOut[];
  assets: Array<{ connection_id: number; total_asset: number; cash: number; market_value: number; captured_at: string }>;
  positions: Array<{ connection_id: number; symbol: string; quantity: number; available_quantity: number; average_price: number; market_value: number; captured_at: string }>;
  orders: Array<{ id: number; client_order_id: string; connection_id: number; symbol: string; side: string; quantity: number; limit_price: number; status: string; source: string; order_owner: string; strategy_name: string | null; filled_quantity: number; average_fill_price: number | null; error_message: string | null; created_at: string }>;
  fills: Array<{ id: number; connection_id: number; broker_trade_id: string; symbol: string; side: string; quantity: number; price: number; strategy_name: string | null; traded_at: string }>;
}
export interface BrokerOrderCreate {
  connection_id: number; symbol: string; side: "buy" | "sell"; quantity: number; limit_price: number;
  strategy_name?: string; remark?: string; source?: "manual" | "agent" | "strategy";
}

export interface QuantResearchRequest {
  description: string;
  name?: string;
  ts_codes?: string[];
  basket_id?: number;
  group_id?: number;
  horizon?: number;
  lookback_days?: number;
  top_n?: number;
  stage_publish?: boolean;
}

export interface QuantTradingRequest {
  account_id: number;
  name?: string;
  ts_codes?: string[];
  basket_id?: number;
  group_id?: number;
  selection_tool?: "screen_indicator" | "query_stocks";
  selection_args?: Record<string, unknown>;
  buy_score?: number;
  sell_score?: number;
  max_positions?: number;
  max_single_weight?: number;
  initial_position_weight?: number;
  dry_run?: boolean;
  approval_ttl_minutes?: number;
  strategy_kind?: "balanced" | "trend" | "momentum" | "breakout" | "volume" | "hotspot_breakout" | "relative_rotation" | "report_signal" | "hybrid_signal" | "daily_dog" | "capital_bottom_ws_v1" | "pair_mean_reversion" | "adaptive_intraday_grid";
  pair_id?: number;
  pair_max_notional?: number;
  pair_entry_z?: number;
  pair_exit_z?: number;
  pair_lookback_days?: number;
  report_signal_field?: "rating" | "upside" | "coverage" | "orgs" | "roe";
  report_window_days?: number;
  indicator_weight?: number;
  report_weight?: number;
  relative_baselines?: Record<string, number>;
  relative_entry_pct?: number;
  relative_top_n?: number;
  rotation_fraction?: number;
  rotation_cooldown_minutes?: number;
  signal_weights?: Record<string, number>;
}

export interface QuantLoopResult {
  run_id: number | null;
  loop_type: "research" | "trading";
  status: string;
  pending_action_id?: number | null;
  pending_action_ids?: number[];
  factor?: Record<string, unknown>;
  review?: Record<string, unknown>;
  decisions?: QuantAgentDecisionView[];
  preview_orders?: QuantAgentPreviewOrder[];
  dry_run?: boolean;
  portfolio?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface QuantAgentPreviewOrder {
  ts_code: string;
  name?: string;
  side: string;
  qty: number;
  reference_price: number;
  estimated_value?: number;
  reason?: string;
  initial_base_entry?: boolean;
}

export interface QuantAgentStrategyPreview {
  status: "ready" | "partial" | "conflict";
  persisted: false;
  account_id: number | null;
  request: Record<string, unknown>;
  portfolio: Record<string, unknown>;
  orders: QuantAgentPreviewOrder[];
  decisions: QuantAgentDecisionView[];
  universe_size?: number;
  note?: string;
  ownership_conflicts?: Array<{
    strategy_id: number;
    strategy_name: string;
    stock_codes: string[];
  }>;
}

export interface QuantAgentDecisionView {
  ts_code: string;
  name?: string;
  action: string;
  score: number;
  confidence: number;
  evidence: Record<string, unknown>;
  risk: Record<string, unknown>;
  order_intent?: Record<string, unknown> | null;
}

export interface QuantAgentRunOut {
  id: number;
  loop_type: "research" | "trading";
  mode: string;
  name: string;
  status: string;
  account_id: number | null;
  strategy_id: number | null;
  request: Record<string, unknown>;
  state: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error_msg: string | null;
  started_at: string;
  completed_at: string | null;
}

export interface QuantAgentStrategyCreate {
  name: string;
  loop_type: "research" | "trading";
  account_id?: number | null;
  request?: Record<string, unknown>;
  schedule_type?: "cron" | "interval" | "event";
  cron_expr?: string | null;
  interval_minutes?: number | null;
  event_types?: ("minute_bar" | "market_anomaly")[];
  session_phase?: "any" | "premarket" | "intraday" | "postmarket" | "closed";
  approval_mode?: "manual" | "auto_paper";
  catch_up?: boolean;
  max_retries?: number;
  retry_delay_seconds?: number;
  start_paused?: boolean;
}

export interface QuantAgentStrategyOut {
  id: number;
  name: string;
  loop_type: "research" | "trading";
  mode: string;
  account_id: number | null;
  enabled: boolean;
  status: string;
  request: Record<string, unknown>;
  scope_codes: string[];
  strategy_family: "research" | "indicator" | "report" | "pair" | "hybrid";
  signal_sources: string[];
  schedule_type: "cron" | "interval" | "event";
  cron_expr: string | null;
  interval_minutes: number | null;
  event_types: string[];
  session_phase: string;
  approval_mode: "manual" | "auto_paper";
  catch_up: boolean;
  max_retries: number;
  retry_delay_seconds: number;
  consecutive_failures: number;
  next_run_at: string | null;
  last_run_at: string | null;
  last_success_at: string | null;
  last_run_id: number | null;
  last_error: string | null;
  kill_switch: boolean;
  created_at: string;
  updated_at: string;
}

export interface QuantAgentActivityOut {
  decision_id: number;
  run_id: number;
  strategy_id: number | null;
  strategy_name: string | null;
  ts_code: string | null;
  action: string;
  score: number | null;
  confidence: number | null;
  evidence: Record<string, unknown>;
  risk: Record<string, unknown>;
  pending_action_id: number | null;
  trigger: string;
  execution_mode: string;
  market_phase: string | null;
  preview_only: boolean;
  checked_at: string;
}

export interface QuantStrategyBacktestRequest {
  frequency?: "daily" | "1min";
  start?: string;
  end?: string;
  initial_cash?: number;
  fee_rate?: number;
  slippage_rate?: number;
  rebalance_days?: number;
  max_volume_participation?: number;
  max_pool_size?: number;
}

export interface QuantExperimentArmIn {
  name: string;
  request: Record<string, unknown>;
}

export interface QuantExperimentSpec {
  name: string;
  stock_pool: string[];
  arms: QuantExperimentArmIn[];
  initial_cash: number;
  benchmark?: string;
  start_at: string;
  end_at: string;
  schedule_type?: "cron" | "interval" | "event";
  cron_expr?: string | null;
  interval_minutes?: number | null;
  event_types?: ("minute_bar" | "market_anomaly")[];
  session_phase?: "any" | "premarket" | "intraday" | "postmarket" | "closed";
  execution_spec?: Record<string, unknown>;
  risk_spec?: Record<string, unknown>;
  close_positions_on_end?: boolean;
}

export interface QuantExperimentPreview extends QuantExperimentSpec {
  confirmation_token: string;
  stock_pool_hash: string;
  total_capital: number;
  requires_confirmation: boolean;
  warnings: string[];
}

export interface QuantExperimentArmOut {
  id: number;
  arm_index: number;
  name: string;
  strategy_spec: Record<string, unknown>;
  account_id: number | null;
  strategy_id: number | null;
  arena_entry_id: number | null;
}

export interface QuantExperimentOut {
  id: number;
  name: string;
  status: string;
  stock_pool: string[];
  stock_pool_hash: string;
  initial_cash: number;
  benchmark: string;
  start_at: string;
  end_at: string;
  execution_spec: Record<string, unknown>;
  risk_spec: Record<string, unknown>;
  close_positions_on_end: boolean;
  started_at: string | null;
  completed_at: string | null;
  final_report: QuantExperimentReport | null;
  last_error: string | null;
  arms: QuantExperimentArmOut[];
}

export interface QuantExperimentReport {
  experiment_id: number;
  name: string;
  status: string;
  start_at: string;
  end_at: string;
  stock_pool: string[];
  stock_pool_hash: string;
  benchmark: string;
  benchmark_return_pct: number | null;
  benchmark_start_date: string | null;
  benchmark_end_date: string | null;
  generated_at: string;
  data_quality: {
    expected_sessions: number;
    benchmark_available: boolean;
    all_arms_have_snapshots: boolean;
    min_coverage_pct: number | null;
  };
  fairness: {
    same_initial_cash: boolean;
    same_frozen_stock_pool: boolean;
    same_execution_spec: boolean;
    same_risk_spec: boolean;
  };
  leaderboard: Array<Record<string, number | string | null>>;
  winner: Record<string, number | string | null> | null;
}

export interface QuantStrategyBacktestResult {
  run_id: number;
  strategy_id: number;
  strategy_name: string;
  mode: "historical" | "historical_1min";
  frequency?: "1min";
  start: string;
  end: string;
  metrics: Record<string, number | null>;
  trades: Record<string, unknown>[];
  equity_curve: Record<string, unknown>[];
  n_trades: number;
  n_signals?: number;
  strategy_kind?: string;
  baseline_mode?: string;
  baseline_prices?: Record<string, number>;
  signals?: Array<Record<string, unknown>>;
  selected_stocks?: Array<{ ts_code: string; name?: string | null; score: number; selected_on: string }>;
  limitations: string[];
}

export interface QuantStrategyBacktestHistory {
  strategy_id: number;
  strategy_name: string;
  rows: Record<string, unknown>[];
  n: number;
  latest_result: QuantStrategyBacktestResult | null;
}

export interface QuantAgentDecisionOut {
  id: number;
  run_id: number;
  decision_type: string;
  ts_code: string | null;
  action: string;
  score: number | null;
  confidence: number | null;
  evidence: Record<string, unknown>;
  risk: Record<string, unknown>;
  order_intent: Record<string, unknown> | null;
  pending_action_id: number | null;
  created_at: string;
}

export interface QuantStrategyEvolutionRequest {
  start: string;
  end: string;
  initial_cash: number;
  fee_rate?: number;
  slippage_rate?: number;
  folds?: number;
  benchmark?: string;
  max_drawdown_pct?: number;
}

export interface QuantStrategyEvolutionFold {
  start: string;
  end: string;
  return_pct: number;
  benchmark_return_pct: number | null;
  excess_return_pct: number;
  max_drawdown_pct: number;
  sharpe: number | null;
  n_round_trips: number;
  market_regime: { regime?: string; trend?: string; volatility?: string; risk_level?: string };
  selected_stocks: Array<{ ts_code: string; name?: string | null; score: number; selected_on: string }>;
}

export interface QuantStrategyEvolutionResult {
  run_id: number;
  strategy_id: number;
  strategy_name: string;
  strategy_kind: string;
  start: string;
  end: string;
  benchmark: string;
  tested_candidates: number;
  strict_holdout: boolean;
  recommended_params: Record<string, unknown>;
  development_score: number;
  development_passed: boolean;
  development_folds: QuantStrategyEvolutionFold[];
  holdout: QuantStrategyEvolutionFold;
  holdout_gate: Record<string, boolean>;
  passed: boolean;
  decision: "candidate_for_paper" | "keep_current_strategy";
  research_context: Array<{
    factor_id: number;
    name: string;
    expression: string;
    rank_ic_mean: number | null;
    walk_forward_passed: boolean;
  }>;
  research_context_used_for_selection: boolean;
  limitations: string[];
}

export type QuantAgentUsefulness = "helpful" | "neutral" | "not_helpful";
export type QuantAgentFeedbackReason =
  | "false_positive" | "missed_signal" | "too_late" | "unclear"
  | "data_issue" | "risk_missing" | "strategy_mismatch";
export type QuantAgentUserAction = "accepted" | "ignored" | "modified" | "not_traded";
export type QuantAgentDesiredChange = "more_conservative" | "more_aggressive" | "less_alerts" | "more_explanation";

export interface QuantAgentFeedbackInput {
  usefulness: QuantAgentUsefulness;
  reason_codes?: QuantAgentFeedbackReason[];
  user_action?: QuantAgentUserAction | null;
  desired_change?: QuantAgentDesiredChange | null;
  note?: string | null;
}

export interface QuantAgentFeedbackOut {
  id: number;
  run_id: number;
  decision_id: number;
  usefulness: QuantAgentUsefulness;
  reason_codes: QuantAgentFeedbackReason[];
  user_action: QuantAgentUserAction | null;
  desired_change: QuantAgentDesiredChange | null;
  note: string | null;
  anchor_trade_date: string | null;
  outcome_status: "pending" | "partial" | "matured" | "unavailable";
  outcome_due_at: string | null;
  outcome_1d: number | null;
  outcome_5d: number | null;
  outcome_20d: number | null;
  objective_label: "correct" | "incorrect" | "mixed" | "not_applicable" | null;
  outcome_snapshot: Record<string, unknown>;
  matured_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuantAgentFeedbackSummaryOut {
  since: string;
  total: number;
  helpful: number;
  neutral: number;
  not_helpful: number;
  pending_outcomes: number;
  partial_outcomes: number;
  matured_outcomes: number;
  objective_correct: number;
  objective_incorrect: number;
  reason_counts: Record<string, number>;
}

// ── Skill packs 类型 ─────────────────────────────────

export interface SkillListItem {
  name: string;
  version: number;
  title: string;
  description: string;
  trigger_keywords: string[];
  required_args: string[];
  required_capabilities: string[];
  estimated_duration_sec: number;
  source: string;
  enabled: boolean;
  protected: boolean;
  validation_status: string;
  access_level: "read_only" | "confirmation_required";
}

export interface SkillListOut {
  skills: SkillListItem[];
}

export interface CapabilityResult {
  name: string;
  ok: boolean;
  result: Record<string, unknown> | null;
  error: string | null;
  duration_ms: number;
}

export interface SkillRunOut {
  run_id: number | null;
  skill_name: string;
  skill_title: string;
  final_answer: string;
  capability_results: CapabilityResult[];
  total_duration_ms: number;
  args: Record<string, unknown>;
}

export interface SkillRunHistoryItem {
  id: number;
  skill_name: string;
  skill_version: number;
  selector: string;     // "explicit" | "keyword" | "llm"
  query: string | null;
  args: Record<string, unknown>;
  total_duration_ms: number;
  cap_ok: number;
  cap_fail: number;
  started_at: string;
  rating: number | null;     // +1 / -1 / null
  rating_note: string | null;
  rated_at: string | null;
  final_answer_preview: string;
  error: string | null;
}

// ── 微信群消息类型 ─────────────────────────────────

export interface WxDeviceOut {
  id: number;
  label: string;
  wx_account_hint: string | null;
  chatlog_version: string | null;
  bridge_version: string | null;
  active: boolean;
  created_at: string;
  last_seen_at: string | null;
  last_ingest_at: string | null;
  n_messages: number;
}

export interface WxDeviceCreated extends WxDeviceOut {
  token: string;
}

export interface WatchSourceOut {
  id: number;
  ts_code: string;
  stock_name: string | null;
  url: string;
  label: string | null;
  link_pattern: string | null;
  enabled: boolean;
  last_fetch_at: string | null;
  last_status: string | null;
  last_n_new: number | null;
  note: string | null;
}

export interface WatchItemOut {
  id: number;
  title: string;
  summary: string | null;
  url: string | null;
  published_at: string | null;
  sentiment: number | null;
  impact: string | null;
  event_type: string | null;
  ts_code: string;
  stock_name: string | null;
}

export interface WxGroupOut {
  id: number;
  talker_id: string;
  name: string;
  member_count: number | null;
  enabled: boolean;
  notes: string | null;
  first_seen_at: string;
  last_message_at: string | null;
  n_messages: number;
}

export interface WxKeywordOut {
  id: number;
  keyword: string;
  created_at: string;
}

export interface WxMessageOut {
  id: number;
  group_id: number;
  group_name: string;
  sender_name: string | null;
  msg_time: string;
  content: string;
  msg_type: string;
  sentiment: number | null;
  is_dropped: boolean;
  ts_codes: string[];
}

export interface WxMessageListOut {
  items: WxMessageOut[];
  count: number;
}

export interface WxStatsOut {
  hours: number;
  n_messages: number;
  n_dropped: number;
  n_groups_enabled: number;
  n_groups_total: number;
  n_devices_active: number;
  last_seen_at: string | null;
  top_groups: { name: string; n: number }[];
  top_tickers: { ts_code: string; n: number }[];
}

export interface CointAnomalyOut {
  ts_a: string; ts_b: string;
  name_a: string | null; name_b: string | null;
  industry_a: string | null; industry_b: string | null;
  biz_date: string;
  price_correlation: number | null;
  return_correlation: number | null;
  stability_score: number;
  observed_snapshots: number;
  beta: number | null;
  z_score: number | null;
  half_life: number | null;
  adf_pvalue: number | null;
  sample_days: number;
}
export interface CointSeriesPoint {
  trade_date: string;
  spread: number;
  z: number;
}
export interface CointSeriesOut {
  ts_a: string; ts_b: string;
  name_a: string | null; name_b: string | null;
  n: number;
  beta: number; alpha: number;
  z_now: number; adf_pvalue: number; half_life: number;
  points: CointSeriesPoint[];
}
export interface CointDateStat {
  biz_date: string;
  total_pairs: number;
  anomalies: number;
  same_industry_pairs: number;
  cross_industry_pairs: number;
}
export interface CointMetaOut {
  dates: CointDateStat[];
  industries: string[];
}
export interface CointOverlayPricePoint { trade_date: string; qfq: number; }
export interface CointOverlayNewsItem {
  ts_code: string;
  published_at: string;
  title: string;
  sentiment: number | null;
  impact: string | null;
}
export interface CointOverlayOut {
  ts_a: string; ts_b: string;
  name_a: string | null; name_b: string | null;
  prices_a: CointOverlayPricePoint[];
  prices_b: CointOverlayPricePoint[];
  news: CointOverlayNewsItem[];
}
export interface CointAdhocBacktestStat {
  n_trades: number;
  pnl_pct: number | null;
  pnl_gross_pct: number | null;
  commission_pct: number | null;
  win_rate: number | null;
  avg_holding_days: number | null;
  max_z_seen: number | null;
  min_z_seen: number | null;
}
export interface CointAdhocBacktestOut {
  ts_a: string; ts_b: string;
  name_a: string | null; name_b: string | null;
  start_date: string; end_date: string;
  lookback_days: number;
  entry_z: number; exit_z: number;
  beta: number;
  commission_bps: number;
  stats: CointAdhocBacktestStat;
  trades: { entry_date: string; exit_date: string; side: string; entry_z: number; exit_z: number; pnl: number; pnl_gross?: number; commission?: number; holding_days: number }[];
}
export interface PairOut {
  id: number; name: string;
  ts_code_a: string; ts_code_b: string;
  name_a: string | null; name_b: string | null;
  lookback_days: number; entry_z: number; exit_z: number;
  notes: string | null; enabled: boolean;
  created_at: string; updated_at: string;
}
export interface McpTokenOut {
  id: number;
  label: string;
  created_at: string;
  last_used_at: string | null;
  masked_token: string;
}
export interface McpTokenCreated {
  id: number;
  label: string;
  token: string;
  created_at: string;
}
export interface McpInfo {
  endpoint: string;
  transport: string;
  auth_header: string;
  tools_count: number;
  resources: string[];
  claude_code_config: Record<string, unknown>;
}

// ── intent types ──────────────────────────────────────
export interface IntentCapability {
  name: string;
  description: string;
  module?: string | null;
  effect?: "read" | "write" | "mixed";
  confirmation?: "none" | "staged" | "conditional";
  resource_scope?: "market" | "user";
}
export interface NavigateIntentAction {
  type: "navigate";
  label: string;
  route: string;
  auto_execute?: boolean;
  navigation_source?: "open_page";
}
export interface PageUpdateIntentAction {
  type: "page_update";
  label: string;
  page_id: string;
  command: string;
  payload: Record<string, unknown>;
}
export type IntentAction = NavigateIntentAction | PageUpdateIntentAction;
export interface CopilotPageContext {
  page_id: string;
  path: string;
  state: Record<string, unknown>;
}
export interface IntentPlanOut {
  id: number;
  description: string;
  name: string | null;
  status: string;             // 'pending' | 'running' | 'done' | 'failed'
  error_msg: string | null;
  plan: { name?: string; steps?: IntentStep[] } & Record<string, unknown>;
  render: IntentRender | null;
  elapsed_ms: number | null;
  created_at: string;
  completed_at: string | null;
  schedule_cron: string | null;
  thread_id?: number | null;
  turn_index?: number | null;
  research?: SelectionResearchReport | null;
}
export interface SelectionResearchReport {
  plan_id?: number;
  status: string;
  summary: string;
  winner_by_training?: string | null;
  horizon_trading_days?: number;
  top_n?: number;
  tracking?: { rule_name: string; snapshot_days: number; first_date: string | null; last_date: string | null };
  variants?: { name: string; training: { folds: number; observations: number; excess_pct: number | null; return_pct: number | null; win_rate: number | null; avg_drawdown_pct: number | null }; later_period: { folds: number; observations: number; excess_pct: number | null; return_pct: number | null; win_rate: number | null; avg_drawdown_pct: number | null } }[];
}
export interface SelectionForwardEvidence {
  rule_name: string;
  snapshot_days: number;
  first_date: string | null;
  last_date: string | null;
  entry_events: number;
  studied_events: number;
  matured_events: number;
  missing_to_30: number;
  mean_20d_pct: number | null;
  win_rate_20d: number | null;
  mean_ci_low: number | null;
  mean_ci_high: number | null;
  decision: string;
  evidence: string;
  reason: string;
  weight: number;
  note: string;
}

export interface LlmTraceSummary {
  trace_id: string;
  parent_trace_id: string | null;
  task: string;
  provider: string;
  model: string;
  status: string;
  duration_ms: number;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  structural_score: number | null;
  training_allowed: boolean;
  reviewed_by: string | null;
  review_note: string | null;
  reviewed_at: string | null;
  created_at: string;
  has_correction: boolean;
}

export interface LlmTraceDetail extends LlmTraceSummary {
  request: Record<string, unknown>;
  response: Record<string, unknown> | null;
  corrected_response: Record<string, unknown> | null;
  quality: Record<string, unknown>;
  error_class: string | null;
  error_detail: string | null;
}

export interface LlmTrainingStats {
  days: number;
  groups: Array<{
    provider: string;
    task: string;
    calls: number;
    success_rate: number;
    approved: number;
    avg_duration_ms: number;
  }>;
}
export interface IntentStep { id: string; tool: string; args: Record<string, unknown> }
export interface IntentRender {
  layout: "table" | "list" | "metric_grid" | "markdown" | "compare";
  title?: string;
  summary?: string | null;
  provenance?: { as_of?: string | null; source?: unknown; quality_filters?: unknown; personalization?: unknown } | null;
  columns?: { key: string; label: string; format?: string }[] | null;
  rows?: unknown[] | null;
  directions?: (string | null)[] | null;          // compare：逐指标方向 higher/lower/neutral
  stocks?: { ts_code: string; name: string | null }[] | null;  // compare：股票列
  price_series?: { trade_date: string; values: Record<string, number> }[] | null;
  n?: number | null;
  route?: string | null;   // open_page 导航：前端据此跳转到功能页
  route_label?: string | null; // 有文案时保留查询结果，并在卡片内展示导航按钮
  actions?: IntentAction[] | null; // 受控结果动作：站内导航或当前页面草稿更新
  caveats?: { aspect: string; suggestion?: string }[] | null;  // 计划层自报"够不着的点"+建议（诚实优先）
}
export interface CustomCapOut {
  id: number;
  name: string;
  description: string | null;
  spec: {
    kind?: string;
    criteria?: Record<string, unknown>;
    terms?: { field: string; weight: number }[];
    top_n?: number;
  };
  created_at: string;
}
export interface IntentFavoriteOut {
  id: number;
  name: string;
  description: string | null;
  plan: { name?: string; steps?: IntentStep[] } & Record<string, unknown>;
  sort_order: number;
  created_at: string;
}
export interface AgentWorkflowState {
  run_id: string;
  status: string;
  current_role: string;
  tasks?: { task_id: string; tool: string; role: string }[];
  steps?: { task_id: string; tool: string; role: string; status: string; error?: string | null }[];
  evidence?: { kind: string; source: string; value: unknown; caveats?: string[] }[];
  approvals?: { approval_id: string; action: string; status: string; risk_level: string }[];
  errors?: string[];
}
export interface IntentRunResponse {
  plan_id: number;
  thread_id?: number | null;
  turn_index?: number | null;
  ok: boolean;
  error?: string | null;
  plan?: { name?: string; steps?: IntentStep[] } & Record<string, unknown>;
  render?: IntentRender | null;
  step_outputs?: Record<string, unknown> | null;
  agent_state?: AgentWorkflowState | null;
  elapsed_ms?: number | null;
}
export interface IntentThreadSummary {
  id: number;
  title: string;
  n_turns: number;
  last_description: string | null;
  updated_at: string;
  created_at: string;
}
export interface IntentThreadDetail {
  id: number;
  title: string;
  turns: IntentPlanOut[];
  created_at: string;
  updated_at: string;
}
export interface IntentProgress {
  phase: "parsing" | "executing" | "done" | "unknown";
  idx?: number;       // 当前第几步（1-based）
  total?: number;     // 总步数
  current?: string;   // 当前 tool
  tools?: string[];   // 各步 tool 列表
}
export interface PendingActionOut {
  id: number;
  action_type: string;
  payload: Record<string, unknown>;
  preview: Record<string, unknown> | null;
  status: string;
  created_at: string;
  expires_at: string | null;
}
export interface PendingConfirmResult {
  confirmed: number;
  skipped: number;
  applied: string[];
  skipped_details: string[];
}
export interface HelpConvSummary {
  id: number;
  title: string;
  n_messages: number;
  updated_at: string;
}
export interface HelpMessage {
  role: "user" | "assistant";
  content: string;
  at?: string | null;
}
export interface HelpConvDetail {
  id: number;
  title: string;
  messages: HelpMessage[];
  created_at: string;
  updated_at: string;
}
export interface HelpChatResponse {
  conversation_id: number;
  title: string;
  reply: string;
}
// ── M9.2 lab: 篮子 ──────────────────────────────────────

export interface BasketMember {
  ts_code: string;
  name: string | null;
  industry: string | null;
  target_weight: number | null;     // null = 等权
}

export interface BasketOut {
  id: number;
  name: string;
  ts_codes: string[];
  members: BasketMember[];
  target_weight: Record<string, number> | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  n_stocks: number;
}

export interface BasketCreate {
  name: string;
  ts_codes: string[];
  target_weight?: Record<string, number> | null;
  notes?: string | null;
}

export interface BasketPatch {
  name?: string;
  ts_codes?: string[];
  target_weight?: Record<string, number> | null;
  notes?: string | null;
}

export interface RebalanceLine {
  ts_code: string;
  name: string | null;
  side: "buy" | "sell" | "hold";
  qty: number;
  price_hint: number;
  target_weight: number;
  current_weight: number;
  weight_diff: number;
  target_value: number;
  current_value: number;
  est_amount: number;
}

// ── 篮子自动化 ─────────────────────────────────────
export type AutomationKind = "dca" | "protect" | "rebalance";

export interface AutomationOut {
  id: number;
  basket_id: number;
  basket_name: string | null;
  account_id: number;
  account_name: string | null;
  kind: AutomationKind;
  enabled: boolean;
  params: Record<string, unknown>;
  last_run_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface AutomationCreate {
  basket_id: number;
  account_id: number;
  kind: AutomationKind;
  params: Record<string, unknown>;
  enabled?: boolean;
}

export interface AutomationPatch {
  params?: Record<string, unknown>;
  enabled?: boolean;
  account_id?: number;
}

// ── 策略赛马 Arena ─────────────────────────────────
export interface ArenaEntryMetrics {
  id: number;
  name: string;
  account_id: number;
  account_name: string | null;
  benchmark: string;
  base_date: string | null;
  enabled: boolean;
  n_snapshots: number;
  nav_start: number | null;
  nav_last: number | null;
  ret_pct: number | null;
  bench_ret_pct: number | null;
  excess_pct: number | null;
  max_drawdown_pct: number | null;
}

export interface ArenaEntryCreate {
  account_id: number;
  name: string;
  benchmark?: string;
}

export interface ArenaCurvePoint {
  date: string;
  nav: number;
  bench: number | null;
}

export interface ArenaEntryCurve {
  id: number;
  name: string;
  points: ArenaCurvePoint[];
}

// ── M10.C 市场体检 ─────────────────────────────────

export interface MarketBriefOut {
  biz_date: string;
  summary_md: string;
  regime: string | null;
  main_lines: string[];
  risks: string[];
  metrics: Record<string, unknown>;
  model: string | null;
  generated_at: string;
}

export interface MarketBriefHistoryItem {
  biz_date: string;
  regime: string | null;
  main_lines: string[];
  generated_at: string;
}

export interface TopgunMetric {
  key: string;
  label: string;
  value: number | null;
  percentile: number | null;
  note: string | null;
  group?: string;   // daily / monthly / context
  asof?: string | null;   // 该分项数据实际日期
}

export interface TopgunSnapshotOut {
  trade_date: string;
  heat_partial: number | null;
  heat_daily?: number | null;
  heat_monthly?: number | null;
  monthly_asof?: string | null;
  metrics: TopgunMetric[];
  is_partial: boolean;
  caveat: string;
  computed_at: string;
}

export interface RpsLeader {
  ts_code: string;
  name: string | null;
  industry: string | null;
  rs_120d: number | null;
  rps_120: number | null;
  rank_120: number | null;
  universe_size: number | null;
  trade_date: string | null;
  rps_60: number | null;
}

export interface RpsTheme {
  name: string;
  industries?: string[];
  leaders?: { ts_code: string; name?: string }[];
  rationale?: string;
}

export interface IndustryStrengthItem {
  industry: string;
  n_strong: number;
  median_rps_120: number;
  top_codes: string[];
}

export interface RpsBriefOut {
  biz_date: string;
  summary_md: string;
  themes: RpsTheme[];
  leaders: RpsLeader[];
  industry_strength: IndustryStrengthItem[];
  model: string | null;
  generated_at: string;
}

export interface RpsHistoryItem {
  biz_date: string;
  n_themes: number;
  theme_names: string[];
  generated_at: string;
}

export interface RpsSeriesPoint {
  trade_date: string;
  close: number | null;
  rps_60: number | null;
  rps_120: number | null;
  rs_60d: number | null;
  rs_120d: number | null;
  industry_median_rps_120: number | null;
  market_rank_120: number | null;
  market_count: number | null;
  industry_rank_120: number | null;
  industry_count: number | null;
}

export interface RpsBreakoutItem {
  ts_code: string;
  name: string | null;
  industry: string | null;
  rps_120_now: number;
  rps_120_prev: number;
  jump: number;
  rs_120d_now: number | null;
  rps_60_now: number | null;
  days: number;
}

export interface RpsBacktestStat {
  n: number;
  win_rate: number;
  mean_ret: number;
  median_ret: number;
  max_gain: number;
  max_loss: number;
  p25: number;
  p75: number;
}

export interface RpsBacktestSample {
  ts_code: string;
  name: string | null;
  industry: string | null;
  entry_date: string;
  rps_120: number | null;
  rs_120d: number | null;
  entry_qfq: number;
  ret: Record<string, number>;
}

export interface RpsBacktestOut {
  threshold: number;
  lookback_days: number;
  hold_days: number[];
  n_triggers: number;
  n_samples: number;
  by_hold: Record<string, RpsBacktestStat | null>;
  samples: RpsBacktestSample[];
}

export interface RpsRotationItem {
  industry: string;
  med_rps_120_now: number;
  med_rps_120_prev: number;
  jump: number;
  n_strong_now: number;
  n_strong_prev: number;
  delta_strong: number;
  top_codes_now: string[];
  days: number;
}

export interface ScreenRuleSummary {
  rule_name: string;
  rule_kind: "custom_screen" | "intraday_track_group" | string;
  rule_ref_id: number | null;
  rule_spec?: { tool?: string; args?: Record<string, unknown> } | null;
  n_runs: number;
  last_run_date: string;
  last_n: number;
  prev_run_date: string | null;
  entered: number;
  exited: number;
}

export interface ScreenRunItem {
  run_date: string;
  n: number;
  source: string;
}

export interface ScreenDiffMember {
  ts_code: string;
  name: string | null;
  industry: string | null;
  rps_120: number | null;
  or_yoy: number | null;
  pct_chg: number | null;
  streak: number | null;
  first_hit_date: string | null;
  hit_price: number | null;
  latest_date: string | null;
  latest_price: number | null;
  current_return_pct: number | null;
  max_gain_pct: number | null;
  max_drawdown_pct: number | null;
  tracking_days: number | null;
}

export interface RpsRankItem {
  ts_code: string;
  trade_date: string;
  rps_120: number | null;
  rps_60: number | null;
  rank_120: number | null;
  universe_size: number;
}

export interface PoolTrendPoint {
  trade_date: string;
  count: number;
  entered: number;
  exited: number;
  kept: number;
}

export interface PoolTreeNode {
  id: string;
  label: string;
  kind: "root" | "category" | "strategy" | string;
  count: number;
  entered: number;
  exited: number;
  kept: number;
  codes: string[];
  history: PoolTrendPoint[];
  children: PoolTreeNode[];
}

export interface PoolTreeOut {
  as_of: string | null;
  days: number;
  strategy_count: number;
  root: PoolTreeNode;
}

export interface StockPoolEntryEvent {
  strategy_name: string;
  strategy_kind: string;
  entered_on: string;
  recorded_at: string;
  reason: string;
}

export interface CompositeScreenCriteria {
  industry?: string;
  score20_min?: number;
  rps120_min?: number;
  mom20_rank_min?: number;
  pe_min?: number;
  pe_max?: number;
  pb_max?: number;
  roe_min?: number;
  market_cap_min?: number;
  market_cap_max?: number;
  turnover_min?: number;
  exclude_st?: boolean;
  sort_by?: "composite_score" | "rps_120" | "mom_20d" | "pe" | "roe" | "circ_mv";
  sort_desc?: boolean;
  limit?: number;
}

export interface CompositeScreenRow extends Record<string, unknown> {
  ts_code: string;
  name?: string | null;
  industry?: string | null;
}

export interface CompositeScreenResult {
  rows: CompositeScreenRow[];
  total: number;
  note?: string | null;
  as_of?: string | null;
  quality_filters?: { rejected?: { stale?: number; st?: number; new_listing?: number; illiquid?: number }; shared_gate?: boolean } | null;
  personalization?: { applied?: boolean; reason?: string } | null;
}

export interface ScreenConceptCluster {
  concept: string;
  n: number;
}

export interface ScreenDiffOut {
  rule_name: string;
  run_prev: string | null;
  run_curr: string;
  entered: ScreenDiffMember[];
  exited: ScreenDiffMember[];
  kept: ScreenDiffMember[];
  n_prev: number;
  n_curr: number;
  entered_concepts: ScreenConceptCluster[];
}

export interface ScreenSignalWindow {
  horizon: number;
  n: number;
  mean_pct: number | null;
  median_pct: number | null;
  win_rate: number | null;
  avg_mdd_pct: number | null;
  mean_ci_low: number | null;
  mean_ci_high: number | null;
  win_rate_ci_low: number | null;
  win_rate_ci_high: number | null;
  evidence: "insufficient" | "moderate" | "strong";
}

export interface ScreenSignalStudyOut {
  rule_name: string;
  kind: "entered" | "exited";
  n_events: number;
  n_with_data: number;
  windows: ScreenSignalWindow[];
  note: string;
}

export interface IndustryMomentumItem {
  industry: string;
  trade_date: string;
  n_stocks: number;
  pct_today: number | null;
  pct_5d: number | null;
  pct_20d: number | null;
  pct_60d: number | null;
  avg_amount_20d_yi: number | null;
  n_strong_now: number;
  leader_code: string | null;
  leader_name: string | null;
  leader_pct: number | null;
}

export interface SwingTarget {
  price: number;
  tag: string | null;
}

export interface SwingPlanOut {
  ts_code: string;
  name: string | null;
  industry: string | null;
  biz_date: string;
  stance: string | null;
  confidence: number | null;
  hold_days_low: number | null;
  hold_days_high: number | null;
  entry_low: number | null;
  entry_high: number | null;
  stop_loss: number | null;
  targets: SwingTarget[];
  rationale: string | null;
  risks: string[];
  summary_md: string;
  features: Record<string, unknown>;
  model: string | null;
  generated_at: string;
}

export interface SwingPlanBrief {
  ts_code: string;
  name: string | null;
  biz_date: string;
  stance: string | null;
  confidence: number | null;
  entry_low: number | null;
  entry_high: number | null;
  stop_loss: number | null;
  target_t1: number | null;
  target_t2: number | null;
  hold_days_low: number | null;
  hold_days_high: number | null;
  generated_at: string;
}

export interface GridLevel {
  level: number;
  price: number;
  side: "buy" | "sell";
  qty: number;
  hint: string | null;
}

export interface GridOut {
  account_id: number;
  ts_code: string;
  name: string | null;
  industry: string | null;
  qty: number;
  avg_cost: number;
  anchor_price: number;
  atr14: number | null;
  atr_pct: number | null;
  step_pct: number;
  qty_unit: number;
  levels: GridLevel[];
  status: "active" | "paused";
  last_triggered_level: number | null;
  last_triggered_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  source: "position" | "custom";
  anchor_type: string;
  n_per_side: number;
  execution: GridExecutionConfig;
  risk: GridRiskState;
}

export interface GridRiskState {
  sellable_qty: number;
  position_notional: number;
  symbol_limit: number;
  symbol_remaining: number;
  grid_total: number;
  grid_limit: number;
  grid_remaining: number;
  last_block_reason?: string | null;
  last_block_at?: string | null;
}

export interface GridExecutionConfig {
  mode: "alerts_only" | "auto_simulation" | "live_approval";
  connection_id?: number | null;
  updated_at?: string;
}

export interface GridBacktestOut {
  ok: boolean;
  note?: string;
  ts_code?: string;
  start?: string;
  end?: string;
  n_days?: number;
  params?: { step_pct: number; n_per_side: number; qty_unit: number; anchor_mode: string };
  t_profit?: number;
  t_profit_pct?: number | null;
  fees?: number;
  n_round_trips?: number;
  n_opens?: number;
  n_open_left?: number;
  unrealized_left?: number;
  level_hits?: Record<string, number>;
  base_value?: number;
  buyhold_pct?: number;
}

export interface GridOptimizeOut {
  ok: boolean;
  note?: string;
  ts_code?: string;
  start?: string;
  end?: string;
  results?: { step_pct: number; n_per_side: number; t_profit: number; t_profit_pct: number | null; n_round_trips: number; n_open_left: number }[];
  best?: { step_pct: number; n_per_side: number; t_profit_pct: number | null } | null;
}

export interface GridAlert {
  id: number;
  ts_code: string;
  name: string | null;
  biz_date: string;
  alert_time: string;
  alert_type: "grid_t_buy" | "grid_t_sell";
  level: number;
  level_price: number;
  current_price: number;
  qty: number;
  hint: string | null;
}

export interface TPair {
  ts_code: string;
  name: string | null;
  industry: string | null;
  buy_at: string;
  sell_at: string;
  qty: number;
  buy_price: number;
  sell_price: number;
  pnl: number;
  pnl_pct: number;
  direction: "T+0_buy_first" | "T+0_sell_first";
  hold_minutes: number;
}

export interface TStockSummary {
  ts_code: string;
  name: string | null;
  industry: string | null;
  n_pairs: number;
  total_pnl: number;
  win_rate: number;
  avg_pnl_per_pair: number;
  cost_reduction: number | null;
  pairs: TPair[];
}

export interface TRecapOut {
  days: number;
  n_trades: number;
  n_pairs: number;
  total_pnl: number;
  win_rate: number;
  avg_pnl_per_pair: number;
  by_stock: TStockSummary[];
  pairs_recent: TPair[];
}

export interface IndustryExposure {
  industry: string;
  value: number;
  pct: number;
  n_stocks: number;
  top_codes: string[];
}

export interface StockHolding {
  ts_code: string;
  name: string | null;
  industry: string | null;
  qty: number;
  avg_cost: number;
  last_close: number | null;
  value: number;
  pct: number;
  gain_loss_pct: number | null;
  atr14_pct: number | null;
}

export interface ConcentrationInfo {
  n_stocks: number;
  top1_pct: number;
  top3_pct: number;
  top5_pct: number;
  hhi: number;
}

export interface CorrelationMatrix {
  codes: string[];
  names: (string | null)[];
  matrix: number[][];
}

export interface RiskOut {
  asof: string | null;
  total_value: number;
  total_cost: number;
  total_unrealized_pnl: number;
  n_stocks: number;
  by_industry: IndustryExposure[];
  holdings: StockHolding[];
  concentration: ConcentrationInfo;
  correlation: CorrelationMatrix | null;
  portfolio_vol_pct: number | null;
}

export interface AdviceItem {
  ts_code: string;
  name: string | null;
  industry: string | null;
  reason: string | null;
  target_pct_change: string | null;
  suggested_pct: string | null;
}

export interface RebalanceAdviceOut {
  account_id: number;
  biz_date: string;
  overall: string | null;
  reduce: AdviceItem[];
  increase: AdviceItem[];
  new_buys: AdviceItem[];
  structure_notes: string[];
  summary_md: string;
  inputs_snapshot: Record<string, unknown>;
  model: string | null;
  generated_at: string;
}

export interface UsagePageStat {
  target: string;
  pv: number;
  uv: number;
  avg_duration_ms: number | null;
  last_at: string;
}

export interface UsageUserStat {
  user_id: number | null;
  user_name: string | null;
  n_events: number;
  n_pages: number;
  last_at: string;
}

export interface UsageActionStat {
  action: string;
  n: number;
  last_at: string;
}

export interface UsageJumpStat {
  referrer: string;
  target: string;
  n: number;
}

export interface UsageStatsOut {
  days: number;
  n_events: number;
  n_users: number;
  pages: UsagePageStat[];
  users: UsageUserStat[];
  actions: UsageActionStat[];
  jumps: UsageJumpStat[];
}

export interface UsageEventOut {
  id: number;
  user_id: number | null;
  user_name: string | null;
  event_type: string;
  target: string;
  referrer: string | null;
  payload: Record<string, unknown> | null;
  server_ts: string;
}

export interface PairOut {
  id: number;
  name: string;
  ts_code_a: string;
  ts_code_b: string;
  name_a: string | null;
  name_b: string | null;
  lookback_days: number;
  entry_z: number;
  exit_z: number;
  notes: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface PairCreate {
  name: string;
  ts_code_a: string;
  ts_code_b: string;
  lookback_days?: number;
  entry_z?: number;
  exit_z?: number;
  notes?: string | null;
}

export interface PairPatch {
  name?: string;
  lookback_days?: number;
  entry_z?: number;
  exit_z?: number;
  notes?: string | null;
  enabled?: boolean;
}

export interface ZSeriesPoint {
  trade_date: string;
  log_a: number;
  log_b: number;
  spread: number;
  spread_mean: number | null;
  spread_std: number | null;
  z: number | null;
}

export interface ZSeriesOut {
  pair_id: number;
  ts_code_a: string;
  ts_code_b: string;
  lookback_days: number;
  entry_z: number;
  exit_z: number;
  n_obs: number;
  beta: number | null;
  intercept: number | null;
  current_z: number | null;
  price_correlation: number | null;
  return_correlation: number | null;
  adf_pvalue: number | null;
  half_life: number | null;
  is_cointegrated: boolean | null;
  current_signal: "long_a_short_b" | "short_a_long_b" | "neutral";
  points: ZSeriesPoint[];
}

export interface PairBacktestStat {
  n_trades: number;
  pnl_pct: number | null;
  win_rate: number | null;
  avg_holding_days: number | null;
  max_z_seen: number | null;
  min_z_seen: number | null;
}

export interface PairBacktestOut {
  pair_id: number;
  start_date: string;
  end_date: string;
  stats: PairBacktestStat;
  trades: {
    entry_date: string;
    exit_date: string;
    side: "long_spread" | "short_spread";
    entry_z: number;
    exit_z: number;
    pnl: number;
    holding_days: number;
  }[];
}

export interface RebalanceOut {
  basket_id: number;
  account_id: number;
  account_name: string;
  nav: number;
  holdings_value: number;
  cash_balance: number;
  skipped: { ts_code: string; reason: string }[];
  lines: RebalanceLine[];
}

// ── 持仓 PnL 账本 ──────────────────────────────────────────

export interface TradeIn {
  ts_code: string;
  side: "buy" | "sell" | "short" | "cover";
  qty: number;
  price: number;
  fee?: number;
  traded_at?: string;
  note?: string | null;
  reason?: string | null;
  account_id?: number | null;
}

export interface TradeOut {
  id: number;
  account_id: number;
  ts_code: string;
  name: string | null;
  side: "buy" | "sell" | "short" | "cover";
  qty: number;
  price: number;
  fee: number;
  traded_at: string;
  note: string | null;
  reason: string | null;
  ret_5d: number | null;
  ret_20d: number | null;
  review_at: string | null;
  source: string;
  quant_agent_strategy_id: number | null;
  quant_agent_run_id: number | null;
  quant_agent_decision_id: number | null;
  order_group_id: number | null;
}

export interface AdaptiveGridAdvice {
  ts_code: string;
  as_of: string;
  close: number;
  atr14: number;
  atr_pct: number;
  adx14: number;
  plus_di14: number;
  minus_di14: number;
  regime: "sideways" | "transition" | "strong_uptrend" | "strong_downtrend" | "extreme_volatility";
  eligible: boolean;
  status: "active" | "paused";
  step_pct: number;
  n_per_side: number;
  qty_fraction: number;
  bar_interval: "5min" | "15min" | "30min" | "60min";
  confirmation_bars: number;
  lower_price: number;
  upper_price: number;
  confidence: number;
  reasons: string[];
}

export interface GridWalkForwardCandidate {
  step_pct: number;
  n_per_side: number;
  fold_returns: number[];
  n_round_trips: number;
  oos_avg: number | null;
  oos_std: number | null;
  score: number | null;
  positive_ratio: number;
  passed: boolean;
}

export interface GridWalkForwardOut {
  ok: boolean;
  note: string;
  ts_code?: string;
  start?: string;
  end?: string;
  folds?: number;
  candidates?: GridWalkForwardCandidate[];
  recommended?: GridWalkForwardCandidate | null;
  gate?: { passed: boolean; rules: string };
}

export interface GridAgentFeedback {
  id: number;
  account_id: number;
  ts_code: string;
  decision_date: string;
  as_of_date: string;
  regime: string;
  decision_status: "active" | "paused";
  parameters: Record<string, unknown>;
  outcomes: Record<string, {
    as_of: string;
    close_return_pct: number;
    max_up_pct: number;
    max_down_pct: number;
    realized_range_pct: number;
    oscillation: boolean;
    breakout: boolean;
    decision_was_prudent: boolean;
  }>;
  evaluation_status: "pending" | "partial" | "complete";
}

export interface GridLearnerStatus {
  status: "not_trained" | "insufficient" | "blocked" | "rejected" | "candidate";
  n_samples: number;
  min_samples?: number;
  version?: string;
  trained_at?: string;
  reason: string;
  gate?: {
    passed: boolean;
    validation_samples: number;
    accuracy: number;
    baseline_accuracy: number;
    accuracy_lift: number;
    auc: number | null;
  };
}

export interface GridLearningRun {
  id: number;
  strategy: string;
  rps_asof: string;
  start_date: string;
  end_date: string;
  succeeded: number;
  failed: number;
  passed_gate: number;
  completed_at: string | null;
}

export interface GridLearningResult {
  id: number;
  ts_code: string;
  name: string | null;
  industry: string | null;
  rps_120: number | null;
  step_pct: number | null;
  n_per_side: number | null;
  score: number | null;
  oos_avg: number | null;
  positive_ratio: number | null;
  n_round_trips: number | null;
  last_applied_at: string | null;
}

export interface PositionOut {
  account_id: number;
  account_name: string | null;
  ts_code: string;
  name: string | null;
  industry: string | null;
  position_side: "long" | "short";
  qty: number;
  avg_cost: number;
  last_close: number | null;
  market_value: number | null;
  cost_basis: number;
  unrealized_pnl: number | null;
  unrealized_pct: number | null;
  realized_pnl: number;
  total_fees: number;
  first_bought_at: string | null;
  last_traded_at: string | null;
}

export interface PositionsHistoryPoint {
  trade_date: string;
  market_value: number;
  cost_basis: number;
  unrealized_pnl: number;
  realized_cumul: number;
  fees_cumul: number;
  total_pnl: number;
  invested: number;
  benchmark_value: number | null;
}

export interface PositionsHistoryOut {
  start_date: string;
  end_date: string;
  points: PositionsHistoryPoint[];
  benchmark_index: string | null;
}

export interface PortfolioSummary {
  account_id: number | null;
  n_positions: number;
  n_closed: number;
  total_market_value: number;
  total_cost_basis: number;
  total_unrealized: number;
  total_realized: number;
  total_fees: number;
  total_pnl: number;
  positions: PositionOut[];
}

export interface ReportRow {
  id: number;
  ts_code: string;
  name: string | null;
  report_date: string;
  report_title: string;
  report_type: string | null;
  org_name: string | null;
  author_name: string | null;
  quarter: string | null;
  rating: string | null;
  eps: number | null;
  pe: number | null;
  roe: number | null;
  np: number | null;
  max_price: number | null;
  min_price: number | null;
}

export interface ReportLlmSummary {
  ts_code: string;
  name: string | null;
  n_reports: number;
  summary: string;
  rating_dist: Record<string, number>;
  avg_target_price: number | null;
  target_price_range: [number, number] | null;
  cited_report_ids: number[];
}

export interface StockReportSummary {
  ts_code: string;
  name: string | null;
  n_reports: number;
  rating_dist: Record<string, number>;
  avg_target_price: number | null;
  latest: ReportRow[];
}

export interface AlertRuleCreate {
  name: string;
  rule_type: "price_chg" | "factor_score";
  params: Record<string, unknown>;
  scope: "watchlist" | "all" | `industry:${string}`;
  enabled: boolean;
  cooldown_h: number;
}

export interface AlertRuleOut extends AlertRuleCreate {
  id: number;
  created_at: string;
  last_run_at: string | null;
  recent_fired_24h: number;
}

export type AlertSeverity = "critical" | "warning" | "info";

export interface AlertEventOut {
  id: number;
  rule_id: number;
  rule_name: string | null;
  ts_code: string | null;
  fired_at: string;
  title: string;
  body: string;
  payload: Record<string, unknown>;
  sent_count: number;
  severity: AlertSeverity;
}

export interface RadarDim {
  label: string;
  score: number | null;       // 0..100；null 数据不足
  raw: number | null;
  detail: string;
}

export interface RadarOut {
  ts_code: string;
  name: string | null;
  industry: string | null;
  asof: string | null;
  composite: number | null;
  dims: RadarDim[];
}

export interface IndustryBoardRow {
  board_name: string;
  trade_date: string;
  pct_chg: number | null;
  amount: number | null;
  net_flow: number | null;
  n_up: number | null;
  n_down: number | null;
  leading_stock: string | null;
  leading_pct: number | null;
}

export interface IndustryRecentPoint {
  trade_date: string;
  pct_chg: number | null;
  net_flow: number | null;
}

export interface IndustryContext {
  ts_code: string;
  industry: string | null;
  matched: boolean;
  asof: string | null;
  pct_chg: number | null;
  net_flow: number | null;
  n_up: number | null;
  n_down: number | null;
  leading_stock: string | null;
  leading_pct: number | null;
  pct_rank: number | null;
  flow_rank: number | null;
  total_industries: number;
  recent: IndustryRecentPoint[];
}

export interface SectorCatalogRow {
  kind: "industry" | "concept" | "area";
  code: string | null;
  name: string;
  member_count: number;
  asof: string | null;
  pct_chg: number | null;
}

export interface SectorCatalog {
  asof: string | null;
  coverage: Record<"industry" | "concept" | "area", number>;
  rows: SectorCatalogRow[];
}

export interface IndustryMember {
  ts_code: string;
  name: string | null;
  pct_chg: number | null;
  close: number | null;
  amount_yi: number | null;
  rps_120: number | null;
  rps_60: number | null;
  global_rank: number | null;
  industry_rank: number | null;
  composite_score: number | null;
  tier: "leader" | "strong" | "watch" | "lagging";
}

export interface IndustryDetail {
  board_name: string;
  asof: string | null;
  matched: boolean;
  pct_chg: number | null;
  net_flow: number | null;
  amount: number | null;
  n_up: number | null;
  n_down: number | null;
  leading_stock: string | null;
  leading_pct: number | null;
  pct_rank: number | null;
  flow_rank: number | null;
  total_industries: number;
  median_rps_120: number | null;
  strong_count: number;
  recent: IndustryRecentPoint[];
  members: IndustryMember[];
}

export interface MlExperimentConfig {
  horizon: number;
  train_days: number;
  holdout_days: number;
  features?: string[] | null;
  hyperparams?: Record<string, unknown> | null;
}

export interface MlExperimentCreate {
  name: string;
  config: MlExperimentConfig;
}

export interface MlExperimentOut {
  id: number;
  name: string;
  config: MlExperimentConfig;
  status: "pending" | "running" | "completed" | "failed";
  model_version: string | null;
  holdout_ic: number | null;
  holdout_mse: number | null;
  n_train: number | null;
  n_holdout: number | null;
  error_msg: string | null;
  notes?: string | null;
  tags?: string[] | null;
  created_at: string;
  completed_at: string | null;
  is_active: boolean;
  feature_importance?: Record<string, number> | null;
  inverted?: boolean;
}

export interface PaperTradingPoint {
  eval_date: string;
  pred_date: string;
  n_samples: number;
  realized_ic: number | null;
  top10_return: number | null;
  bot10_return: number | null;
  long_short: number | null;
}

export interface PaperTradingGroup {
  source: "live" | "simulation";
  n_days: number;
  avg_realized_ic: number | null;
  cum_long_short: number | null;
  points: PaperTradingPoint[];
}

export interface PaperTradingSummary {
  model_version: string;
  live: PaperTradingGroup;
  simulation: PaperTradingGroup;
}

export interface MlPredRow {
  ts_code: string;
  name: string | null;
  industry: string | null;
  trade_date: string;
  horizon: number;
  pred_return: number;       // 预测未来 N 日收益 %
  pred_rank: number | null;  // 0..100 横截面排名
  model_version: string;
}

export interface StockPrediction {
  horizon: number;
  trade_date: string;
  model_version: string;
  pred_return: number;
  pred_rank: number | null;
  universe_size: number;
  holdout_ic: number | null;
  is_active: boolean;
}

export interface StockPredictionsOut {
  ts_code: string;
  name: string | null;
  industry: string | null;
  predictions: StockPrediction[];
}

export interface PredictionSummary {
  ts_code: string;
  horizon: number;
  trade_date: string;
  pred_return: number;
}

export interface TimelinePoint {
  trade_date: string;
  horizon: number;
  model_version: string;
  pred_return: number;
  pred_rank: number | null;
  universe_size: number | null;
  eval_date: string | null;
  realized_return: number | null;
  is_active_model: boolean;
  horizon_due: boolean;
}
export interface StockPredictionTimelineOut {
  ts_code: string;
  name: string | null;
  horizon: number;
  n_points: number;
  n_due: number;
  n_pending: number;
  avg_pred: number | null;
  std_pred: number | null;
  avg_realized: number | null;
  points: TimelinePoint[];
}

export interface ForwardTrackingRow {
  ts_code: string;
  name: string | null;
  industry: string | null;
  horizon: number;
  active_model: string | null;
  latest_pred_date: string | null;
  latest_pred: number | null;
  latest_rank: number | null;
  universe_size: number | null;
  n_points: number;
  avg_pred: number | null;
  std_pred: number | null;
  n_due: number;
  n_pending: number;
  avg_realized: number | null;
  same_dir_rate: number | null;
  avg_abs_err: number | null;
}
export interface ForwardTrackingOut {
  horizon: number;
  days: number;
  n_stocks: number;
  rows: ForwardTrackingRow[];
}

export interface MlAlertSettingIn {
  ts_code: string;
  horizon: number;
  enabled: boolean;
  notify_prediction_change: boolean;
  change_threshold_pct: number;
  notify_prediction_reversal: boolean;
  reversal_min_pct: number;
  notify_due: boolean;
  notify_error: boolean;
  error_threshold_pct: number;
  dedup_minutes: number;
  quiet_start: string | null;
  quiet_end: string | null;
}

export interface MlAlertSettingOut extends MlAlertSettingIn {
  explicit: boolean;
  updated_at: string | null;
}

export interface MlAlertEventOut {
  id: number;
  ts_code: string;
  name: string | null;
  horizon: number;
  event_type: string;
  trade_date: string;
  model_version: string;
  source: string;
  severity: string;
  payload: Record<string, unknown>;
  notified_at: string | null;
  suppressed_reason: string | null;
  created_at: string;
}

export interface MlPerformanceBucket {
  model_version: string;
  horizon: number;
  source: string;
  is_active: boolean;
  n_predictions: number;
  n_evaluated: number;
  n_pending: number;
  avg_pred: number | null;
  avg_realized: number | null;
  avg_abs_error: number | null;
  win_rate: number | null;
  same_direction_rate: number | null;
  avg_realized_ic: number | null;
  avg_long_short: number | null;
  n_paper_days: number;
  sample_quality: string;
}

export interface MlPerformanceOut {
  days: number;
  horizon: number | null;
  source: string;
  last_computed_at: string | null;
  buckets: MlPerformanceBucket[];
}

export interface JumpFeatureDiff {
  name: string;
  prev: number;
  curr: number;
  delta: number;
  abs_delta: number;
}
export interface JumpAttributionOut {
  ts_code: string;
  name: string | null;
  industry: string | null;
  trade_date: string;
  horizon: number;
  model_version: string;
  prev_pred: number;
  curr_pred: number;
  delta: number;
  rolling_std: number;
  jump_z: number;
  n_history: number;
  feature_diff: JumpFeatureDiff[] | null;
  news_count: number | null;
  has_anomaly: boolean;
  has_minute_alert: boolean;
  has_top_list: boolean;
  has_recent_fina: boolean;
  primary_cause: string | null;
  primary_cause_label: string | null;
  confidence: number | null;
  attribution_md: string | null;
  llm_model: string | null;
  created_at: string;
}

export interface PushSubscriptionItem {
  endpoint: string;
  endpoint_short: string;
  user_agent: string;
  created_at: string | null;
  last_used: string | null;
}
export interface PushSubscriptionsList {
  count: number;
  items: PushSubscriptionItem[];
}

export interface AutoTrainRunOut {
  id: number;
  horizon: number;
  config: Record<string, number>;
  new_model_version: string | null;
  new_holdout_ic: number | null;
  new_oos_avg_ic: number | null;
  new_oos_std_ic: number | null;
  new_score: number | null;
  prev_model_version: string | null;
  prev_oos_avg_ic: number | null;
  prev_oos_std_ic: number | null;
  prev_score: number | null;
  activated: boolean;
  activation_reason: string | null;
  triggered_by: string;
  started_at: string;
  finished_at: string | null;
  error_msg: string | null;
}
export interface AutoTrainActive {
  horizon: number;
  model_version: string | null;
  holdout_ic: number | null;
  train_start: string | null;
  train_end: string | null;
}

export interface AutoTuneCandidate {
  params: Record<string, number | string>;
  is_sharpe: number | null;
  is_return_pct: number | null;
  is_mdd_pct: number | null;
  oos_sharpes: number[];
  oos_avg: number | null;
  oos_std: number | null;
  score: number | null;
  error: string | null;
  n_obs: number;
}

export interface AutoTuneRunOut {
  id: number;
  strategy: string;
  scope: Record<string, unknown>;
  grid: Record<string, (number | string)[]>;
  n_combos: number;
  candidates: AutoTuneCandidate[];
  recommended: AutoTuneCandidate | null;
  prev_active: { params?: Record<string, number | string>; score?: number } | null;
  activated: boolean;
  activation_reason: string | null;
  triggered_by: string;
  started_at: string;
  finished_at: string | null;
  error_msg: string | null;
}

export interface AutoTuneActive {
  strategy: string;
  params: Record<string, number | string> | null;
  score: number | null;
  oos_avg: number | null;
  oos_std: number | null;
  is_sharpe: number | null;
  activated_at: string | null;
}

export interface WalkForwardFoldOut {
  fold: number;
  train_start: string;
  train_end: string;
  test_start: string;
  test_end: string;
  n_train: number;
  n_test: number;
  oos_ic: number | null;
  oos_mse: number | null;
}
export interface WalkForwardResultOut {
  horizon: number;
  n_folds_completed: number;
  train_days: number;
  test_days: number;
  features: string[];
  avg_oos_ic: number | null;
  std_oos_ic: number | null;
  min_oos_ic: number | null;
  max_oos_ic: number | null;
  folds: WalkForwardFoldOut[];
}

export interface DecayPoint {
  eval_date: string;
  realized_ic: number | null;
  long_short: number | null;
  source: string;
  n_samples: number;
}

export interface ModelDecayOut {
  model_version: string;
  experiment_name: string | null;
  horizon: number;
  is_active: boolean;
  holdout_ic: number | null;
  n_paper_obs: number;
  avg_realized_ic: number | null;
  avg_long_short: number | null;
  points: DecayPoint[];
}

export interface TushareApiStat {
  calls: number;
  ok: number;
  fail: number;
  last_err: { at: string; msg: string } | null;
}

export interface JobRunEntry {
  verification_status?: "complete" | "incomplete" | "unverified";
  duration_ms?: number | null;
  result?: Record<string, unknown>;
  job_id: string;
  status: "ok" | "error" | "missed" | string;
  ran_at: string;
  scheduled_for: string | null;
  error: string | null;
}

export interface JobRunByJob {
  last_verification_status?: string;
  job_id: string;
  n_total: number;
  n_ok: number;
  n_error: number;
  n_missed: number;
  last_status: string;
  last_ran_at: string;
  last_error: string | null;
}

export interface JobRunSummary {
  n_unverified?: number;
  n_entries: number;
  n_jobs: number;
  n_ok: number;
  n_error: number;
  n_missed: number;
  by_job: JobRunByJob[];
  recent: JobRunEntry[];
}

export interface TushareQuotaSummary {
  hours: number;
  total_calls: number;
  total_ok: number;
  total_fail: number;
  fail_rate: number;
  by_api: Record<string, TushareApiStat>;
  failing_apis: (TushareApiStat & { api: string })[];
}

export interface CorrPair { a: string; b: string; corr: number }
export interface StockBeta { ts_code: string; name: string | null; beta: number | null; n_obs: number }
export interface PortfolioAnalysisOut {
  ts_codes: string[];
  names: Record<string, string | null>;
  industries: Record<string, string | null>;
  start_date: string;
  end_date: string;
  n_days: number;
  correlations: CorrPair[];
  betas: StockBeta[];
  avg_pairwise_corr: number | null;
  portfolio_beta_equal: number | null;
  portfolio_beta_weighted: number | null;
  weights: Record<string, number> | null;
  beta_by_window: Record<number, number> | null;
  portfolio_beta_by_window: Record<number, number> | null;
}

export interface MlTrainingOut {
  model_version: string;
  horizon: number;
  train_start: string;
  train_end: string;
  n_train: number;
  n_holdout: number;
  holdout_ic: number | null;
  holdout_mse: number | null;
  created_at: string;
}

export interface HsgtPoint {
  trade_date: string;
  north_money: number | null;       // 百万元
  hgt: number | null;
  sgt: number | null;
}

export interface HsgtSeries {
  days: number;
  points: HsgtPoint[];
  sum_north_5d: number | null;
  sum_north_20d: number | null;
}

export interface StockMfRow {
  ts_code: string;
  name: string | null;
  industry: string | null;
  trade_date: string;
  net_mf_amount: number | null;       // 万元
  buy_elg_amount: number | null;
  sell_elg_amount: number | null;
  buy_lg_amount: number | null;
  sell_lg_amount: number | null;
}

export interface StockMfPoint {
  trade_date: string;
  net_mf_amount: number | null;
  buy_elg_amount: number | null;
  sell_elg_amount: number | null;
  buy_lg_amount: number | null;
  sell_lg_amount: number | null;
}

export interface StockMfSeries {
  ts_code: string;
  days: number;
  points: StockMfPoint[];
  sum_net_5d: number | null;
  sum_net_20d: number | null;
  in_watchlist: boolean;
}

export interface HorizonStat {
  horizon: number;
  n: number;
  mean_pct: number | null;
  median_pct: number | null;
  win_rate: number | null;       // 0..1
  avg_mdd_pct: number | null;
  mean_ci_low: number | null;
  mean_ci_high: number | null;
  win_rate_ci_low: number | null;
  win_rate_ci_high: number | null;
  evidence: "insufficient" | "moderate" | "strong";
}

export interface SelectionReplayRequest {
  rule_name: string;
  start: string;
  end: string;
  execution_modes: Array<"next_open" | "next_close" | "minute_fixed" | "minute_vwap">;
  fixed_time: string;
  initial_cash: number;
  max_positions: number;
  commission_rate: number;
  stamp_duty_rate: number;
  slippage_bps: number;
  min_commission: number;
  max_volume_participation: number;
}

export interface SelectionReplayOrder {
  ts_code: string;
  side: "buy" | "sell";
  status: "filled" | "partial" | "unfilled";
  qty?: number;
  price?: number;
  amount?: number;
  fee?: number;
  reason?: string;
}

export interface SelectionReplayResult {
  mode: SelectionReplayRequest["execution_modes"][number];
  metrics: { total_return_pct: number; annualized_return_pct: number; max_drawdown_pct: number; annualized_volatility_pct: number; sharpe: number | null; turnover: number };
  curve: Array<{ trade_date: string; signal_date: string | null; nav: number; cash: number; market_value: number; orders: SelectionReplayOrder[] }>;
  orders_filled: number;
  orders_unfilled: number;
  blocked_reasons: Record<string, number>;
}

export interface SelectionReplayOut {
  rule_name: string;
  start: string;
  end: string;
  snapshot_count: number;
  stock_count: number;
  results: SelectionReplayResult[];
  notes: string[];
}

export interface StrategyLeagueEntry {
  rank: number;
  rule_name: string;
  rule_kind: string;
  n_runs: number;
  n_events: number;
  n_matured: number;
  mean_20d_pct: number | null;
  win_rate_20d: number | null;
  mean_ci_low: number | null;
  mean_ci_high: number | null;
  decision: "promote" | "neutral" | "observe" | "demote" | "retire_candidate";
  weight: number;
  evidence: "insufficient" | "moderate" | "strong";
  reason: string;
  regime_breakdown: Array<{ regime: string; n: number; mean_20d_pct: number | null; win_rate_20d: number | null; evidence: string }>;
}

export interface StrategyLeagueOut {
  as_of: string | null;
  entries: StrategyLeagueEntry[];
  safeguards: string[];
}

export interface CockpitSignal {
  ts_code: string;
  name: string | null;
  industry: string | null;
  rule_name: string;
  signal_date: string;
  streak: number | null;
  score: number | null;
  factor_used_count: number | null;
  factor_expected_count: number | null;
  factor_weight_coverage: number | null;
  missing_factors: string[];
}

export interface FactorQualityOut {
  trade_date: string;
  passed: boolean;
  issues: string[];
  expected_rows: number;
  factor_rows: number;
  row_coverage: number;
  score_rows: number;
  score_coverage: number;
  components: Record<string, { rank_field: string; count: number; coverage: number; raw_rank_correlation: number | null; direction_ok: boolean }>;
  minimum_stock_weight_coverage: number;
}

export interface SelectionCockpitOut {
  filter_audit?: { checked: number; excluded: number; reasons: Record<string, number>; excluded_codes: Record<string, string[]>; baseline_dates: Record<string, string | null> } | null;
  as_of: string | null;
  policy_name: string;
  policy_description: string;
  score_threshold: number;
  config: CockpitStrategyConfig;
  new_signals: CockpitSignal[];
  continuing: CockpitSignal[];
  risk_exits: CockpitSignal[];
  new_signal_count: number;
  continuing_count: number;
  risk_exit_count: number;
  strategies: StrategyLeagueEntry[];
  daily_flows: CockpitDailyFlow[];
  factor_quality: FactorQualityOut | null;
}

export interface CockpitStrategyConfig {
  entry_threshold: number;
  exit_threshold: number;
  candidate_limit: number;
  review_horizon: number;
  review_horizons: number[];
  exclude_st: boolean;
  exclude_high_gain?: boolean;
  high_gain_windows?: number[];
  high_gain_pct?: number;
  high_gain_match?: 'any' | 'all';
  high_gain_price_basis?: 'adjusted' | 'unadjusted';
  high_gain_missing_exclude?: boolean;
  version: number;
  effective_date: string;
  change_note: string;
}

export interface CockpitDailyFlow {
  trade_date: string;
  selected?: { ts_code: string; name: string | null }[];
  entered: { ts_code: string; name: string | null }[];
  exited: { ts_code: string; name: string | null }[];
  selected_count: number;
  strategy_version: number;
}

export interface ScoreReviewItem {
  ts_code: string;
  name: string | null;
  industry: string | null;
  score: number;
  components: Record<string, number | null>;
  signal_close: number | null;
  entry_date: string | null;
  entry_open: number | null;
  target_close: number | null;
  return_pct: number | null;
  signal_close_return_pct: number | null;
}

export interface ScoreReviewOut {
  requested_date: string;
  score_date: string | null;
  threshold: number;
  horizon: number;
  target_date: string | null;
  latest_market_date: string | null;
  matured: boolean;
  n_candidates: number;
  n_with_return: number;
  mean_return_pct: number | null;
  median_return_pct: number | null;
  win_rate: number | null;
  items: ScoreReviewItem[];
  score_logic: string;
  return_logic: string;
  entry_basis: string;
}

export interface EventStudyResult {
  source: string;
  n_events: number;
  n_with_data: number;
  windows: HorizonStat[];
}

export interface FactorRow {
  ts_code: string;
  name: string | null;
  industry: string | null;
  trade_date: string;
  composite_score: number | null;
  rank_mom_5d: number | null;
  rank_mom_20d: number | null;
  rank_rev_5d: number | null;
  rank_rs_20d: number | null;
  rank_rs_industry: number | null;
  rank_vol_5d: number | null;
  rank_price_pos: number | null;
  rank_mdd: number | null;
  mom_5d: number | null;
  mom_20d: number | null;
  rs_20d: number | null;
  rs_industry_20d: number | null;
  vol_ratio_5d: number | null;
  price_pos_60d: number | null;
  mdd_60d: number | null;
}

export interface NotifierConfig {
  bark_url: string;          // 掩码后字符串，如 "abc***7890"
  wecom_webhook: string;
  tg_bot_token: string;
  tg_chat_id: string;
  gotify_url: string;
  gotify_token: string;
  generic_webhook: string;
  active_provider: string | null;        // "bark" / "wecom" / "telegram" / "gotify" / "generic" / null
  source: Record<string, "" | "user" | "env">;       // M9: 改成 per-user 配置
  updated_at: string | null;
}

export interface NotifierConfigUpdate {
  bark_url?: string;          // null/未传 = 不变；空串 = 清除；非空 = 写入
  wecom_webhook?: string;
  tg_bot_token?: string;
  tg_chat_id?: string;
  gotify_url?: string;
  gotify_token?: string;
  generic_webhook?: string;
}

export type HealthStatus = "ok" | "stale" | "critical" | "empty";

export interface HealthCheck {
  name: string;
  status: HealthStatus;
  last: string | null;
  detail: string;
}

export interface HealthReport {
  checked_at: string;
  overall: HealthStatus;
  research_overall: HealthStatus;
  checks: HealthCheck[];
}

export type AndonLevel = "green" | "yellow" | "red";

export interface AndonTable {
  name: string;
  table: string;
  last: string | null;
  lag_days: number;
  group: "critical" | "derived";
}

export interface AndonStatus {
  level: AndonLevel;
  reference_date: string | null;
  as_of: string;
  stale: AndonTable[];
  freeze_weekday_gap: number;
  message: string;
}

export interface EventNewsItem {
  id: number;
  title: string;
  source: string;
  url: string | null;
  published_at: string;
  sentiment: number | null;
  event_type: string;
  event_label: string;
}

export interface EventTypeSummary {
  event_type: string;
  label: string;
  count: number;
  items: EventNewsItem[];
}

export interface BreakingItem {
  ts_code: string;
  name: string | null;
  industry: string | null;
  n_news: number;
  n_sources: number;
  sources: string[];
  titles: string[];
  avg_sentiment: number | null;
  window_start: string;
  window_end: string;
}

export interface SentimentDailyPoint {
  date: string;
  count: number;
  pos: number;
  neu: number;
  neg: number;
  score: number;       // (pos - neg) / count，[-1, 1]
}

export interface SentimentTimeSeries {
  scope: string;       // 'market' | 'stock:600519.SH' | 'industry:白酒'
  days: number;
  total: number;
  points: SentimentDailyPoint[];
}

export type MinuteAlertType =
  | "breakout_high"
  | "breakout_low"
  | "volume_spike"
  | "morning_strong"
  | "late_pump"
  | "intraday_reversal"
  | "grid_t_buy"
  | "grid_t_sell"
  | "rps_breakout"
  | "tracking_up_1m"
  | "tracking_down_1m"
  | "tracking_up_5m"
  | "tracking_down_5m"
  | "tracking_up_15m"
  | "tracking_down_15m"
  | "tracking_up_30m"
  | "tracking_down_30m"
  | "tracking_up_60m"
  | "tracking_down_60m"
  | "tracking_volume"
  | "tracking_breakout"
  | "tracking_confluence";

export interface AlertOutcomeBucket {
  alert_type: string;
  n: number;
  n_5d: number;
  n_10d: number;
  n_20d: number;
  win_rate_5d: number | null;
  win_rate_10d: number | null;
  win_rate_20d: number | null;
  avg_ret_5d: number | null;
  avg_ret_10d: number | null;
  avg_ret_20d: number | null;
  avg_peak_20d: number | null;
  avg_dd_20d: number | null;
}

export interface AlertOutcomesOut {
  days_back: number;
  buckets: AlertOutcomeBucket[];
  overall: AlertOutcomeBucket | null;
}

export interface MinuteAlertOut {
  id: number;
  ts_code: string;
  name: string | null;
  industry: string | null;
  biz_date: string;
  alert_time: string;
  alert_type: MinuteAlertType;
  severity: AlertSeverity;
  pushed_at: string | null;
  payload: {
    close?: number;
    prev_high?: number;
    prev_low?: number;
    bar_index?: number;
    bars_total?: number;
    vol?: number;
    avg_vol?: number;
    ratio?: number;
    open?: number;            // morning_strong
    pct?: number;             // morning_strong
    vol_ratio?: number;       // morning_strong / late_pump
    day_high?: number;        // intraday_reversal
    drop_pct?: number;        // intraday_reversal
    // grid_t_buy / grid_t_sell
    account_id?: number;
    level?: number;
    level_price?: number;
    current_price?: number;
    anchor_price?: number;
    step_pct?: number;
    qty?: number;
    hint?: string;
    // rps_breakout
    rps_120_now?: number;
    rps_120_prev?: number;
    jump?: number;
    rs_120d_now?: number | null;
    industry?: string | null;
    days?: number;
  };
  created_at: string;
}

export interface IntradayTrackOut {
  id: number;
  user_id: number;
  ts_code: string;
  name: string | null;
  industry: string | null;
  enabled: boolean;
  window_minutes: number;
  return_threshold_pct: number;
  volume_ratio_threshold: number;
  baseline_minutes: number;
  cooldown_minutes: number;
  track_return: boolean;
  track_volume: boolean;
  track_breakout: boolean;
  condition_mode: "any" | "all";
  threshold_mode: "fixed" | "adaptive";
  target_type: "none" | "price" | "ma" | "ema" | "vwap" | "open" | "daily_ma" | "daily_ema" | "prev_high" | "prev_low";
  target_value: number | null;
  target_period: number | null;
  target_tolerance_pct: number;
  target_direction: "near" | "cross_up" | "cross_down" | "above" | "below";
  target_confirm_minutes: number;
  last_signal_at: string | null;
  created_at: string;
  updated_at: string;
  groups: string[];
}

export interface IntradayTrackGroupCreate {
  name: string;
  description?: string | null;
  selection_tool?: "screen_indicator" | "query_stocks" | null;
  selection_args?: Record<string, unknown>;
  enabled?: boolean;
  auto_sync?: boolean;
  remove_stale?: boolean;
  sync_now?: boolean;
}

export interface QQConnection {
  configured: boolean;
  connected: boolean;
  account: string | null;
  phase: string;
  message: string;
  qrcode_data_url: string | null;
}

export interface IntradayTrackGroupFromSourceCreate {
  source_type: "holdings" | "screen_run" | "score_cohort" | "theme" | "watchlist_group";
  name?: string;
  screen_run_id?: number;
  screen_rule?: string;
  screen_date?: string;
  score_date?: string;
  score_threshold?: number;
  theme?: string;
  watchlist_group?: string;
}

export interface IntradayTrackGroupOut {
  id: number;
  name: string;
  description: string | null;
  selection_tool: "screen_indicator" | "query_stocks" | null;
  selection_args: Record<string, unknown>;
  enabled: boolean;
  auto_sync: boolean;
  remove_stale: boolean;
  member_count: number;
  last_synced_at: string | null;
  last_result_count: number;
  last_added: number;
  last_removed: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  observation_days: number;
  observation_status: "collecting" | "review_ready";
}

export interface IntradayTrackGroupSyncPreviewOut {
  ok: boolean;
  group_id: number;
  group_name: string;
  enabled: boolean;
  selection_tool: string | null;
  selection_args: Record<string, unknown>;
  remove_stale: boolean;
  current_count: number;
  result_count: number;
  added_count: number;
  removed_count: number;
  stale_count: number;
  added_codes: string[];
  removed_codes: string[];
  matched_sample: string[];
  note: string | null;
  error: string | null;
  evaluated_at: string;
}

export interface IntradayTrackPerformanceBucket {
  alert_type: string;
  n: number;
  evaluated: number;
  pending: number;
  n_5d: number;
  n_10d: number;
  n_20d: number;
  win_rate_5d: number | null;
  win_rate_10d: number | null;
  win_rate_20d: number | null;
  avg_ret_5d: number | null;
  avg_ret_10d: number | null;
  avg_ret_20d: number | null;
  avg_peak_20d: number | null;
  avg_dd_20d: number | null;
  sample_quality: "good" | "limited" | "insufficient";
}

export interface IntradayTrackPerformanceOut {
  days_back: number;
  group_id: number | null;
  total_events: number;
  evaluated_events: number;
  pending_events: number;
  last_computed_at: string | null;
  buckets: IntradayTrackPerformanceBucket[];
  overall: IntradayTrackPerformanceBucket | null;
}

export interface IntradayTrackGroupLeagueItem {
  rank: number | null;
  group_id: number;
  group_name: string;
  member_count: number;
  observation_days: number;
  observation_status: "collecting" | "review_ready";
  total_events: number;
  triggered_members: number;
  trigger_rate_pct: number | null;
  evaluated_5d: number;
  win_rate_5d: number | null;
  avg_ret_5d: number | null;
  avg_ret_10d: number | null;
  avg_ret_20d: number | null;
  avg_dd_20d: number | null;
  score: number | null;
  sample_quality: "good" | "limited" | "insufficient";
}

export interface IntradayTrackGroupLeagueOut {
  days_back: number;
  generated_at: string;
  groups: IntradayTrackGroupLeagueItem[];
}

export interface IntradayTrackEventOut {
  id: number;
  ts_code: string;
  name: string | null;
  industry: string | null;
  biz_date: string;
  alert_time: string;
  alert_type: string;
  severity: AlertSeverity;
  payload: {
    close?: number;
    trade_time?: string;
    window_minutes?: number;
    return_pct?: number;
    threshold_pct?: number;
    volume_ratio?: number;
    threshold_ratio?: number;
    direction?: "up" | "down";
    target_type?: string;
    target_value?: number;
    target_period?: number;
    distance_pct?: number;
    target_tolerance_pct?: number;
    target_direction?: string;
    target_confirm_minutes?: number;
    signal_strength?: number | null;
    signal_level?: IntradayStrengthLevel;
    signal_direction?: "up" | "down" | "mixed" | "neutral";
    signal_state?: "triggered" | "ongoing" | "recovered" | "idle";
    condition_mode?: "any" | "all";
    active_signals?: string[];
    active_signal_count?: number;
    required_signal_count?: number;
  };
  created_at: string;
}

export interface IntradayTrackValidationIssue {
  level: "error" | "warning" | "info";
  code: string;
  message: string;
  field: string | null;
}

export interface IntradayTrackPreviewOut {
  ts_code: string;
  valid: boolean;
  issues: IntradayTrackValidationIssue[];
  normalized: Record<string, unknown>;
  snapshot: {
    status?: string;
    data_quality?: string;
    data_quality_reasons?: string[];
    data_age_seconds?: number | null;
    price?: number | null;
    return_pct?: number | null;
    volume_ratio?: number | null;
    condition_mode?: "any" | "all";
    threshold_mode?: "fixed" | "adaptive";
    effective_return_threshold_pct?: number | null;
    effective_volume_ratio_threshold?: number | null;
    strength_level?: IntradayStrengthLevel;
    strength_score?: number | null;
    flags?: string[];
  } | null;
}

export type IntradaySignalState = "triggered" | "ongoing" | "recovered" | "idle";
export type IntradayStrengthLevel = "strong" | "medium" | "weak" | "none" | "degraded";

export interface IntradayTrackSignalStateOut {
  key: string;
  label: string;
  state: IntradaySignalState;
  active: boolean;
  value: number | null;
  threshold: number | null;
  direction: "up" | "down" | null;
  active_signals?: string[];
}

export interface IntradayTrackBatchRequest {
  ts_codes: string[];
  group_name?: string | null;
  enabled?: boolean;
  window_minutes?: number;
  return_threshold_pct?: number;
  volume_ratio_threshold?: number;
  baseline_minutes?: number;
  cooldown_minutes?: number;
  track_return?: boolean;
  track_volume?: boolean;
  track_breakout?: boolean;
  condition_mode?: "any" | "all";
  threshold_mode?: "fixed" | "adaptive";
  target_type?: "none" | "price" | "ma" | "ema" | "vwap" | "open" | "daily_ma" | "daily_ema" | "prev_high" | "prev_low";
  target_value?: number | null;
  target_period?: number | null;
  target_tolerance_pct?: number;
  target_direction?: "near" | "cross_up" | "cross_down" | "above" | "below";
  target_confirm_minutes?: number;
}

export interface IntradayTrackBatchPreviewOut {
  valid: boolean;
  requested: number;
  valid_codes: string[];
  unknown_codes: string[];
  existing_codes: string[];
  new_codes: string[];
  group_name: string | null;
  issues: IntradayTrackValidationIssue[];
  normalized: Record<string, unknown>;
  added?: number;
  updated?: number;
  group_id?: number | null;
}

export interface IntradayTrackSnapshotOut {
  track_id: number;
  ts_code: string;
  price: number | null;
  trade_time: string | null;
  data_age_seconds: number | null;
  status: "fresh" | "delayed" | "closed" | "no_data";
  market_phase: IntradayMarketPhase;
  market_open: boolean;
  actionable: boolean;
  window_minutes: number;
  condition_mode: "any" | "all";
  threshold_mode: "fixed" | "adaptive";
  effective_return_threshold_pct: number | null;
  effective_volume_ratio_threshold: number | null;
  realized_volatility_pct: number | null;
  volume_cv: number | null;
  return_pct: number | null;
  volume_ratio: number | null;
  breakout_direction: "up" | "down" | null;
  flags: string[];
  signal_states: IntradayTrackSignalStateOut[];
  strength_score: number | null;
  strength_level: IntradayStrengthLevel;
  strength_direction: "up" | "down" | "mixed" | "neutral";
  strength_components: Record<string, number>;
  data_quality: "good" | "degraded" | "no_data";
  data_source: string | null;
  observed_bars: number;
  expected_bars: number;
  missing_bars: number;
  gap_count: number;
  max_gap_minutes: number;
  data_quality_reasons: string[];
}

export type IntradayMarketPhase =
  | "premarket"
  | "morning"
  | "lunch_break"
  | "afternoon"
  | "postmarket"
  | "closed";

export interface IntradayTrackBarOut {
  trade_time: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  vol: number | null;
  amount: number | null;
}

export interface SandboxWorkspaceCreate {
  name: string;
  stock_pool: string[];
  strategy_spec: { kind: string; [key: string]: unknown };
  initial_cash: number;
}

export interface SandboxWorkspaceOut extends SandboxWorkspaceCreate {
  id: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface SandboxRunCreate {
  request_key?: string;
  mode: "historical" | "forward";
  start?: string;
  end?: string;
}

export interface SandboxRunOut {
  id: number;
  workspace_id: number;
  mode: "historical" | "forward";
  status: string;
  input_snapshot: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error_msg: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  candidate?: SandboxCandidateOut | null;
  review?: { eligible: boolean; reasons: string[]; strategy_kind: string; note: string };
}

export interface SandboxCandidateOut {
  current_status?: string;
  strategy_version_id: number;
  account_id: number;
  account_name: string;
  source_run_id: number;
  stock_codes?: string[];
  target_strategy_id?: number;
  enabled: boolean;
  route: string;
  note: string;
}

export interface IntradayTrackBarsOut {
  ts_code: string;
  prev_close: number | null;
  status: "fresh" | "delayed" | "closed" | "no_data";
  market_phase: IntradayMarketPhase;
  market_open: boolean;
  data_age_seconds: number | null;
  condition_mode: "any" | "all";
  threshold_mode: "fixed" | "adaptive";
  effective_return_threshold_pct: number | null;
  effective_volume_ratio_threshold: number | null;
  realized_volatility_pct: number | null;
  volume_cv: number | null;
  bars: IntradayTrackBarOut[];
  signal_states: IntradayTrackSignalStateOut[];
  strength_score: number | null;
  strength_level: IntradayStrengthLevel;
  strength_direction: "up" | "down" | "mixed" | "neutral";
  strength_components: Record<string, number>;
  data_quality: "good" | "degraded" | "no_data";
  data_source: string | null;
  observed_bars: number;
  expected_bars: number;
  missing_bars: number;
  gap_count: number;
  max_gap_minutes: number;
  data_quality_reasons: string[];
}

export interface ResearchTask {
  id: string; kind: string; target_id: number; status: string; phase: string;
  attempts: number; error: string | null; result: Record<string, unknown> | null;
  created_at: string; updated_at: string;
}
export interface NotificationDelivery {
  id: string; notification_id: number; title: string; channel: string;
  status: string; error: string | null; created_at: string;
}

export const learningApi = {
  forward: (id: number) => fetchJSON<{ items: import("@/lib/autonomousLearning").LearningForwardObservation[] }>(`/api/v1/adaptive-research/learning/campaigns/${id}/forward`),
  startForward: (id: number, candidate_hash: string, request_key: string) => postJSON<import("@/lib/autonomousLearning").LearningForwardObservation>(`/api/v1/adaptive-research/learning/campaigns/${id}/forward`, { candidate_hash, request_key }),
  stopForward: (id: number, observationId: number) => postJSON(`/api/v1/adaptive-research/learning/campaigns/${id}/forward/${observationId}/stop`, {}),
  factorBackfillStatus: () => fetchJSON<{ items: { id: number; status: string; cursor: number; total_units: number; stored_rows: number; finite_rows: number; empty_units: number; failed_units: number; stocks: number; factors: number; start: string; end: string; error?: string; heartbeat_at?: string }[] }>("/api/v1/adaptive-research/learning/factor-backfill-status"),
  factorCatalog: () => fetchJSON<{ items: import("@/lib/autonomousLearning").LearningFactorOption[] }>("/api/v1/adaptive-research/learning/factor-catalog"),
  factorExperiments: (id: number, before?: number) => fetchJSON<import("@/lib/autonomousLearning").FactorExperimentHistory>(`/api/v1/adaptive-research/learning/campaigns/${id}/factor-experiments?limit=3${before === undefined ? "" : `&before=${before}`}`),
  list: () => fetchJSON<import("@/lib/autonomousLearning").LearningCampaign[]>("/api/v1/adaptive-research/learning/campaigns"),
  create: (body: unknown) => postJSON<import("@/lib/autonomousLearning").LearningCampaign[]>("/api/v1/adaptive-research/learning/campaigns", body),
  control: (id: number, action: "pause" | "resume" | "stop") => postJSON(`/api/v1/adaptive-research/learning/campaigns/${id}/${action}`, {}),
  artifact: (id: number) => fetchJSON<unknown>(`/api/v1/adaptive-research/learning/campaigns/${id}/artifact`),
  expressions: (id: number, before?: number) => fetchJSON<import("@/lib/autonomousLearning").ExpressionHistory>(`/api/v1/adaptive-research/learning/campaigns/${id}/expressions?limit=3${before === undefined ? "" : `&before=${before}`}`),
};
